/** Editorial index over the pinned Yang corpus. Literal forms are NOT semantic aliases.
 * This expands the retained seventeen-topic LY spine. Each reviewed relationship
 * below is separately checked against its quoted original; no inferred game tags
 * are promoted to reviewed teaching routes. */
export const CONCEPT_REVIEW_SOURCE = {
  version: "analects-concept-editorial-20261007-v2",
  repository: "ieduer/lunyu",
  commit: "69674ff161ff2272ab97f3aa3fda97a8c10a8fb4",
  sha256: "8c9ebc18d6c6f0c156cad89aaab89f6a413cab0f9b2fa2fa7b65ac2b0d4400b1",
  method: "Source-grounded editorial reading of each quoted chapter and applicable Yang notes; separate from inferred game tags",
  editorialComplete: true,
  reviewedCanonicalChapters: 512,
  acceptanceReceipts: {"THEMATIC_COMPLETE_ACCEPTANCE_v1.json":"7ca8a0b9e14eb5304b5248407673d8cd1340767d102f227687da78c13882d9af","CATALOG_GLYPH_ACCEPTANCE_v1.json":"c62ad1b12ce728d075aa3c1a3e8610ff1d24fc45390450f1924e4aec1f181ccd","ALL_CONCEPT_LEXICAL_AUDIT_v1.json":"7771d49fb5bcac7c188065fb4106d2db16bf7a470670b8e4201055745a9e2117","SEMANTIC_CLARIFICATIONS_v1.json":"3906f7d413c2b23c784682c1cdd42afa66fcd01308e6144105f53e5fcf11e6e8"},
  completeThematicRoute: false,
  changesHistoricalGrades: false,
} as const;
export interface ConceptEntry {
  id: string; label: string; group: string; terms: string[];
  aliases?: string[]; note?: string; related?: string[]; basis?: string[];
}
const entry = (id: string, label: string, group: string, note?: string, related?: string[]): ConceptEntry => ({ id, label, group, terms: [label], note, related });
export const CONCEPT_CATALOG: ConceptEntry[] = [
  entry("ren", "仁", "德行與修身", "列出所有含「仁」的原文；指仁德、仁者或地名等仍須看上下文。", ["shu", "li", "airen", "keji"]),
  entry("li", "禮", "禮樂與文質", "禮／礼合查，儀節、制度與內在敬意須連同上下文閱讀。", ["ren", "jing", "yue"]),
  entry("xue", "學", "學習與教育", undefined, ["xi", "si", "haoxue", "jiaoxue"]),
  entry("junzi", "君子", "人格與人物", "不同章句中的德行標準與身分稱謂，須據原文辨別。", ["xiaoren", "shi", "yili"]),
  entry("xiao", "孝", "親友與相處", undefined, ["ti", "jing", "fumu"]),
  entry("shu", "忠恕", "親友與相處", "直接命中只查連寫的「忠恕」；忠、恕及推己及人的相關章另有獨立入口。", ["zhong", "forbearance", "jisuobuyu"]),
  entry("xin", "信", "德行與修身", "既可能指守信／信任，也可能意為「確實」（如14.13「信乎」）；字詞命中不等於同一思想。", ["zhong", "yi", "pengyou"]),
  entry("yili", "義利", "德行與修身", "「義利」是比較主題；原文往往分說義、利。連寫命中可能為零，請另讀下方已核對關聯或分查兩字。", ["yi", "li-benefit", "fugui", "pin"]),
  entry("zheng", "為政", "政治與秩序", undefined, ["de", "min", "zhengming", "xing"]),
  entry("zhengming", "正名", "政治與秩序", undefined, ["ming-name", "yan", "li"]),
  entry("zhiren", "知人", "人格與人物", "「知」在知人、知道、智慧等處用法不同；不把全部「知」自動列入智慧主題。", ["zhi", "xian", "guan"]),
  entry("zhongyong", "中庸", "德行與修身", undefined, ["he", "guoyoubuji", "xiangyuan"]),
  entry("tianming", "天命", "天命與鬼神", "天命、命令、性命並非同義；「命」另列全量字詞入口。", ["tian", "ming", "guishen"]),
  entry("wenzhi", "文質", "禮樂與文質", undefined, ["wen", "zhi-quality", "binbin"]),
  entry("shijiao", "詩教", "禮樂與文質", "主題名稱不必是原文連寫詞；詩的字詞命中與已核對教學關聯分開。", ["shi-poetry", "yue", "jiaoxue"]),
  entry("chushi", "出處", "行道與進退", "出仕或退隱是此處的主題義；不把所有「出」「處」合算為主題證據。", ["shi-office", "yin", "dao", "bangyoudao"]),
  entry("jiaoxue", "教學", "學習與教育", "以原文具體施教方式核對主題；教／學字詞命中可分別查找。", ["jiao", "hui", "youjiaowulei", "juyiyu"]),
  entry("yi", "義", "德行與修身"), entry("li-benefit", "利", "德行與修身"),
  entry("de", "德", "德行與修身"), entry("zhong", "忠", "德行與修身"),
  entry("forbearance", "恕", "親友與相處"), entry("yong", "勇", "德行與修身"),
  entry("jing", "敬", "德行與修身"), entry("cheng", "誠", "德行與修身"),
  entry("gong", "恭", "德行與修身"), entry("kuan", "寬", "德行與修身"),
  entry("min-diligence", "敏", "德行與修身"), entry("hui-kindness", "惠", "德行與修身"),
  entry("zhi-upright", "直", "德行與修身"), entry("chi", "恥", "德行與修身"),
  entry("guo", "過", "德行與修身", "過可指過失，也可指經過或過度，全部命中保留。"),
  entry("gai", "改", "德行與修身"), entry("sheng", "省", "德行與修身"),
  entry("keji", "克己", "德行與修身"), entry("sanxing", "三省", "德行與修身"),
  entry("he", "和", "德行與修身"), entry("guoyoubuji", "過猶不及", "德行與修身"),
  entry("hebutong", "和而不同", "親友與相處"), entry("xiangyuan", "鄉愿", "德行與修身", "愿與願不可任意互換；此處保留底本「鄉愿／乡愿」。"),
  entry("xi", "習", "學習與教育"), entry("si", "思", "學習與教育"),
  entry("zhi", "知", "學習與教育", "本入口不把「知」一律改為智；請依註釋區分知道、識人與智慧。", ["wisdom", "zhiren"]),
  entry("wisdom", "智", "德行與修身", "「智」與通作智的「知」分開：直接查智，主題相關列明已核對的知字章句。", ["zhi", "ren", "yong"]),
  entry("wen-ask", "問", "學習與教育"), entry("wen-hear", "聞", "學習與教育"),
  entry("haoxue", "好學", "學習與教育"), entry("wengu", "溫故", "學習與教育"),
  entry("zhixin", "知新", "學習與教育"), entry("jiao", "教", "學習與教育"),
  entry("hui", "誨", "學習與教育"), entry("qi", "啟", "學習與教育"),
  entry("youjiaowulei", "有教無類", "學習與教育"), entry("juyiyu", "舉一隅", "學習與教育"),
  entry("buyichiwen", "不恥下問", "學習與教育"), entry("xuezhizhi", "學而知之", "學習與教育"),
  entry("xueersi", "學而不思", "學習與教育"), entry("xueroushi", "學而優則仕", "學習與教育"),
  entry("ti", "悌", "親友與相處", "悌與弟不全量互換；1.2的孝弟另列有依据的思想關聯。", ["di", "xiao"]),
  entry("di", "弟", "親友與相處", "弟子、兄弟與通悌的弟須分辨；這裡保留所有字詞命中。"),
  entry("fumu", "父母", "親友與相處"), entry("xiongdi", "兄弟", "親友與相處"),
  entry("pengyou", "朋友", "親友與相處"), entry("you", "友", "親友與相處"),
  entry("ai", "愛", "親友與相處"), entry("airen", "愛人", "親友與相處"),
  entry("jisuobuyu", "己所不欲", "親友與相處"), entry("wushiyuren", "勿施於人", "親友與相處"),
  entry("jiyuli", "己欲立而立人", "親友與相處"), entry("jinqupi", "能近取譬", "親友與相處"),
  entry("zheng-politics", "政", "政治與秩序"), entry("min", "民", "政治與秩序"),
  entry("xing", "刑", "政治與秩序"), entry("jun", "君", "政治與秩序"),
  entry("chen", "臣", "政治與秩序"), entry("bang", "邦", "政治與秩序"),
  entry("ming-name", "名", "政治與秩序"), entry("zheng-upright", "正", "政治與秩序"),
  entry("minxin", "民信", "政治與秩序"), entry("zhengyide", "為政以德", "政治與秩序"),
  entry("yan", "言", "政治與秩序"), entry("xing-act", "行", "行道與進退", "讀音與意思須依章句判断，不把行走與德行混作同義。"),
  entry("dao", "道", "行道與進退", "道路、學說、方法、政治有道及引導的道不同；完整命中不作同義判定。"),
  entry("shi-office", "仕", "行道與進退"), entry("yin", "隱", "行道與進退"),
  entry("bangyoudao", "邦有道", "行道與進退"), entry("bangwudao", "邦無道", "行道與進退"),
  entry("fugui", "富貴", "行道與進退"), entry("fu", "富", "行道與進退"),
  entry("pin", "貧", "行道與進退"), entry("jian", "賤", "行道與進退"),
  entry("buyi", "不義", "行道與進退"), entry("zhizhebuwei", "知其不可而為之", "行道與進退"),
  entry("tian", "天", "天命與鬼神"), entry("ming", "命", "天命與鬼神"),
  entry("guishen", "鬼神", "天命與鬼神"), entry("si-death", "死", "天命與鬼神"),
  entry("sheng-life", "生", "天命與鬼神"), entry("ji", "祭", "禮樂與文質"),
  entry("sang", "喪", "禮樂與文質"), entry("jian-frugal", "儉", "禮樂與文質"),
  entry("rang", "讓", "禮樂與文質"), entry("wen", "文", "禮樂與文質"),
  entry("zhi-quality", "質", "禮樂與文質"), entry("binbin", "彬彬", "禮樂與文質"),
  entry("shi-poetry", "詩", "禮樂與文質"), entry("yue", "樂", "禮樂與文質", "音樂、喜樂、樂於等用法一併命中；已核對主題用法另附理由，不能全當作樂教。", ["shijiao", "li"]),
  entry("renle", "仁者樂山", "天命與鬼神"), entry("zhile", "知者樂水", "天命與鬼神"),
  entry("xiaoren", "小人", "人格與人物"), entry("shi", "士", "人格與人物"),
  entry("xian", "賢", "人格與人物"), entry("sheng-sage", "聖", "人格與人物"),
  entry("xing-nature", "性", "學習與教育", "性與習相對的討論，須保留其章句語境。", ["xi", "xue"]),
  entry("zhi-aspiration", "志", "德行與修身"), entry("yu", "欲", "德行與修身"),
  entry("yuan", "怨", "親友與相處"), entry("jin", "謹", "德行與修身"),
  entry("shen", "慎", "德行與修身"), entry("jie", "節", "禮樂與文質"),
  entry("xiuyi", "修己", "德行與修身"), entry("anren", "安人", "政治與秩序"),
  entry("anbaixing", "安百姓", "政治與秩序"), entry("boshi", "博施", "政治與秩序"),
  entry("jizhong", "濟眾", "政治與秩序"), entry("boxue", "博學", "學習與教育"),
  entry("duzhi", "篤志", "學習與教育"), entry("qiewen", "切問", "學習與教育"),
  entry("jinsi", "近思", "學習與教育"), entry("qiaoyanlingse", "巧言令色", "人格與人物"),
  entry("zhongxin", "忠信", "德行與修身"), entry("sibi", "毋意", "德行與修身"),
  entry("renren", "仁人", "人格與人物"), entry("shanren", "善人", "人格與人物"),
  entry("chengren", "成人", "人格與人物"), entry("guan", "觀", "人格與人物"),
  {"id":"gang","label":"剛","group":"德行與修身","terms":["剛"],"related":["yu","yong"],"basis":["5.11"],"note":"剛毅不屈須與欲望及勇分辨；16.7血氣方剛屬不同語義。字詞全量保留，思想關聯另逐章核對。"},
  {"id":"ning-eloquence","label":"佞","group":"人格與人物","terms":["佞"],"related":["ren","yan","qiaoyanlingse"],"basis":["5.5"],"note":"在5.5按楊注指能言善說，須連同口給及屢憎於人的語境；不將一切口才與仁德等同。"},
  {"id":"qi-vessel","label":"器","group":"人格與人物","terms":["器"],"related":["junzi","zhiren"],"basis":["2.12","3.22","5.4"],"note":"器皿、器量與論人之譬用法分開；字詞全量結果不保證思想完全相同。"},
  {"id":"qing-integrity","label":"清","group":"德行與修身","terms":["清"],"related":["ren","chushi"],"basis":["5.19"],"note":"以陳文子的行止說清，孔子未因此直接許之以仁；保留德目之間的區別。"},
  {"id":"tiandao","label":"天道","group":"天命與鬼神","terms":["天道"],"related":["tian","xing-nature","wen"],"basis":["5.13"],"note":"直接查連寫天道；5.13記子貢聞與未聞的區別，不能用後世學說補作孔子同章已說的內容。"},
  {"id":"jian-simple","label":"簡","group":"政治與秩序","terms":["簡"],"related":["jing","zheng"],"basis":["6.2"],"note":"簡與儉義別，6.2須連居敬及臨民讀。簡/简僅作字詞查詢歸一，原始來源不改；運行索引尚未整合。"},
  {"id":"ru","label":"儒","group":"人格與人物","terms":["儒"],"related":["junzi","xiaoren","xue"],"basis":["6.13"],"note":"6.13區分君子儒與小人儒，不由儒的稱號直接判定個人德行。"},
  {"id":"fa","label":"伐","group":"德行與修身","terms":["伐"],"related":["yong","zhi-aspiration"],"basis":["5.26","6.15"],"note":"伐可指夸功，也可指征伐；字詞全量檢索分義閱讀，無伐善與孟之反不伐列入不自夸的思想關聯。"},
  {"id":"yi-arts","label":"藝","group":"學習與教育","terms":["藝"],"related":["xue","li","yue"],"basis":["7.6"],"note":"藝依7.6底本為禮樂射御書數；不只指現代美術，其他章多才多藝須按上下文另辨。"},
  {"id":"heng","label":"恆","group":"德行與修身","terms":["恆"],"related":["cheng","junzi"],"basis":["7.26"],"note":"7.26有恆指一定操守，不由一次表現或登入天數推成恆德。恆/恒僅查詢歸一，運行索引尚未整合。"},
  {"id":"she-extravagance","label":"奢","group":"禮樂與文質","terms":["奢"],"related":["jian-frugal","li"],"basis":["7.36"],"note":"奢與儉的具體取捨須讀全句；不將奢、泰在所有章中的字詞用法自動歸為同義。"},
  {"id":"hongyi","label":"弘毅","group":"德行與修身","terms":["弘毅"],"related":["gang","shi","ren","zhi-aspiration"],"basis":["8.7"],"note":"弘毅依8.7楊注為強毅，與任重道遠、仁以為己任連讀；字詞入口不替代具體使命及持續實踐。"},
  {"id":"quan","label":"權","group":"德行與修身","terms":["權"],"related":["yi","li","dao"],"basis":["9.30"],"note":"9.30權指通權達變，20.1權量的權指稱重，不同於權變或政治權力。權/权僅查詢歸一，原文不改，運行索引尚未整合。"},
  {"id":"shi-time","label":"時","group":"行道與進退","terms":["時"],"related":["chushi","shen","xing-act"],"basis":["10.27"],"note":"時/时直接命中須分時機、時候、按時等用法；10.27得時的語境本身有解讀不確定性，不預設全量命中同屬政治進退。"},
  {"id":"da-attainment","label":"達","group":"人格與人物","terms":["達"],"related":["wen-hear","zhi-upright","yi"],"basis":["12.20"],"note":"12.20達與聞非同義名望；該章原文在察言截斷，按已接受固定來源保留，後文意義須標為主譯層，不冒稱完整古文。15.41辭達是達意，字詞命中不等於此主題。"},
  {"id":"chongde","label":"崇德","group":"德行與修身","terms":["崇德"],"related":["de","zhongxin","yi","sheng"],"basis":["12.10","12.21"],"note":"崇德在子張與樊遲問答中有不同具體說明；保留提問者、相關行為與兩章完整脈絡。"},
  {"id":"bianhuo","label":"辨惑","group":"德行與修身","terms":["辨惑"],"related":["si","zhi","yu","chongde"],"basis":["12.10","12.21"],"note":"辨惑為原文概念入口；不能因與遊戲門類同名而將一次點閱或一次答題當作倫理辨惑已完成。"},
  {"id":"xiute","label":"修慝","group":"德行與修身","terms":["修慝"],"related":["sheng","gai","chongde"],"basis":["12.21"],"note":"修慝須連攻其惡、無攻人之惡及當前底本譯法閱讀；字詞與思想關聯保存具體理由，不混同其他版本釋義。"},
  {"id":"kuangjuan","label":"狂狷","group":"人格與人物","terms":["狂狷"],"related":["kuang","juan","zhongyong"],"basis":["13.21"],"note":"直接查連寫狂狷；分開的狂、狷各有入口，不以兩種人格傾向完全相同處理。"},
  {"id":"kuang","label":"狂","group":"人格與人物","terms":["狂"],"related":["kuangjuan","juan","zhongyong"],"basis":["13.21"],"note":"13.21狂者進取須連不得中行的語境；8.16狂而不直另有評價，不把同字命中一律讀作褒義。"},
  {"id":"juan","label":"狷","group":"人格與人物","terms":["狷"],"related":["kuangjuan","kuang","jie"],"basis":["13.21"],"note":"狷者有所不為；楊注引孟子的狷介說作參考，不以注引中的獧自動替換全書字形。"},
  {"id":"ne","label":"訥","group":"德行與修身","terms":["訥"],"related":["yan","jin","ren"],"basis":["13.27"],"note":"訥/讷沿現有字形映射；主要與不輕言相連，不將口語速度當作仁德測量。"},
  {"id":"tai","label":"泰","group":"德行與修身","terms":["泰"],"related":["junzi","gong","she-extravagance"],"basis":["13.26"],"note":"泰在13.26為安舒，7.26為用度豪華，9.3為倨傲；字面全量命中與分義關聯須分列。"},
  {"id":"yiguan","label":"一以貫之","group":"學習與教育","terms":["一以貫之"],"related":["shu","xue","xing-act"],"basis":["15.3"],"note":"直接查一以貫之；15.3與4.15的連結依楊注，但15.3本章未直接寫忠恕，不能自動將全部學問壓縮成一語。"},
  {"id":"wuwei","label":"無為而治","group":"政治與秩序","terms":["無為而治"],"related":["zheng","zhiren","gong"],"basis":["15.5"],"note":"15.5以舜為例，楊注引任人得當解釋；不預先將不同思想家的無為一概視為同義。"},
  {"id":"zhen","label":"貞","group":"德行與修身","terms":["貞"],"related":["xin","liang","cheng"],"basis":["15.37"],"note":"15.37貞依原注言行抱一、大信，諒為小信；不採未取異說作全局替換。貞/贞僅查詢歸一，運行索引尚未整合。"},
  {"id":"liang","label":"諒","group":"德行與修身","terms":["諒"],"related":["xin","zhen","yi"],"basis":["15.37","16.4"],"note":"15.37與14.17諒有小信語境，16.4友諒是信實正面義；14.40諒陰為喪語，不混為同一德目。諒/谅沿既有查詢映射。"},
  {"id":"jun-equity","label":"均","group":"政治與秩序","terms":["均"],"related":["zheng","min","he","pin"],"basis":["16.1"],"note":"均與貧、和、安的關係须连整段論證讀；16.1校語混入原文，当前全量字詞統計不能冒稱校定本統計。"},
  {"id":"sanyou","label":"三友","group":"德行與修身","terms":["三友"],"related":["pengyou","you","zhi-upright","liang"],"basis":["16.4"],"note":"益與損各三友，連寫詞入口保留六種具體行為，不能以單一好壞標籤取代識人。"},
  {"id":"sanle","label":"三樂","group":"德行與修身","terms":["三樂"],"related":["yue","li","you","jie"],"basis":["16.5"],"note":"益與損各三樂，樂於與音樂同字分義，不只按數字三來合併其他三項教誨。"},
  {"id":"sanjie","label":"三戒","group":"德行與修身","terms":["三戒"],"related":["shen","yu","junzi"],"basis":["16.7"],"note":"少壯老戒色鬥得，分別保留原文所指；不是以年齡直接判定誰必有某種缺點。"},
  {"id":"sanwei","label":"三畏","group":"德行與修身","terms":["三畏"],"related":["tianming","jing","sheng-sage"],"basis":["16.8"],"note":"天命、大人、聖言三畏屬本章原句，保留楊注對大人的高位身分解讀。"},
  {"id":"jiusi","label":"九思","group":"德行與修身","terms":["九思"],"related":["si","yi","jing","wen-ask"],"basis":["16.10"],"note":"九思逐一對應視聽色貌言事疑忿得，完整原文聚合與教學路線都應保留九項。"},
  {"id":"liubi","label":"六蔽","group":"學習與教育","terms":["六蔽"],"related":["haoxue","xue","ren","wisdom","xin","zhi-upright","yong","gang"],"basis":["17.8"],"note":"六言六蔽逐一配對，問題在好德而不好學的偏失；不得把六德本身等同六種弊病。"},
  {"id":"yimin","label":"逸民","group":"行道與進退","terms":["逸民"],"related":["chushi","yin","shi"],"basis":["18.8"],"note":"逸民依18.8楊注為遺落人才，不必每人都是同樣的退隱；逸/佚屬此處詞義注解，未作全書全局替換。"},
  {"id":"wumei","label":"五美","group":"政治與秩序","terms":["五美"],"related":["sie","zheng","hui-kindness","tai"],"basis":["20.2"],"note":"20.2五美含惠勞欲泰威及各自限制，須連後文追問回答完整閱讀，不只列五個抽象名詞。"},
  {"id":"sie","label":"四惡","group":"政治與秩序","terms":["四惡"],"related":["wumei","zheng","jiao","min"],"basis":["20.2"],"note":"20.2四惡為不教而殺、不戒視成、慢令致期、出納吝的治理行為；須按原句逐項對應，不把四項化成籠統小人標籤。"},
];
export interface ReviewedRelation { coordinate: string; quote: string; concepts: string[]; reason: string; primary?: string; noteBoundary?: string; canonicalId?: number; historicalIds?: number[]; sourceRowSha256?: string; sourceCompleteness?: string }
export const REVIEWED_RELATIONS: ReviewedRelation[] = [
  {
    "coordinate": "1.1",
    "quote": "学而时习之",
    "concepts": [
      "xue",
      "xi",
      "junzi"
    ],
    "reason": "學、習與人不知而不愠同章呈現；後一句將待人態度連到君子。"
  },
  {
    "coordinate": "1.2",
    "quote": "孝弟也者，其为仁之本",
    "concepts": [
      "xiao",
      "ti",
      "ren",
      "junzi"
    ],
    "reason": "有子把孝弟稱為為仁之本；此處弟据本章注釋讀作悌，不據此改寫全書所有弟字。"
  },
  {
    "coordinate": "1.4",
    "quote": "吾日三省吾身",
    "concepts": [
      "sanxing",
      "sheng",
      "zhong",
      "xin",
      "xue",
      "xi"
    ],
    "reason": "曾子以替人盡心、交友守信與溫習所傳，作為每日反省的三項內容。"
  },
  {
    "coordinate": "1.6",
    "quote": "弟子入则孝",
    "concepts": [
      "xiao",
      "ti",
      "xin",
      "ren",
      "xue"
    ],
    "reason": "孝悌、謹信、愛眾親仁在前，行有餘力學文在後，呈現躬行與學文關係。"
  },
  {
    "coordinate": "1.8",
    "quote": "过，则勿惮改",
    "concepts": [
      "junzi",
      "zhong",
      "xin",
      "guo",
      "gai"
    ],
    "reason": "莊重、忠信與改過共同構成此章君子自修的要求。"
  },
  {
    "coordinate": "1.12",
    "quote": "礼之用，和为贵",
    "concepts": [
      "li",
      "he",
      "zhongyong"
    ],
    "reason": "和須有禮的節制；本章可與中庸比較，不能把和簡化為無條件附和。"
  },
  {
    "coordinate": "1.13",
    "quote": "信近于义",
    "concepts": [
      "xin",
      "yi",
      "li",
      "gong"
    ],
    "reason": "守約先合義，恭敬合禮；信與恭都帶有規範條件。"
  },
  {
    "coordinate": "2.3",
    "quote": "道之以德，齐之以礼",
    "concepts": [
      "zheng",
      "de",
      "li",
      "xing",
      "chi",
      "dao"
    ],
    "reason": "政刑與德禮兩種治理效果被直接比較；此章道字作引導理解。"
  },
  {
    "coordinate": "2.4",
    "quote": "五十而知天命",
    "concepts": [
      "tianming",
      "xue",
      "li"
    ],
    "reason": "自述從志學至知命、從心不踰矩的生命歷程，可比較學習與規範的內化。"
  },
  {
    "coordinate": "2.7",
    "quote": "不敬，何以别乎",
    "concepts": [
      "xiao",
      "jing"
    ],
    "reason": "以不敬則無別辨明：供養不足以單獨構成孝。"
  },
  {
    "coordinate": "2.11",
    "quote": "温故而知新",
    "concepts": [
      "xue",
      "jiaoxue",
      "wengu",
      "zhixin"
    ],
    "reason": "溫故須能知新，並以此說明可以為師的條件。"
  },
  {
    "coordinate": "2.15",
    "quote": "学而不思则罔",
    "concepts": [
      "xue",
      "si",
      "xueersi"
    ],
    "reason": "章句成對指出只學不思與只思不學的問題。"
  },
  {
    "coordinate": "2.17",
    "quote": "知之为知之，不知为不知，是知也",
    "concepts": [
      "zhi",
      "wisdom",
      "jiaoxue"
    ],
    "reason": "據楊注，末一知指智慧，前面的知指知道；同章不能用全字替換混淆詞義。"
  },
  {
    "coordinate": "3.3",
    "quote": "人而不仁，如礼何",
    "concepts": [
      "ren",
      "li",
      "yue"
    ],
    "reason": "禮與樂並列，以仁作為有意義地對待二者的內在條件；此處樂指音樂。"
  },
  {
    "coordinate": "3.8",
    "quote": "绘事后素",
    "concepts": [
      "wenzhi",
      "shijiao",
      "jiaoxue",
      "li"
    ],
    "reason": "由繪畫底子引出禮後的追問；子夏舉一反三反過來啟發老師。這是文質的比較材料，非原文明說文質。"
  },
  {
    "coordinate": "4.5",
    "quote": "不以其道得之，不处也",
    "concepts": [
      "yili",
      "ren",
      "junzi",
      "fugui",
      "pin"
    ],
    "reason": "富貴的取捨以得之是否合道為條件，並要求君子顛沛中不違仁。"
  },
  {
    "coordinate": "4.15",
    "quote": "夫子之道，忠恕而已矣",
    "concepts": [
      "shu",
      "zhong",
      "forbearance",
      "dao"
    ],
    "reason": "曾子用忠恕解釋一以貫之；須保留孔子與曾子發言層次。"
  },
  {
    "coordinate": "4.16",
    "quote": "君子喻于义，小人喻于利",
    "concepts": [
      "yili",
      "yi",
      "li-benefit",
      "junzi",
      "xiaoren"
    ],
    "reason": "以義與利對舉辨君子小人的關注，兩字分開出現，不能只檢索義利連寫。"
  },
  {
    "coordinate": "5.12",
    "quote": "我不欲人之加诸我也",
    "concepts": [
      "forbearance",
      "shu"
    ],
    "reason": "子貢提出不欲受施亦不施人的志向，孔子指出實行的難度，可與15.24對讀。"
  },
  {
    "coordinate": "6.18",
    "quote": "文质彬彬",
    "concepts": [
      "wenzhi",
      "wen",
      "zhi-quality",
      "binbin",
      "junzi"
    ],
    "reason": "以野、史兩偏說明文與質須相配，最後落到君子的標準。"
  },
  {
    "coordinate": "6.22",
    "quote": "敬鬼神而远之",
    "concepts": [
      "wisdom",
      "zhi",
      "ren",
      "tianming",
      "guishen"
    ],
    "reason": "答問知時強調務民之義與敬而遠之；答仁又說先難後獲，兩段問答不可混成一句。"
  },
  {
    "coordinate": "6.23",
    "quote": "知者乐水，仁者乐山",
    "concepts": [
      "wisdom",
      "ren",
      "yue",
      "renle",
      "zhile"
    ],
    "reason": "以山水動靜比況知者與仁者；此處樂水樂山作喜好，末句知者樂指快樂，不是音樂。"
  },
  {
    "coordinate": "6.29",
    "quote": "中庸之为德也",
    "concepts": [
      "zhongyong",
      "de"
    ],
    "reason": "此章直接稱中庸為至德，並慨嘆其長期稀少。"
  },
  {
    "coordinate": "6.30",
    "quote": "己欲立而立人",
    "concepts": [
      "ren",
      "shu",
      "forbearance",
      "jiyuli",
      "jinqupi"
    ],
    "reason": "从博施濟眾之問轉到由己及人的實行方法，能近取譬是仁之方。"
  },
  {
    "coordinate": "7.8",
    "quote": "举一隅不以三隅反",
    "concepts": [
      "jiaoxue",
      "qi",
      "juyiyu"
    ],
    "reason": "啟發以學生已用力求解為條件，並要求由一隅推及三隅。"
  },
  {
    "coordinate": "11.16",
    "quote": "过犹不及",
    "concepts": [
      "zhongyong",
      "guoyoubuji"
    ],
    "reason": "師過與商不及被並列指出；子貢追問後，孔子明說過與不及同樣有偏。"
  },
  {
    "coordinate": "11.22",
    "quote": "求也退，故进之；由也兼人，故退之",
    "concepts": [
      "jiaoxue",
      "wen-hear",
      "xing-act"
    ],
    "reason": "同問聞斯行諸，冉求與仲由得到不同回答；孔子明說根據二人的退與勇進分別引導。"
  },
  {
    "coordinate": "12.1",
    "quote": "克己复礼为仁",
    "concepts": [
      "ren",
      "li",
      "keji"
    ],
    "reason": "答顏淵以克己復禮界定實行仁的路徑，再具體落到視聽言動。"
  },
  {
    "coordinate": "12.2",
    "quote": "己所不欲，勿施于人",
    "concepts": [
      "ren",
      "shu",
      "forbearance",
      "jing",
      "jisuobuyu",
      "wushiyuren"
    ],
    "reason": "答仲弓問仁，把待人敬慎與不強施所不欲並列；本章沒有恕字也可據內容關聯。"
  },
  {
    "coordinate": "12.7",
    "quote": "民无信不立",
    "concepts": [
      "xin",
      "zheng",
      "minxin",
      "min"
    ],
    "reason": "子貢問政的三次取捨把民信置於最後；這裡信偏重人民對治理者的信任。"
  },
  {
    "coordinate": "12.17",
    "quote": "政者，正也",
    "concepts": [
      "zheng",
      "zheng-upright",
      "de"
    ],
    "reason": "以治理者率先端正回答政治問題；正己與為政直接相連。"
  },
  {
    "coordinate": "12.22",
    "quote": "樊迟问仁。子曰：“爱人。”问知。子曰：“知人。”",
    "concepts": [
      "ren",
      "airen",
      "zhiren",
      "wisdom",
      "zhi"
    ],
    "reason": "仁答愛人，知答知人，後以舉直錯枉及選賢說明，保留二者区别與相聯。"
  },
  {
    "coordinate": "13.3",
    "quote": "必也正名乎",
    "concepts": [
      "zhengming",
      "zheng",
      "li",
      "yan",
      "ming-name"
    ],
    "reason": "正名連到言順、事成、禮樂與刑罰，須閱讀整個治理推論，不能只擷取口號。"
  },
  {
    "coordinate": "13.5",
    "quote": "诵《诗》三百",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "xue",
      "zheng"
    ],
    "reason": "以行政與外交不能運用所學質疑徒多的誦詩，構成知行遷移的反省。"
  },
  {
    "coordinate": "13.23",
    "quote": "君子和而不同",
    "concepts": [
      "zhongyong",
      "he",
      "hebutong",
      "junzi",
      "xiaoren"
    ],
    "reason": "君子和與小人同構成對照；作為中庸比較讀物，不能將二詞宣稱完全同義。"
  },
  {
    "coordinate": "14.13",
    "quote": "信乎，夫子不言",
    "concepts": [
      "xin",
      "yi",
      "yan"
    ],
    "reason": "此處信乎是詢問是否確實，不是德目守信；後文義然後取另屬取捨條件，特保留此消歧反例。"
  },
  {
    "coordinate": "14.38",
    "quote": "知其不可而为之",
    "concepts": [
      "chushi",
      "zhizhebuwei"
    ],
    "reason": "晨門者評孔子之行，可與隱者的選擇比較；評語的發言者不是孔子。"
  },
  {
    "coordinate": "15.9",
    "quote": "有杀身以成仁",
    "concepts": [
      "ren",
      "yong",
      "renren",
      "shi"
    ],
    "reason": "志士仁人以仁為生死取捨的條件，可與一般求勇相區分。"
  },
  {
    "coordinate": "15.24",
    "quote": "其恕乎",
    "concepts": [
      "shu",
      "forbearance",
      "jisuobuyu",
      "wushiyuren"
    ],
    "reason": "孔子以恕回答終身可行的一言，再以己所不欲勿施於人具體解說。"
  },
  {
    "coordinate": "15.39",
    "quote": "有教无类",
    "concepts": [
      "jiaoxue",
      "jiao",
      "youjiaowulei"
    ],
    "reason": "本章直接說明施教不以類別設限；不額外假造同章中不存在的教學方法。"
  },
  {
    "coordinate": "16.9",
    "quote": "学而知之者次也",
    "concepts": [
      "xue",
      "zhi",
      "xuezhizhi"
    ],
    "reason": "區分生知、學知、困而學及困而不學；字面排列不等於學生的固定能力等級。"
  },
  {
    "coordinate": "17.9",
    "quote": "诗，可以兴，可以观，可以羣，可以怨",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "xue",
      "guan"
    ],
    "reason": "孔子鼓勵學詩，列興觀群怨與事父事君、辨名物等用途。羣／群是本索引核可字形對應。"
  },
  {
    "coordinate": "17.13",
    "quote": "乡愿，德之贼也",
    "concepts": [
      "xiangyuan",
      "zhongyong",
      "de"
    ],
    "reason": "孔子明斥鄉愿；可作中庸不是無是非附和的反面比較，並非原文明說中庸。"
  },
  {
    "coordinate": "18.6",
    "quote": "鸟兽不可与同羣",
    "concepts": [
      "chushi",
      "yin",
      "dao"
    ],
    "reason": "隱者避世與孔子不離人群相對照，須保留不同說話人的立場。"
  },
  {
    "coordinate": "19.13",
    "quote": "学而优则仕",
    "concepts": [
      "chushi",
      "xue",
      "shi-office",
      "xueroushi"
    ],
    "reason": "子夏把仕與學互為有餘力時應做之事；據底本譯文，優不直接改寫為考試優秀。"
  },
  {
    "coordinate": "20.3",
    "quote": "不知命，无以为君子也",
    "concepts": [
      "tianming",
      "ming",
      "li",
      "zhiren",
      "junzi"
    ],
    "reason": "知命、知禮、知言分別聯到君子、立身、知人，形成互補的三項要求。"
  },
  {
    "coordinate": "1.3",
    "quote": "巧言令色",
    "primary": "ren",
    "concepts": [
      "ren",
      "qiaoyanlingse"
    ],
    "reason": "以花言與矯飾面色作仁德稀少的反例，適合追問外在討好與內在仁德的區別。不是一概否定說話婉轉或面色和悅。",
    "noteBoundary": "楊注引朱注說明巧言令色是刻意修飾以取悅人。",
    "canonicalId": 3,
    "historicalIds": [
      3
    ],
    "sourceRowSha256": "d7bf14e54db506eb277b113af47028f8337b5f4fd10157f887a716844cfa0070"
  },
  {
    "coordinate": "1.5",
    "quote": "敬事而信，节用而爱人",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "jing",
      "xin",
      "jie",
      "airen",
      "min"
    ],
    "reason": "治理千乘之國的要求涵蓋敬事守信、節用愛人與役民合時，把政治責任落到具體行為。",
    "noteBoundary": "據本章楊注，與民對言的人是較狹義的官吏階層；使民以時指不違農時，不將這一處愛人直接等同所有章句中的泛愛眾。",
    "canonicalId": 5,
    "historicalIds": [
      5
    ],
    "sourceRowSha256": "29ada3840ee2ae4b860b48e3332e7edd3e8aba171fbc89b60a6cfbd1fdc3898a"
  },
  {
    "coordinate": "1.7",
    "quote": "虽曰未学，吾必谓之学矣",
    "primary": "xue",
    "concepts": [
      "xue",
      "xian",
      "xiao",
      "xin",
      "pengyou"
    ],
    "reason": "子夏以重德、事親、事君、交友的實行判斷是否有學，連結生活關係與學習，而不是只看有無讀書名目。",
    "noteBoundary": "賢賢易色按本底本解為對妻子重德不重容貌；楊注同時記錄一般泛論重德的另一解。保留兩說，不把採用的一說說成唯一原義。",
    "canonicalId": 7,
    "historicalIds": [
      7
    ],
    "sourceRowSha256": "38483002ca10ea6e1f16fa6fa89add05ea0937ff0a0117605bee7ba989b4dde5"
  },
  {
    "coordinate": "1.9",
    "quote": "慎终，追远，民德归厚矣",
    "primary": "sang",
    "concepts": [
      "sang",
      "ji",
      "xiao",
      "de"
    ],
    "reason": "曾子把慎重辦理父母喪事、敬念先人與民德歸厚相聯，提供孝與喪祭的社會影響材料。",
    "noteBoundary": "楊注以裝殮埋葬之誠信與祭祀之敬解說慎終追遠；不推導為喪祭越奢侈越有德。",
    "canonicalId": 9,
    "historicalIds": [
      9
    ],
    "sourceRowSha256": "a5d063d3dfaadeaffaf6d823257c90bb23edf1b8b9d148b0d60718fa858ef2f9"
  },
  {
    "coordinate": "1.10",
    "quote": "温、良、恭、俭、让以得之",
    "primary": "gong",
    "concepts": [
      "gong",
      "jian-frugal",
      "rang",
      "zheng"
    ],
    "reason": "子貢以孔子的待人品格解釋其得聞邦政的方式，將恭儉讓與政治交往相連；是子貢對老師的敘述。",
    "noteBoundary": "末句其諸帶推測語氣；保留求與被告知的問答層次，不寫成孔子索取機密的事實。",
    "canonicalId": 10,
    "historicalIds": [
      10
    ],
    "sourceRowSha256": "786dc3eac083338817af314c1d5e53ecf779e894a4509fa492f5a42ca814f541"
  },
  {
    "coordinate": "1.11",
    "quote": "父在，观其志；父没，观其行",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "guan",
      "zhi-aspiration",
      "xing-act"
    ],
    "reason": "從父在與父沒兩種處境觀察子女志向和行為，並以延續父親之道論孝，可與知人的觀察方法相互參看。",
    "noteBoundary": "楊注的其指兒子，道取合理部分，三年不機械限定年數；不把本章概括為凡父命皆不可改。",
    "canonicalId": 11,
    "historicalIds": [
      11
    ],
    "sourceRowSha256": "ba0157eee97449a735e027df62c56fb843095bffd24136708d21faa062d8b7f8"
  },
  {
    "coordinate": "1.14",
    "quote": "敏于事而慎于言",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "junzi",
      "min-diligence",
      "shen",
      "yan"
    ],
    "reason": "不以飲食居處的安逸為先、做事勤敏、言語謹慎並就有道者匡正自己，共同構成本章的好學。",
    "noteBoundary": "君子在本章依楊注傾向有德者；正採匡正自身，不簡化成只向人問答案。",
    "canonicalId": 14,
    "historicalIds": [
      14
    ],
    "sourceRowSha256": "df38eed2b9a82fe02f6feb6e636ceb86205a43a19a011c3b0abfe178d8691376"
  },
  {
    "coordinate": "1.15",
    "quote": "告诸往而知来者",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "shijiao",
      "pin",
      "fu",
      "li"
    ],
    "reason": "由貧富處境中的不諂不驕進一步到樂道好禮，子貢借詩中切磋琢磨的譬喻發揮，呈現以已知推向未知的對話學習。",
    "noteBoundary": "貧而樂據楊注指樂於道；本章不是音樂之樂。切磋琢磨作詩意引申，不冒充全詩唯一主旨。",
    "canonicalId": 15,
    "historicalIds": [
      15
    ],
    "sourceRowSha256": "d4c9bacf2831cf255a3d3e9348c68f857c598e3236234b37a5510cb756e0cd26"
  },
  {
    "coordinate": "1.16",
    "quote": "患不知人也",
    "primary": "zhiren",
    "concepts": [
      "zhiren"
    ],
    "reason": "把憂慮重心從別人不理解自己轉為自己能否理解別人，直接收於知人路線。",
    "noteBoundary": "不由一句勸勉推導為不需要交往或不必表達自己。",
    "canonicalId": 16,
    "historicalIds": [
      16
    ],
    "sourceRowSha256": "f30182f9e0e9b2a67019336ddccc5f86955c444259d16f1d2ed6c23feae87fc8"
  },
  {
    "coordinate": "2.1",
    "quote": "为政以德",
    "primary": "zhengyide",
    "concepts": [
      "zhengyide",
      "zheng",
      "de"
    ],
    "reason": "以北辰與眾星的比喻說明德治的感召位置，與2.3德禮和政刑的比較可連續閱讀。",
    "noteBoundary": "此處使用原文政治比喻；不把舊註中的天文數據轉成新的自然科學結論。",
    "canonicalId": 17,
    "historicalIds": [
      17
    ],
    "sourceRowSha256": "91c30aa18e8b7227b558f735bc51d031050196d83c4662144e6bf0a4ce3ed2aa"
  },
  {
    "coordinate": "2.2",
    "quote": "思无邪",
    "primary": "shijiao",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "si"
    ],
    "reason": "孔子以思無邪概括詩，適合討論引詩、概括與思想理解的關係。",
    "noteBoundary": "楊注明說駉篇原句的思本為語首詞，孔子在此借作思想解；須區分原詩語境與引詩者賦予的用法。",
    "canonicalId": 18,
    "historicalIds": [
      18
    ],
    "sourceRowSha256": "6afe1619eae17c04439f103ddb661cab59cb7a354e082fe77bb92d9b62b22888"
  },
  {
    "coordinate": "2.5",
    "quote": "生，事之以礼；死，葬之以礼，祭之以礼",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "li",
      "sang",
      "ji"
    ],
    "reason": "無違經樊遲追問後落實為生養、安葬、祭祀皆合禮，孝與禮的規範相連，不能只取前兩字判為無條件服從。",
    "noteBoundary": "楊注將違解為違禮；關於針對三家僭禮的背景只作可能說明，不改成已證明的發言動機。",
    "canonicalId": 21,
    "historicalIds": [
      21
    ],
    "sourceRowSha256": "0773084f63fe75463e56067245f5c516bebe145bdb095b3bd09b121ecf32441e"
  },
  {
    "coordinate": "2.6",
    "quote": "父母唯其疾之忧",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu"
    ],
    "reason": "問孝的答語把父母的憂念與子女的生活行為相聯，依底本譯文理解為不妄為，使父母所憂限於疾病。",
    "noteBoundary": "楊注明列其代父母與其代孝子兩說，譯文取後說。異說保留；不是要求子女隱瞞疾病或把患病視作不孝。",
    "canonicalId": 22,
    "historicalIds": [
      22
    ],
    "sourceRowSha256": "c300d449e01fbe4a3852f46436103d36b6dc79b0b8f208c331daa489c0369dd1"
  },
  {
    "coordinate": "2.8",
    "quote": "色难",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu"
    ],
    "reason": "以色難追問侍奉父母的態度，指出代勞與供食本身未足以盡孝，與2.7的不敬形成可對讀材料。",
    "noteBoundary": "本頁採子女侍親時愉悅容色說，載侍奉父母容色的另一說但明言不取，不能稱兩說同採。弟子、先生在此為年幼、年長者；馔與《魯論》馂的異文讀法仍保留。",
    "canonicalId": 24,
    "historicalIds": [
      24
    ],
    "sourceRowSha256": "97013f37d486ad3f25c3ce491911cf716cd77f48a8586a35a028feee8ee753cc"
  },
  {
    "coordinate": "2.9",
    "quote": "回也不愚",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "xue",
      "zhiren"
    ],
    "reason": "孔子不以顏回當面不反駁就斷其愚，而看退後能有所發揮，將觀察學生與理解其學習相聯。",
    "noteBoundary": "退而省其私依底本譯為顏回退後研究；楊注亦記孔子察其日用實踐的一說。保留兩者，不把沉默一律當作理解。",
    "canonicalId": 25,
    "historicalIds": [
      25
    ],
    "sourceRowSha256": "b3a8edd029b5b4e9e9bd47db88c9682ce3e4d51af6f193a7088d6221eb3ed0d3"
  },
  {
    "coordinate": "2.10",
    "quote": "视其所以，观其所由，察其所安",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "guan"
    ],
    "reason": "從交往、所行方式與心之所安考察一個人，而非只凭表面言語，構成知人的多重觀察材料。",
    "noteBoundary": "所以按底本取所交往的人；楊注保留考其所為的另解。兩解不混成同一字的無差別譯義。",
    "canonicalId": 26,
    "historicalIds": [
      26
    ],
    "sourceRowSha256": "85ada0ae06e55d5ff4a2456ef0ea5252d53a48a582bd9ac424539162dace18fd"
  },
  {
    "coordinate": "2.12",
    "quote": "君子不器",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xue",
      "boxue",
      "qi-vessel"
    ],
    "reason": "用器皿用途固定的比喻說君子不局限於一端，與博學及人格養成相聯。",
    "noteBoundary": "博學是依楊注形成的思想關聯，原文未出博學二字；不推導為每位學生必須精通一切專業。",
    "canonicalId": 28,
    "historicalIds": [
      28
    ],
    "sourceRowSha256": "8a9249de70485cdb899aebab0e2da5c94b4c4ebb9da114163b09d1f947abccd3"
  },
  {
    "coordinate": "2.13",
    "quote": "先行其言而后从之",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xing-act",
      "yan"
    ],
    "reason": "回答如何成為君子時，把先做後說作為要求，收於言行關係與君子路線。",
    "noteBoundary": "先行其將說之言，不改寫成永遠不發言或已說出的承諾無須履行。",
    "canonicalId": 29,
    "historicalIds": [
      29
    ],
    "sourceRowSha256": "a9bc3df5a3cc8d2fd76dbdf3a390def965307c2d1578455019a4475c1cdab74b"
  },
  {
    "coordinate": "2.14",
    "quote": "君子周而不比",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xiaoren",
      "yi"
    ],
    "reason": "以周、比對比君子小人的結合方式，按楊注區分基於道義的團結與基於暫時利害的勾結。",
    "noteBoundary": "義是註釋所明示的關聯，不是原文義字命中；不能泛化為一切共同利益都屬小人結黨。",
    "canonicalId": 30,
    "historicalIds": [
      30
    ],
    "sourceRowSha256": "92dc6aabe0fda1cf073e3689bcdbd23ddae16e6a907bad3bf3790e566556289c"
  },
  {
    "coordinate": "2.16",
    "quote": "攻乎异端，斯害也已",
    "primary": "xue",
    "concepts": [
      "xue",
      "jiaoxue"
    ],
    "reason": "依本底本譯注讀作批判不正確議論以止其害，作為學習中如何辨析議論的材料；不由字面異端自動生成對不同觀點的否定。",
    "noteBoundary": "本頁採攻為批判攻擊、斯為承接、已為止；亦載攻作治、斯作指代、也已作語氣詞的通常讀法，卻明言其不合詞法句法。須保存異解及注者取捨，不稱本頁同時採納兩說；異端不直接套為後世諸子學派名單。",
    "canonicalId": 32,
    "historicalIds": [
      32
    ],
    "sourceRowSha256": "796e628b22de1bee0ea029d3338669b9fa852bb5cc55cadb19a44ecfaee47bcf"
  },
  {
    "coordinate": "2.18",
    "quote": "多闻阙疑，慎言其余",
    "primary": "shen",
    "concepts": [
      "shen",
      "wen-hear",
      "yan",
      "xing-act",
      "shi-office"
    ],
    "reason": "由子張求仕之問轉為多聞多見、存疑與慎言慎行，把求仕的準備落在少過少悔的言行，學習和從政相連。",
    "noteBoundary": "阙殆依楊注與阙疑互文同義；不能把有所疑問改寫成一律不聞不見不行。",
    "canonicalId": 34,
    "historicalIds": [
      34
    ],
    "sourceRowSha256": "a2393d491947e679857a3b3ff1ea011e328cd28e4e9838d4cf300c1201c7debd"
  },
  {
    "coordinate": "2.19",
    "quote": "举直错诸枉，则民服",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "zhi-upright",
      "zhiren",
      "min"
    ],
    "reason": "對民服之問，以舉直與舉枉的相反效果說明用人取向，連結政治、識人與正直。",
    "noteBoundary": "錯諸枉按楊注為把正直者置於邪曲者之上，不直接當成廢掉所有不合意者。",
    "canonicalId": 35,
    "historicalIds": [
      35
    ],
    "sourceRowSha256": "ca770f7b0a7c4c26f4fe2632ef82964fad389710ad5d728044dccf427298443e"
  },
  {
    "coordinate": "2.20",
    "quote": "举善而教不能，则劝",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "jing",
      "zhong",
      "xiao",
      "jiao",
      "min"
    ],
    "reason": "季康子問民敬、忠、勸，孔子逐項落到在上者莊重、孝慈、舉善與施教的作為，突出以身作則和教人的政治責任。",
    "noteBoundary": "教不能保留對能力不足者的教育，不轉為排斥或固定能力等級。",
    "canonicalId": 36,
    "historicalIds": [
      36
    ],
    "sourceRowSha256": "b9a3a3aba49e0eccdae084990ed027eb25b4ba8125e874d9f1e22f5313d72ed3"
  },
  {
    "coordinate": "2.21",
    "quote": "是亦为政，奚其为为政",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "xiao",
      "xiongdi",
      "shi-office"
    ],
    "reason": "以孝親友兄弟之風可以影響政治，回應只有出任官職才算為政的看法，適合討論家庭實行與公共生活的聯繫。",
    "noteBoundary": "施依楊注作延及；關於有政指政治或卿相大臣的解釋差別保留，不把引書語當作現存尚書的獨立原篇證明。",
    "canonicalId": 37,
    "historicalIds": [
      37
    ],
    "sourceRowSha256": "06c74a7f3d02e02d65453bc290cf471d032c6e81bd793f751f4e7aedefe3d96b"
  },
  {
    "coordinate": "2.22",
    "quote": "人而无信，不知其可也",
    "primary": "xin",
    "concepts": [
      "xin"
    ],
    "reason": "用車缺少關鍵部件便無法行駛的比喻，說明信對人際生活的支撐作用。",
    "noteBoundary": "行在車的比喻中是行駛，不據字面把此章加入所有德行或仕進的思想集合。",
    "canonicalId": 38,
    "historicalIds": [
      38
    ],
    "sourceRowSha256": "29177dc3234e0ab1101ac7daf07cb332cf44b6021b87656f662e1371589e3134"
  },
  {
    "coordinate": "2.23",
    "quote": "所损益，可知也",
    "primary": "li",
    "concepts": [
      "li",
      "zheng"
    ],
    "reason": "以夏、殷、周之禮的因襲與損益回答後世之問，表現制度傳承中的沿用和調整。",
    "noteBoundary": "按楊注問題限於禮儀制度，不擴張為能預言每一項未來事件。",
    "canonicalId": 39,
    "historicalIds": [
      39
    ],
    "sourceRowSha256": "bb8fed21fb0db577a933a33f880fc4e5d320ad136d9a48907d5a87f501be0b1e"
  },
  {
    "coordinate": "2.24",
    "quote": "见义不为，无勇也",
    "primary": "yi",
    "concepts": [
      "yi",
      "yong",
      "ji",
      "guishen"
    ],
    "reason": "前段反對祭祀不當祭之鬼以獻媚，後段以見義不為說明勇的欠缺；兩個判斷都涉及行為是否合宜。",
    "noteBoundary": "保留前後兩句各自所論，不說勇就是求鬼神庇佑；鬼據楊注通常指已死祖先，也有泛指情況。",
    "canonicalId": 40,
    "historicalIds": [
      40
    ],
    "sourceRowSha256": "3fb5a0789b2fcca29494142a7515cb29ab1251046a955359a842e6dd0e3f1758"
  },
  {
    "coordinate": "3.1",
    "quote": "八佾舞于庭",
    "primary": "li",
    "concepts": [
      "li",
      "yue",
      "zheng"
    ],
    "reason": "孔子批評季氏使用八佾，按本章禮制背景連結樂舞規模、政治身分和僭越，不將此章只讀成一般欣賞舞蹈。",
    "noteBoundary": "忍依底本取忍心而做，不直接套用今日容忍義；季氏究指季平子等人的異說保持為註釋中的考辨。",
    "canonicalId": 41,
    "historicalIds": [
      41
    ],
    "sourceRowSha256": "bd01f15ddb757227e7a9af991442fd20126650d3b59e4149122c1b68c0f12517"
  },
  {
    "coordinate": "3.2",
    "quote": "奚取于三家之堂",
    "primary": "li",
    "concepts": [
      "li",
      "ji",
      "shi-poetry",
      "yue"
    ],
    "reason": "三家用雍撤祭品，孔子以詩中諸侯助祭、天子主祭的內容反問其合宜性，表現祭禮與樂章使用情境的關係。",
    "noteBoundary": "引詩的意思與使用者身分需一起核對，不把所有唱詩活動都定作僭禮。",
    "canonicalId": 42,
    "historicalIds": [
      42
    ],
    "sourceRowSha256": "2fd584507263c14d573946963d3f58e8cd18da23a6e2441849a5d7057189e3d5"
  },
  {
    "coordinate": "3.4",
    "quote": "礼，与其奢也，宁俭",
    "primary": "li",
    "concepts": [
      "li",
      "jian-frugal",
      "sang"
    ],
    "reason": "林放問禮之本，答語分別以儉對奢、戚對易，將禮的本意和喪中的哀情置於鋪張及儀文周到之前。",
    "noteBoundary": "易按楊注是事情辦妥、儀文周到；不是把喪禮中的容易與困難相比，也不是否定一切必要儀節。",
    "canonicalId": 44,
    "historicalIds": [
      44
    ],
    "sourceRowSha256": "25e701afef1ad7b50cd5c1bcdd92b960b6e12b9e61436fd9cca686391a9e46ff"
  },
  {
    "coordinate": "3.5",
    "quote": "夷狄之有君，不如诸夏之亡也",
    "primary": "jun",
    "concepts": [
      "jun",
      "zheng"
    ],
    "reason": "本章比較夷狄與諸夏的有君、無君，可用來辨析歷史政治稱謂與句義；其比較方向本身需要連同異說閱讀。",
    "noteBoundary": "底本正文譯為有君仍不及諸夏無君；楊注另載夷狄尚有賢君而中原沒有的讀法。不消除兩說差別，也不將古代分類用作今日民族優劣判定。",
    "canonicalId": 45,
    "historicalIds": [
      45
    ],
    "sourceRowSha256": "1fed88020962ae2a641c914532571b8b8fd6d62883c70f9464b9e39bd99b51a1"
  },
  {
    "coordinate": "3.6",
    "quote": "女弗能救与",
    "primary": "li",
    "concepts": [
      "li",
      "ji",
      "chen",
      "guishen"
    ],
    "reason": "季氏祭泰山引發孔子對其臣冉有的責問，禮的界限與在職者能否阻止不合禮行為相連。",
    "noteBoundary": "救在問答中是阻止僭禮；泰山不如林放是反問，不把章句當作神靈是否實際接受祭祀的記錄。",
    "canonicalId": 46,
    "historicalIds": [
      46
    ],
    "sourceRowSha256": "9a2d043f0563e622b486b65a1886b9f9bcf7caeb2a9df0719f034b47441b8e2a"
  },
  {
    "coordinate": "3.7",
    "quote": "揖让而升，下而饮",
    "primary": "li",
    "concepts": [
      "li",
      "junzi",
      "rang"
    ],
    "reason": "用射禮說明君子的競爭仍有揖讓與飲酒的規範，爭的方式而非只求勝負是本章重點。",
    "noteBoundary": "無所爭後接射的具體情境；不據前半句取消後半句明示的合禮競賽。",
    "canonicalId": 47,
    "historicalIds": [
      47
    ],
    "sourceRowSha256": "cb5859fe92b314f9f6108269dee6bcc326ebd9a98765052529d46d8d08c83034"
  },
  {
    "coordinate": "3.9",
    "quote": "文献不足故也",
    "primary": "li",
    "concepts": [
      "li",
      "wen",
      "xian",
      "xue"
    ],
    "reason": "孔子區分能談夏殷之禮與能否得到充分證據，以文獻不足交代徵證的限制，可連到有依據的學習方法。",
    "noteBoundary": "文獻按楊注兼指典籍與賢者，不只等於今天的書面資料；未足徵不是證明夏殷無禮。",
    "canonicalId": 49,
    "historicalIds": [
      49
    ],
    "sourceRowSha256": "64dfcd4a7710c0d988ebbbbd72297808cb840b846ae0fafbb48890278649f479"
  },
  {
    "coordinate": "3.10",
    "quote": "吾不欲观之矣",
    "primary": "ji",
    "concepts": [
      "ji",
      "li"
    ],
    "reason": "以孔子對禘祭的觀看態度呈現他對祭禮的評判，可與3.11禘之說接讀。",
    "noteBoundary": "底本楊注以魯君沿襲天子祭典解說背景；原文只明示既灌後不欲觀，不補造現場細節或新的發言原因。",
    "canonicalId": 50,
    "historicalIds": [
      50
    ],
    "sourceRowSha256": "daa5b522c0ab9ad1759648c201e29ce662db2ea30dffcc94f64765eb3c8d7a08"
  },
  {
    "coordinate": "3.11",
    "quote": "知其说者之于天下也",
    "primary": "li",
    "concepts": [
      "li",
      "ji",
      "zheng"
    ],
    "reason": "問禘之說，答語把理解祭禮與處理天下相連，以指掌作比喻；可考察禮與政治秩序的聯繫。",
    "noteBoundary": "不知也在楊注中被解為不直言僭礼的曲折表達；不要孤立地算成孔子知識缺漏，亦須保留示同置或視的註釋異說。",
    "canonicalId": 51,
    "historicalIds": [
      51
    ],
    "sourceRowSha256": "e7095674e62ae55c4ff3c064c5999c83b1d314296fc0e8f72f8c72c45e066296"
  },
  {
    "coordinate": "3.12",
    "quote": "祭如在，祭神如神在",
    "primary": "ji",
    "concepts": [
      "ji",
      "jing",
      "cheng",
      "guishen"
    ],
    "reason": "祭如在與親自參與相連，表現祭祀的認真態度；敬、誠是從這種行為讀出的主題關聯。",
    "noteBoundary": "依底本與讀參與、如不祭意譯為不請人代理；注中另載與作贊同及不同斷句，並明言不同意該義。保存另說與注者取捨，不稱兩說同時採納；祭如在不是神靈存在的實證。",
    "canonicalId": 52,
    "historicalIds": [
      52
    ],
    "sourceRowSha256": "2528ba5ed07ed93f81fcb501b7dc468b8d894e32a4aa88e227f0b4a56d5e3193"
  },
  {
    "coordinate": "3.13",
    "quote": "获罪于天，无所祷也",
    "primary": "tian",
    "concepts": [
      "tian",
      "guishen",
      "zheng"
    ],
    "reason": "以媚奧與媚灶的問話引出獲罪於天便無可祈禱的回應，可討論祈禱、取悅權勢與行為是否合宜。",
    "noteBoundary": "楊注把對話視作比喻並列不同政治指涉，明說正意只能揣想，且認為第二種請教說比較近情理；保留此傾向與不確定性，不把人物與奧灶的對應寫成確證。",
    "canonicalId": 53,
    "historicalIds": [
      53
    ],
    "sourceRowSha256": "c59354427409a4b018961805a2f79165a046c08f2edd1a8e25c375d69edb76b1"
  },
  {
    "coordinate": "3.14",
    "quote": "郁郁乎文哉！吾从周",
    "primary": "li",
    "concepts": [
      "li",
      "wen",
      "zheng"
    ],
    "reason": "孔子以周參照夏商二代形成的豐富禮制表達從周立場，接續制度因革和文化傳承的路線。",
    "noteBoundary": "監於二代是參照兩代；不把從周改成無條件復刻一切過去制度。",
    "canonicalId": 54,
    "historicalIds": [
      54
    ],
    "sourceRowSha256": "6d9640ebc808cd3d5f6c8c0fbefaa81e58625ff0b2f5af52cf6269d2562db82b"
  },
  {
    "coordinate": "3.15",
    "quote": "入太庙，每事问",
    "primary": "li",
    "concepts": [
      "li",
      "wen-ask",
      "xue"
    ],
    "reason": "孔子以每事問回應別人對其知禮的質疑，示範詢問本身可以是尊重禮的做法。",
    "noteBoundary": "不能僅由問得多判定無知，也不把每事問改成任何場合一律不作自主判斷。",
    "canonicalId": 55,
    "historicalIds": [
      55
    ],
    "sourceRowSha256": "c2eb98c8ce3b952769a32bb69f237a6ddbe6cb987a27a8e952789d1f40d48cb0"
  },
  {
    "coordinate": "3.16",
    "quote": "射不主皮",
    "primary": "li",
    "concepts": [
      "li",
      "dao"
    ],
    "reason": "依本章的禮射情境，以不專看穿透箭靶說明不能只用力量作同一標準，規範與競技目的需要一起看。",
    "noteBoundary": "楊注明區分禮射與軍中武射；不能把禮射準則直接當成所有戰鬥或運動的規則。道在此是古時規矩。",
    "canonicalId": 56,
    "historicalIds": [
      56
    ],
    "sourceRowSha256": "77f94b88610737dc3c0ac9e8fe5d97af8b1d0967a1e92fa7fa07405b83c12d7f"
  },
  {
    "coordinate": "3.17",
    "quote": "尔爱其羊，我爱其礼",
    "primary": "li",
    "concepts": [
      "li",
      "ji",
      "ai"
    ],
    "reason": "子貢欲去告朔饩羊，孔子則看重殘存的禮，呈現節省祭品與保存制度象徵之間的不同考量。",
    "noteBoundary": "愛在此按楊注為可惜，並非愛人概念的直接用例；底本說禮只餘形式，不刪去這個爭論背景。",
    "canonicalId": 57,
    "historicalIds": [
      57
    ],
    "sourceRowSha256": "a51c01661a0d0b14e7d2809a0a19fd915c768aa1071cf4019b1aedc6d492bb9f"
  },
  {
    "coordinate": "3.18",
    "quote": "事君尽礼，人以为谄也",
    "primary": "li",
    "concepts": [
      "li",
      "jun",
      "chen"
    ],
    "reason": "把事君盡禮與被人視作諂媚對照，可辨析行禮的行為與旁觀者的判斷並不必然一致。",
    "noteBoundary": "既不能由恭敬外貌便斷為諂媚，也不能反過來把所有討好權勢都認作盡禮。",
    "canonicalId": 58,
    "historicalIds": [
      58
    ],
    "sourceRowSha256": "fb2268b64850ae7d433b2b268563f0a6ba7bc1c500794d3b23adfadce6a3d933"
  },
  {
    "coordinate": "3.19",
    "quote": "君使臣以礼，臣事君以忠",
    "primary": "zhong",
    "concepts": [
      "zhong",
      "li",
      "jun",
      "chen"
    ],
    "reason": "君與臣各有對應要求：君用臣以禮，臣事君以忠；保留雙向責任而非只摘臣的一半。",
    "noteBoundary": "原文回答特定君臣關係，不直接將其作為當代所有私人關係的服從規則。",
    "canonicalId": 59,
    "historicalIds": [
      59
    ],
    "sourceRowSha256": "78a171c9f1f5ebf6eaa7b0183dc73835e9c6d35a76da7f4b5ac042143535ff75"
  },
  {
    "coordinate": "3.20",
    "quote": "乐而不淫，哀而不伤",
    "primary": "shijiao",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "yue"
    ],
    "reason": "對關雎的評述兼顧情感表達及其分寸，適合詩樂閱讀中辨析喜樂與哀情。",
    "noteBoundary": "淫按楊注指過分失當；注中另載本章據樂章組合而說的解釋，保留關雎文本本身並無悲哀情調的問題。",
    "canonicalId": 60,
    "historicalIds": [
      60
    ],
    "sourceRowSha256": "0db3d49f0d0a07f0de909b3a87b8aa8e65788ec3f3cf14187ba7d48d6a26fe18"
  },
  {
    "coordinate": "3.21",
    "quote": "成事不说，遂事不谏，既往不咎",
    "primary": "yan",
    "concepts": [
      "yan",
      "shen",
      "ji",
      "zheng"
    ],
    "reason": "哀公問社，宰我把栗說成使民戰栗，孔子隨後談成事、遂事與既往，宜連同發言背景討論言語及其後果。",
    "noteBoundary": "依底本呈現孔子對宰我的責備；不割掉前文，把既往不咎說成任何已發生錯誤都不得處理的通則。社主採木牌說，另載立社栽樹說但注者稱未必可信；兩說及取捨均保存。",
    "canonicalId": 61,
    "historicalIds": [
      61
    ],
    "sourceRowSha256": "7ca87f50ec426072432f9406788f219cd16ccb49faa309553033df0f8811901f"
  },
  {
    "coordinate": "3.22",
    "quote": "管氏而知礼，孰不知礼",
    "primary": "li",
    "concepts": [
      "li",
      "jian-frugal",
      "zhiren",
      "qi-vessel"
    ],
    "reason": "對管仲的評議由器量轉到儉與知禮，藉具體財用、官事和設施說明評人的標準。",
    "noteBoundary": "三歸採底本市租說；注中所列娶三國之女、三處家庭、采邑、泉幣府庫四說被注者判為不正確，保留其內容與取捨。此章批評儉與禮，不抹除全書其他章對管仲功業的評價。",
    "canonicalId": 62,
    "historicalIds": [
      62
    ],
    "sourceRowSha256": "605bc064d21fc43864d0ff57c601dc37e8aada6cacda6535958ab82ff9a94d44"
  },
  {
    "coordinate": "3.23",
    "quote": "纯如也，皦如也，绎如也",
    "primary": "yue",
    "concepts": [
      "yue"
    ],
    "reason": "孔子向樂官說音樂從起奏、延續到完成的層次，以和諧、清晰、連綿形容演奏，保留真正的音樂內容。",
    "noteBoundary": "大師是樂官之長，語讀告訴；此章的樂是音樂，不併作心理快樂或樂學的同義用例。",
    "canonicalId": 63,
    "historicalIds": [
      63
    ],
    "sourceRowSha256": "c11d00bceaab97c616c60665151ed9b353c32f1a4571473594974fec94feef02"
  },
  {
    "coordinate": "3.24",
    "quote": "天将以夫子为木铎",
    "primary": "tianming",
    "concepts": [
      "tianming",
      "tian",
      "chushi",
      "dao"
    ],
    "reason": "儀封人用木鐸比喻孔子的引導作用，回應弟子對失官的憂慮，連結行道、出處與天命觀。",
    "noteBoundary": "這是儀封人的發言，非孔子自宣天命；喪按楊注是失官，不是喪葬。儀的具體地望仍依註釋保留不確定。",
    "canonicalId": 64,
    "historicalIds": [
      64
    ],
    "sourceRowSha256": "ec0b1caf48dce0a51a0253256b72f45b25bd796fc29026037a8a70d72cb7631a"
  },
  {
    "coordinate": "3.25",
    "quote": "尽美矣，未尽善也",
    "primary": "yue",
    "concepts": [
      "yue",
      "de",
      "zheng"
    ],
    "reason": "對韶、武皆言盡美而在盡善與否上有別，提供音樂審美與政治倫理可能有不同評價的材料。",
    "noteBoundary": "楊注以美或指聲音、善或指內容並聯到禪讓與征伐，語氣是可能解釋；不把這種解釋升格為原文明確定義。",
    "canonicalId": 65,
    "historicalIds": [
      65
    ],
    "sourceRowSha256": "dc0328811b1347ed7b3dfc5146b1814028ccd6256faf0d8cd259ca9f34716441"
  },
  {
    "coordinate": "3.26",
    "quote": "居上不宽，为礼不敬，临丧不哀",
    "primary": "jing",
    "concepts": [
      "jing",
      "kuan",
      "li",
      "sang",
      "zheng"
    ],
    "reason": "以在上、行禮、臨喪三種情境分說寬、敬、哀的欠缺，顯示角色行為需有相應態度。",
    "noteBoundary": "不是只按外在儀式判斷禮是否成立，也不把三種情境中的要求互相混作一條。",
    "canonicalId": 66,
    "historicalIds": [
      66
    ],
    "sourceRowSha256": "739e66ce3a568ae2f1dfee6ffa1b09746e972915a13e8898898818adb7848957"
  },
  {
    "coordinate": "4.1",
    "quote": "里仁为美",
    "primary": "ren",
    "concepts": [
      "ren",
      "wisdom"
    ],
    "reason": "選擇處身之所與仁德相聯，並用知評判選擇，適合仁與智慧的對讀。",
    "noteBoundary": "楊注明說究竟專指擇居，還是兼及擇鄰、擇業、擇友不能肯定；不只因篇名里仁就把里當成已確證地名。",
    "canonicalId": 67,
    "historicalIds": [
      67
    ],
    "sourceRowSha256": "ff1d3990f13680bd6d37cabb8b1089c80270a0e055b515c89e05ef0730c8bd2e"
  },
  {
    "coordinate": "4.2",
    "quote": "仁者安仁，知者利仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "wisdom",
      "li-benefit"
    ],
    "reason": "以久處窮約、安樂的考驗起筆，再區分仁者安仁與知者利仁，表現行仁的不同根據。",
    "noteBoundary": "利仁依底本譯文指認識行仁的長遠利益；不能把本章直接併成唯利是圖的反例，也不把樂當音樂。",
    "canonicalId": 68,
    "historicalIds": [
      68
    ],
    "sourceRowSha256": "5f13ab998a08c4308cc6e526cf4f5e2ecbe43e0d343f3dbc89489c4260f69787"
  },
  {
    "coordinate": "4.3",
    "quote": "唯仁者能好人，能恶人",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhiren"
    ],
    "reason": "仁者對人有好有惡，按楊注重點在好惡得當，並非不作是非判斷的一概親愛。",
    "noteBoundary": "惡人在此是厭惡人的動賓結構，不誤讀為單稱壞人；好惡須連同仁者的判斷來理解。",
    "canonicalId": 69,
    "historicalIds": [
      69
    ],
    "sourceRowSha256": "b227072e99a06bffa0fda87feed1ad851bbb476ce56a100c00507883e5d2872d"
  },
  {
    "coordinate": "4.4",
    "quote": "苟志于仁矣，无恶也",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhi-aspiration"
    ],
    "reason": "將立志行仁與無惡相連，可與4.6對行仁用力的追問連讀，區分志向與實踐。",
    "noteBoundary": "無惡按本底本譯為總沒有壞處；不把本章惡字未經辨義就合成4.3好惡人的同一詞義。",
    "canonicalId": 70,
    "historicalIds": [
      70
    ],
    "sourceRowSha256": "43ad340682597d07cd36ffe94ae4cf4bd8507305acc082c78064295732e0d909"
  },
  {
    "coordinate": "4.6",
    "quote": "有能一日用其力于仁矣乎",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhi-aspiration",
      "xing-act"
    ],
    "reason": "由好仁、惡不仁的志趣轉向實際用力，孔子的反問著重能否把力量用於行仁。",
    "noteBoundary": "末句蓋帶推測、我未之見是說話者所見的限度；不抹去保留語氣，寫成已窮盡所有人的能力判定。",
    "canonicalId": 72,
    "historicalIds": [
      72
    ],
    "sourceRowSha256": "29d76e415ef0d4b7fbea1f4c9cc76eae20abd435d30a0c301731c03851ae967b"
  },
  {
    "coordinate": "4.7",
    "quote": "观过，斯知仁矣",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "guo",
      "guan"
    ],
    "reason": "從所犯過失考察一個人，連到知人、觀察與過失的路線；按本注仁通人，不因原文仁字就加入仁德主題選讀。",
    "noteBoundary": "楊注此處仁通人。字詞檢索仍保留仁字命中，思想關聯則按知人的句義處理，兩種來源不互相覆蓋。",
    "canonicalId": 73,
    "historicalIds": [
      73
    ],
    "sourceRowSha256": "86b332f36bfab2f92cb75a5f7cc85e6fdcb195163018b513a227a8124195493a"
  },
  {
    "coordinate": "4.8",
    "quote": "朝闻道，夕死可矣",
    "primary": "dao",
    "concepts": [
      "dao",
      "wen-hear",
      "si-death"
    ],
    "reason": "用朝聞與夕死的強烈對比表達聞道的重要，適合討論生命中所求的價值。",
    "noteBoundary": "道依底本譯作真理；朝聞與夕死用以表達價值取捨，不換算為學習時限，也不轉成要求學生傷害自身或以死亡證明求道的實踐任務。",
    "canonicalId": 74,
    "historicalIds": [
      74
    ],
    "sourceRowSha256": "7f0782db7e55a8dc30c73850e19c119c49fe0bd87171ddab94b028f14f730eaa"
  },
  {
    "coordinate": "4.9",
    "quote": "士志于道，而耻恶衣恶食者",
    "primary": "shi",
    "concepts": [
      "shi",
      "dao",
      "zhi-aspiration",
      "chi",
      "pin"
    ],
    "reason": "指出志道與以衣食簡陋為恥的不一致，把士的志向和物質處境下的態度相連。",
    "noteBoundary": "恥是以粗衣粗食為恥的動詞用法；這是對志向是否一致的評議，不以穿著本身推斷任何人的道德。",
    "canonicalId": 75,
    "historicalIds": [
      75
    ],
    "sourceRowSha256": "a273b4a4fa7497850d32a249a72a0223b9d48f6f26ae7cd1ee55fe43236b9afd"
  },
  {
    "coordinate": "4.10",
    "quote": "义之与比",
    "primary": "yi",
    "concepts": [
      "yi",
      "junzi",
      "chushi"
    ],
    "reason": "君子不預設一定做或一定不做，依義作取捨；出處進退是楊注所引可比較的實踐情境。",
    "noteBoundary": "無適無莫在註中有多說，本底本採不固執於一種做法；不是任意而行，取捨條件仍是義。",
    "canonicalId": 76,
    "historicalIds": [
      76
    ],
    "sourceRowSha256": "3e8bc97b434f674d9c640337f1e2422ab96c24381ec189d405c5e5ffe2f2285f"
  },
  {
    "coordinate": "4.11",
    "quote": "君子怀德，小人怀土",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xiaoren",
      "de",
      "xing",
      "hui-kindness"
    ],
    "reason": "由德與土、刑與惠的對照呈現君子小人關注所在，保留兩組不同內容的比較。",
    "noteBoundary": "刑依楊注是法度，不單是刑罰；注中文字形缺位按已接受固定來源原樣保留。土主譯鄉土、注稱田土亦通。惠是所獲恩惠，與施惠於人的用例分辨。",
    "canonicalId": 77,
    "historicalIds": [
      77
    ],
    "sourceRowSha256": "1177b4e62eb3d610fc45369ac39b3d121fc8318429fc34d054e7fe67c5c65428"
  },
  {
    "coordinate": "4.12",
    "quote": "放于利而行，多怨",
    "primary": "li-benefit",
    "concepts": [
      "li-benefit",
      "yuan",
      "xing-act"
    ],
    "reason": "以個人利益為行動依據與招致怨恨相聯，可對照義利取捨和人際後果。",
    "noteBoundary": "放依楊注讀仿、作依據；多怨不是主張凡有利益的行動必然錯誤。",
    "canonicalId": 78,
    "historicalIds": [
      78
    ],
    "sourceRowSha256": "7f2d2bcf2bf51d813358bd91533faff6e53e7580455451775a2de6a2595acead"
  },
  {
    "coordinate": "4.13",
    "quote": "能以礼让为国乎",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "li",
      "rang"
    ],
    "reason": "把治國能否落實禮讓作為如何對待禮的問題，連結政治內容與禮儀形式。",
    "noteBoundary": "何有依楊注為有何困難；若失去禮讓內容，只保留儀節形式，不能據此自稱盡禮。",
    "canonicalId": 79,
    "historicalIds": [
      79
    ],
    "sourceRowSha256": "27607498ad61038066490f6ea3936a55cf937665dafb57f77fcf3b1ecc22a57f"
  },
  {
    "coordinate": "4.14",
    "quote": "不患无位，患所以立",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "xiuyi"
    ],
    "reason": "把關注由沒有職位、無人知己轉到任職本領與可為人知的實質，適合出處與修己的對讀。",
    "noteBoundary": "修己是從追求自身本領形成的思想關聯；立依本章楊注通位，不把字面立泛作站立。",
    "canonicalId": 80,
    "historicalIds": [
      80
    ],
    "sourceRowSha256": "5ec54b1a38659a49906314384dc30fab2825ec671a16e1ed50e230c948b8efa7"
  },
  {
    "coordinate": "4.17",
    "quote": "见贤思齐焉，见不贤而内自省也",
    "primary": "sheng",
    "concepts": [
      "sheng",
      "xian",
      "si",
      "xue"
    ],
    "reason": "見賢向之看齊，見不賢則反觀自己，兩種對人觀察都轉成自身學習與反省。",
    "noteBoundary": "不能只把見不賢用來評斷別人而略去內自省的主語轉換。",
    "canonicalId": 83,
    "historicalIds": [
      83
    ],
    "sourceRowSha256": "08978558c175de2ea198cdabbaf309ab2b26d88df1b5a99a516a5cb4e9f329af"
  },
  {
    "coordinate": "4.18",
    "quote": "事父母几谏",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu",
      "jing",
      "yuan"
    ],
    "reason": "孝親包含對不對之處婉轉勸止；不被採納時仍敬而不怨，呈現勸諫方式和態度的要求。",
    "noteBoundary": "幾是輕微婉轉，違依本章楊注是觸忤，勞是憂愁；不能僅取不違就刪去起首應勸諫的內容。",
    "canonicalId": 84,
    "historicalIds": [
      84
    ],
    "sourceRowSha256": "45b1cdaf95b4430ec5ae606a7e06c6f370ec2f85d623ca11fc61ea14ac938cc0"
  },
  {
    "coordinate": "4.19",
    "quote": "游必有方",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu"
    ],
    "reason": "父母在世與遠遊的去處相連，出遠門仍須有可知的一定去向；保留後句對實際出遊的說明。",
    "noteBoundary": "不能只摘不遠遊而刪去游必有方，造成任何外出都被絕對禁止的讀法。",
    "canonicalId": 85,
    "historicalIds": [
      85
    ],
    "sourceRowSha256": "aad4e6efd445c279efd1ae1a0009a57040458108f64dde2888c7d75b755e283c"
  },
  {
    "coordinate": "4.20",
    "quote": "三年无改于父之道，可谓孝矣",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "dao"
    ],
    "reason": "重述延續父親之道與孝的關係，依本章所指1.11讀其志行及合理部分的說明。",
    "noteBoundary": "此固定底本的譯文欄重複原句，註釋只指1.11；保留這項來源現況，不暗中補譯。4.20與1.11是不同章的原ID，不能因內容相關就刪除或合併歷史。",
    "canonicalId": 86,
    "historicalIds": [
      86
    ],
    "sourceRowSha256": "8d7b0ea3658a282918e492f54fc16015ed5f243d5065928062c77d46e646ee3d"
  },
  {
    "coordinate": "4.21",
    "quote": "一则以喜，一则以惧",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu"
    ],
    "reason": "知父母年齡同時帶來高壽之喜與年高之憂，呈現親情中的雙重感受。",
    "noteBoundary": "喜與懼都以父母之年為語境；不從這一章推算未載的具體年齡或生命預期。",
    "canonicalId": 87,
    "historicalIds": [
      87
    ],
    "sourceRowSha256": "0af98c6bf5684779762057a1278dcb41a5d915502549f431c9d19bd324b494be"
  },
  {
    "coordinate": "4.22",
    "quote": "耻躬之不逮也",
    "primary": "yan",
    "concepts": [
      "yan",
      "xing-act",
      "chi"
    ],
    "reason": "古人不輕易出口的理由是擔心自身實行趕不上所言，連結言行、羞恥與自我要求。",
    "noteBoundary": "躬之不逮是行動未能跟上的問題；不把謹言理解成單純沉默越多越好。",
    "canonicalId": 88,
    "historicalIds": [
      88
    ],
    "sourceRowSha256": "bd031f75fcf9f119689a46df9b0570353622f768245407f8b7f3a2a2ce6030ee"
  },
  {
    "coordinate": "4.23",
    "quote": "以约失之者鲜矣",
    "primary": "xiuyi",
    "concepts": [
      "xiuyi",
      "guo",
      "jie"
    ],
    "reason": "本章說因自我節制約束而犯過失的情況少，故收於修己和節制；保留因約而失的句法，不只憑約字加入節儉路線。",
    "noteBoundary": "楊注此處取約束，並列全書約的窮困義；節儉義雖已見荀子卻被注者稱未必適用此處。保留該語氣，不改成原文已絕對否定節儉。",
    "canonicalId": 89,
    "historicalIds": [
      89
    ],
    "sourceRowSha256": "3b54ea671709ea31ba64177e44fd65fd75796f0ff54a7e71f4a1fe772b75de01"
  },
  {
    "coordinate": "4.24",
    "quote": "君子欲讷于言而敏于行",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "yan",
      "min-diligence",
      "xing-act"
    ],
    "reason": "以言語謹慎、做事勤敏相對，呈現君子在說與做上的不同要求，可與1.14及4.22對讀。",
    "noteBoundary": "本章訥依楊注由語言遲鈍引出謹慎，不把口語能力較弱本身等同道德更高。",
    "canonicalId": 90,
    "historicalIds": [
      90
    ],
    "sourceRowSha256": "46fc85f110145aebb38755ed757a3562566188528caef269172abda7604813f0"
  },
  {
    "coordinate": "4.25",
    "quote": "德不孤，必有邻",
    "primary": "de",
    "concepts": [
      "de",
      "pengyou"
    ],
    "reason": "以德不孤和有鄰表達志同道合者相親，從德行連到同伴關係。",
    "noteBoundary": "鄰按底本譯文是志同道合的伙伴，不限於住址相鄰；這是章句所表達的信念，不作個人必然獲得社交回報的保證。",
    "canonicalId": 91,
    "historicalIds": [
      91
    ],
    "sourceRowSha256": "2d780cd8e72b1bd0e9c2155de13daa0baff3e2c775294a305e3c25a953c7a33e"
  },
  {
    "coordinate": "4.26",
    "quote": "事君数，斯辱矣；朋友数，斯疏矣",
    "primary": "pengyou",
    "concepts": [
      "pengyou",
      "jun",
      "chen",
      "yan"
    ],
    "reason": "子游就事君與交友指出反覆煩瑣可能招辱致疏，楊注聯到忠告善道而不可則止，供相處分寸的閱讀。",
    "noteBoundary": "數讀屢屢，不是數量的數；與4.18的幾諫對讀時保留關係情境差別，不概括成永不勸人。",
    "canonicalId": 92,
    "historicalIds": [
      92
    ],
    "sourceRowSha256": "fde57222b206dd3a3d686f3abcf14560e61fef62c36167f9cdfa309e43752ef2"
  },
  {
    "coordinate": "5.1",
    "quote": "虽在缧绁之中，非其罪也",
    "primary": "zhiren",
    "concepts": [
      "zhiren"
    ],
    "reason": "孔子判斷公冶長可婚，不以其曾被監禁就斷其有罪，呈現評人需分辨遭遇與實際責任。",
    "noteBoundary": "非其罪是本章敘述的判斷，原文沒有交代案情；不補造入獄原因或把現代個案套作同一事實。",
    "canonicalId": 93,
    "historicalIds": [
      93
    ],
    "sourceRowSha256": "586e82dac896c8b26e1dd57aa10805ce1f50fdfa1096929ab9e8fc0931187fec"
  },
  {
    "coordinate": "5.2",
    "quote": "邦有道，不废；邦无道，免于刑戮",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "bangyoudao",
      "bangwudao",
      "xing"
    ],
    "reason": "以有道、無道兩種政治處境中的行止評南容，收於出處與自處路線。",
    "noteBoundary": "這是對南容的具體評語，不推成只要懂得自處便必然免於一切刑禍的保證。",
    "canonicalId": 94,
    "historicalIds": [
      94
    ],
    "sourceRowSha256": "cf5a7db0c63da55efe0331aa64013668f957b06c40fcbbfc2f9efbddd7898ddd"
  },
  {
    "coordinate": "5.3",
    "quote": "君子哉若人",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "zhiren",
      "xue"
    ],
    "reason": "稱許子賤為君子，又以魯國君子作為他取法的來源，連結人物評價與品德學習環境。",
    "noteBoundary": "不由這句稱許推算未載的師承名單；本章人物年代資料不是這次主題審訂的新增歷史結論。",
    "canonicalId": 95,
    "historicalIds": [
      95
    ],
    "sourceRowSha256": "2e321c5098689da06f352db443be67897aa27377b9efd86acd05c284fbcb29df"
  },
  {
    "coordinate": "5.4",
    "quote": "瑚琏也",
    "primary": "qi-vessel",
    "concepts": [
      "qi-vessel",
      "zhiren",
      "li"
    ],
    "reason": "孔子以尊貴祭器瑚璉譬子貢，具體說明論人的器之喻，可與2.12君子不器比較其語境差別。",
    "noteBoundary": "器在此不是任意貶低，也不能抹去與不器章之間值得思考的差別；不把兩章機械合為同一句人物等第。",
    "canonicalId": 96,
    "historicalIds": [
      96
    ],
    "sourceRowSha256": "83fed44f8d70280144a3eb5583048564f987a80293904b0968b747329a8136fc"
  },
  {
    "coordinate": "5.5",
    "quote": "焉用佞",
    "primary": "ning-eloquence",
    "concepts": [
      "ning-eloquence",
      "ren",
      "yan",
      "zhiren"
    ],
    "reason": "別人把冉雍的仁與不佞並列，孔子反問何用佞並指出口給御人易招憎，分開口才與仁德的評價。",
    "noteBoundary": "不知其仁在楊注被解為委婉否定其已達仁；保留此解與正文語氣，不將所有善於表達者一律歸入不仁。",
    "canonicalId": 97,
    "historicalIds": [
      97
    ],
    "sourceRowSha256": "a844a3cb64066d1b83b6834b0cdf66f7d327a93405ef18c8ddd9ec6fe1148631"
  },
  {
    "coordinate": "5.6",
    "quote": "吾斯之未能信",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office"
    ],
    "reason": "漆雕開對出仕尚未有信心，孔子聞之而悅，呈現是否任職可以保留自我判斷，而非一經被推薦就必須出仕。",
    "noteBoundary": "信在此按譯文是對自身能否勝任有信心，不用字詞命中冒充守約誠信的思想用例。",
    "canonicalId": 98,
    "historicalIds": [
      98
    ],
    "sourceRowSha256": "09739b04dbc87b72b2be5fa9ba34350390a59dea9522363dd5df3eb784d56073"
  },
  {
    "coordinate": "5.7",
    "quote": "由也好勇过我",
    "primary": "yong",
    "concepts": [
      "yong",
      "dao",
      "chushi"
    ],
    "reason": "由道不行、浮海之說引出子路的喜與孔子對好勇的評語，可討論行道困境中勇與判斷的關係。",
    "noteBoundary": "材依本底本通哉；注另列木材說並稱一定不合原意、裁制說因取字難解也不採用。保存異說及注者取捨，不把乘桴之說補成已實際完成的海行事件。",
    "canonicalId": 99,
    "historicalIds": [
      99
    ],
    "sourceRowSha256": "36b33b4ca469f3057db503dc034fc6f4bf82643721b527e5c40b20daa4783bf5"
  },
  {
    "coordinate": "5.8",
    "quote": "不知其仁也",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhiren",
      "zheng"
    ],
    "reason": "孔子逐一肯定子路、冉求、公西赤可任的工作，卻未由這些能力直接許之以仁，區別職能與德目。",
    "noteBoundary": "正文譯文保留不曉得，5.5楊注則把同式語看作委婉否定；不能把特定任職能力換算為完整仁德分數。",
    "canonicalId": 100,
    "historicalIds": [
      100
    ],
    "sourceRowSha256": "2e839512bd1e3ce4946296bd969538192e92730207e50958e1dd83a18b5d6131"
  },
  {
    "coordinate": "5.9",
    "quote": "闻一以知十",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "xue",
      "zhi"
    ],
    "reason": "子貢以聞一而能推知更多比較自己與顏回，孔子回應其判斷，呈現由已知推及新知的學習能力。",
    "noteBoundary": "吾與女的與依楊注為贊同，不解成孔子連自己一起與顏回相比；十、二不是可直接套用的學生智力或正式分數刻度。",
    "canonicalId": 101,
    "historicalIds": [
      101
    ],
    "sourceRowSha256": "94089d337faadb79b45ce67951782afb53993eedd0dc3cd8216f7cd4be9d9070"
  },
  {
    "coordinate": "5.10",
    "quote": "听其言而观其行",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "guan",
      "yan",
      "xing-act"
    ],
    "reason": "宰予晝寢引出孔子從信其行到觀其行的改變，把言語承諾和實際行動分開核對。",
    "noteBoundary": "兩次子曰依楊注是不同時候的話；保留敘述層次，不把朽木的嚴厲譬喻轉成對所有犯錯學生不可改變的身分標籤。",
    "canonicalId": 102,
    "historicalIds": [
      102
    ],
    "sourceRowSha256": "9a5d4c5e26701e0c71a420f80753036c229d7a6e751ad6a697481da699535b88"
  },
  {
    "coordinate": "5.11",
    "quote": "枨也欲，焉得刚",
    "primary": "gang",
    "concepts": [
      "gang",
      "yu"
    ],
    "reason": "以申棖多欲質問其能否剛毅不屈，呈現欲望可能牽制立場的問題。",
    "noteBoundary": "剛與勇不同，不能因舊目錄缺剛就改歸勇；焉得剛是本章對具體人物的判斷。",
    "canonicalId": 103,
    "historicalIds": [
      103
    ],
    "sourceRowSha256": "1ddce2796951d1e4cede82878b142ea2204225c456746962f56902008d085e4c"
  },
  {
    "coordinate": "5.13",
    "quote": "夫子之言性与天道",
    "primary": "wen",
    "concepts": [
      "wen",
      "xing-nature",
      "tiandao",
      "wen-hear"
    ],
    "reason": "子貢區分能聞的文章與不可得聞的性、天道之言，標出教學內容與弟子所聞的界限。",
    "noteBoundary": "文章依楊注指古代文獻學問，不等同今日一篇文章；不以孟荀或後世性論補成孔子本章已講的內容。",
    "canonicalId": 105,
    "historicalIds": [
      105
    ],
    "sourceRowSha256": "c4a4a6220bd36e1b02883dccaf98e331ec06d46ca65f2c3c108a816c35debad8"
  },
  {
    "coordinate": "5.14",
    "quote": "未之能行，唯恐有闻",
    "primary": "xing-act",
    "concepts": [
      "xing-act",
      "wen-hear",
      "xue"
    ],
    "reason": "子路在已有所聞尚未做到時擔心再聞，突顯把所學落實的迫切態度。",
    "noteBoundary": "有依楊注通又；不把子路的個別學習態度寫成未完成一次實踐就一律不得再學的制度。",
    "canonicalId": 106,
    "historicalIds": [
      106
    ],
    "sourceRowSha256": "a713dea547175d9ea17a9d5549397e21eee8769965080568e1f600f42827de18"
  },
  {
    "coordinate": "5.15",
    "quote": "敏而好学，不耻下问",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "buyichiwen",
      "min-diligence",
      "chi",
      "wen"
    ],
    "reason": "孔文子得文之諡，孔子以好學、敏及不恥下問解說，連結評人標準與求學態度。",
    "noteBoundary": "文在問答中是諡號，敏按本底本譯為聰敏靈活；不能把每個文或敏字都按同一詞義解讀。",
    "canonicalId": 107,
    "historicalIds": [
      107
    ],
    "sourceRowSha256": "c8d7b163203c6253fd8b60bdf24875560985e6f4d4d583973ad0c9adee361cd3"
  },
  {
    "coordinate": "5.16",
    "quote": "其使民也义",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "gong",
      "jing",
      "hui-kindness",
      "yi",
      "min"
    ],
    "reason": "以行己、事上、養民、使民四種情境分述子產之恭敬惠義，保留各德目所對的具體行為。",
    "noteBoundary": "君子之道四項是此章的評述結構，不冒充全書只有四項君子標準；惠與使民之義亦不可互換。",
    "canonicalId": 108,
    "historicalIds": [
      108
    ],
    "sourceRowSha256": "c96222bfd7609ef69edc0fccb455881fb9dbd8b28df40cb50048e4b63c1bef5f"
  },
  {
    "coordinate": "5.17",
    "quote": "久而敬之",
    "primary": "pengyou",
    "concepts": [
      "pengyou",
      "you",
      "jing"
    ],
    "reason": "以晏平仲善交友、久而敬之呈現相處與尊敬的關係，可與4.26朋友數則疏對讀。",
    "noteBoundary": "底本採人久而敬晏平仲；楊注另列晏平仲久而敬人的解釋，保留敬的主客體分歧。",
    "canonicalId": 109,
    "historicalIds": [
      109
    ],
    "sourceRowSha256": "9e3a5012af6d766645d9251556ffd3382d1e09464fec5587c280198bf00d13ae"
  },
  {
    "coordinate": "5.18",
    "quote": "何如其知也",
    "primary": "wisdom",
    "concepts": [
      "wisdom",
      "zhiren"
    ],
    "reason": "孔子從臧文仲為大龜營造華美居所反問其智，提供依具體行為評判智慧的材料。",
    "noteBoundary": "蔡依楊注指大龜，居為使其居住，知通智；不是說臧文仲住在蔡國，也不把注中的卜筮信念當科學事實。",
    "canonicalId": 110,
    "historicalIds": [
      110
    ],
    "sourceRowSha256": "5b5c33beaaf2c34901bf3b802ff7c50e0133dd111017d48aca7060a64e223e96"
  },
  {
    "coordinate": "5.19",
    "quote": "忠矣",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhong",
      "qing-integrity",
      "chushi"
    ],
    "reason": "子文盡交政令被許以忠，陳文子離開無道執政者被許以清，孔子仍未由單一德行直接許之以仁，兩段都须完整保留。",
    "noteBoundary": "未知後接焉得仁，楊注解作否定的曲折表達；三仕三已可能表多次，不能機械生成三次任免的確定年表。",
    "canonicalId": 111,
    "historicalIds": [
      111
    ],
    "sourceRowSha256": "eec369d1f260b05e0cae1381664039c1c689cfd6abf9220ef72ea408a92e56e5"
  },
  {
    "coordinate": "5.20",
    "quote": "再，斯可矣",
    "primary": "si",
    "concepts": [
      "si",
      "xing-act",
      "jie"
    ],
    "reason": "以季文子三思後行與孔子的再思可矣作對照，討論思考和行動之間是否過度計較的問題。",
    "noteBoundary": "楊注三表多次，並引過度計較利害的解釋；不把本章變成所有決策只准想兩遍的操作規則。",
    "canonicalId": 112,
    "historicalIds": [
      112
    ],
    "sourceRowSha256": "a19fa9bbfa57af37a4a5a720d31e959671f68f0175294e73801ce67603ba8890"
  },
  {
    "coordinate": "5.21",
    "quote": "其愚不可及也",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "wisdom",
      "bangyoudao",
      "bangwudao"
    ],
    "reason": "寧武子在有道、無道之邦表現不同，孔子特別稱其愚難及，構成依政治處境自處的比較材料。",
    "noteBoundary": "愚依底本取佯愚而非能力不足，故不能用字面愚給人物作低能力評分。",
    "canonicalId": 113,
    "historicalIds": [
      113
    ],
    "sourceRowSha256": "d6e0d30c9b777efb238b3a8245e9a7a47c102c27567c6bbea453bd36cdab3091"
  },
  {
    "coordinate": "5.22",
    "quote": "不知所以裁之",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "jiao",
      "zhi-aspiration",
      "wen"
    ],
    "reason": "孔子在陳思歸，想到本鄉學生的志向與文采並談如何裁成，呈現教育者對引導學生的牽掛。",
    "noteBoundary": "不知所以裁之依底本補孔子為主語，而非學生不知自裁；裁以剪裁比喻指導，不照字面理解。",
    "canonicalId": 114,
    "historicalIds": [
      114
    ],
    "sourceRowSha256": "05e4610359994cadb3a07600d8e238f179d2ce9e9e29307a3745c645d93b5e8e"
  },
  {
    "coordinate": "5.23",
    "quote": "不念旧恶",
    "primary": "yuan",
    "concepts": [
      "yuan"
    ],
    "reason": "以伯夷叔齊不記舊惡與少怨相聯，提供人際怨隙如何延續或減少的思考。",
    "noteBoundary": "惡在此依註指嫌隙仇恨；譯文的怨為別人對他們的怨，不任意換成兩人完全沒有不滿。",
    "canonicalId": 115,
    "historicalIds": [
      115
    ],
    "sourceRowSha256": "90073ee8d339fd188d0a2d4e2cb9b7b700f6e15bb488a5e3ae2fde3b8470b77f"
  },
  {
    "coordinate": "5.24",
    "quote": "孰谓微生高直",
    "primary": "zhi-upright",
    "concepts": [
      "zhi-upright",
      "zhiren"
    ],
    "reason": "用替人向鄰居討醋的事質問微生高之直，評人的名聲需由具體行為核對。",
    "noteBoundary": "底本在譯文補出不直說自己沒有的語意；不將一切向鄰居轉借物品都定為不直，也不把尾生故事的同人推測當確證。",
    "canonicalId": 116,
    "historicalIds": [
      116
    ],
    "sourceRowSha256": "661e1969d0d70ed7c475281e1b60dceddb6b5998f1ec937f3e9d506e2fa33842"
  },
  {
    "coordinate": "5.25",
    "quote": "匿怨而友其人",
    "primary": "qiaoyanlingse",
    "concepts": [
      "qiaoyanlingse",
      "chi",
      "yuan",
      "you",
      "gong"
    ],
    "reason": "孔子以巧言令色、足恭和匿怨而友人為恥，對照外在姿態與內在態度不相符的情形。",
    "noteBoundary": "此章所斥有矯飾之意，不能一併否定合宜的恭敬；不把注中對左丘明著作問題的考辨轉成未经另核的新歷史斷言。",
    "canonicalId": 117,
    "historicalIds": [
      117
    ],
    "sourceRowSha256": "1fce5f46bd0477b5f22c4236ad953f33e1b39908fe4668055a86a3bdc68f60ee"
  },
  {
    "coordinate": "5.26",
    "quote": "老者安之，朋友信之，少者怀之",
    "primary": "zhi-aspiration",
    "concepts": [
      "zhi-aspiration",
      "pengyou",
      "xin",
      "anren"
    ],
    "reason": "子路、顏回、孔子分別言志，從與友共用、無伐無施到老安友信少懷，保留三人的不同願望與相互問答。",
    "noteBoundary": "固定原文車馬衣裘處保留混入的輕字當刪校語及原注異文；來源已按serial24/SOURCE_ACCEPTANCE_v1.json接受，不暗改也不待再次校訂。sourceCompleteness舊review-required值只作原草稿歷史標籤，不能重啟來源門檻。信之、懷之的使動與一般用法兩說照存。",
    "sourceCompleteness": "original-editorial-contamination-review-required",
    "canonicalId": 118,
    "historicalIds": [
      118
    ],
    "sourceRowSha256": "0119ab22b267b3c6188380e2567f6290af60c0fc5995a5fb65bd7ae880edab36"
  },
  {
    "coordinate": "5.27",
    "quote": "能见其过而内自讼者",
    "primary": "guo",
    "concepts": [
      "guo",
      "sheng",
      "xiuyi"
    ],
    "reason": "見過與內自訟相連，重點在能否面對自己的過失並自我責備，可接4.17的內自省。",
    "noteBoundary": "吾未見是孔子慨嘆的表達，不以此宣布現實中沒有任何能自省的人。",
    "canonicalId": 119,
    "historicalIds": [
      119
    ],
    "sourceRowSha256": "517e1c1fe00ea93f919414adffb8b7bd8125b1f877570b2eccce0e229683f7ae"
  },
  {
    "coordinate": "5.28",
    "quote": "不如丘之好学也",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "zhongxin",
      "zhong",
      "xin",
      "xue"
    ],
    "reason": "孔子承認小邑亦有忠信如己者，而以好學作進一步區分，顯示德行基礎與持續學習都重要。",
    "noteBoundary": "十室之邑的說法不是人口普查；不將如丘、不如丘量化為可直接使用的學生分數。",
    "canonicalId": 120,
    "historicalIds": [
      120
    ],
    "sourceRowSha256": "d7436ba4991c41fcdd21c945e4166195331cdd0b1d778480f19c0acee47696e1"
  },
  {
    "coordinate": "6.1",
    "quote": "雍也可使南面",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "zhiren",
      "shi-office"
    ],
    "reason": "以南面論冉雍可任長官，作為察人任用的具體評語，可與5.5及6.6對讀。",
    "noteBoundary": "南面按楊注指長官之位，不限於天子；本章一句並未詳細列明具體職務或任命結果。",
    "canonicalId": 121,
    "historicalIds": [
      121
    ],
    "sourceRowSha256": "b5e7361bf03de5a6f4f05c26b9bba7ddbd9eb7cec388a9c55808969924460a0a"
  },
  {
    "coordinate": "6.2",
    "quote": "居敬而行简",
    "primary": "jian-simple",
    "concepts": [
      "jian-simple",
      "jing",
      "zheng",
      "min"
    ],
    "reason": "仲弓區分居敬行簡與居簡行簡，指出治理可以不繁瑣，但不能連存心也草率，孔子同意其分辨。",
    "noteBoundary": "子桑伯子的身份在楊注中未能確考；簡不與節儉之儉混同，也不因少做程序就稱居敬。",
    "canonicalId": 122,
    "historicalIds": [
      122
    ],
    "sourceRowSha256": "6fc0b4635fa88054291c081dad8cd03cdf613af4a0aed7c654643fa2541fe06d"
  },
  {
    "coordinate": "6.3",
    "quote": "不迁怒，不贰过",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "guo",
      "gai"
    ],
    "reason": "問弟子誰好學，孔子以顏回不遷怒、不重犯同樣過失回答，將好學具體聯到態度與改過。",
    "noteBoundary": "保留哀悼顏回的上下文；年齡年表在註中有考辨，本項不據此新增確定生卒年，也不以一次犯錯判定學習終結。",
    "canonicalId": 123,
    "historicalIds": [
      123
    ],
    "sourceRowSha256": "0f0e2de0198de0adeeb0fdfc649dbecb280e2f9bf696bdfc687569954741afff"
  },
  {
    "coordinate": "6.4",
    "quote": "君子周急不继富",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "hui-kindness",
      "fu",
      "pin"
    ],
    "reason": "由為出使者之母請粟的實例，引出援助應先周濟急困而非繼增富足，討論資源給予的目的。",
    "noteBoundary": "保留冉有請益、實給與孔子說明的差別；古代量名沿源註閱讀，不把未重核的換算直接作現代救濟配額。",
    "canonicalId": 124,
    "historicalIds": [
      124
    ],
    "sourceRowSha256": "6cb9bfb64a8c5c34ea44e9cd12aca87e0c99cdce79a7308166f6f3dc48f5f16b"
  },
  {
    "coordinate": "6.5",
    "quote": "以与尔邻里乡党乎",
    "primary": "hui-kindness",
    "concepts": [
      "hui-kindness",
      "pin"
    ],
    "reason": "原思辭粟，孔子建議有餘可給鄰里鄉黨，將個人受用與扶助周圍人的需要相連。",
    "noteBoundary": "九百之下未載量名，楊注明說不知其單位；本項不猜作九百石或任一現代數量。",
    "canonicalId": 125,
    "historicalIds": [
      125
    ],
    "sourceRowSha256": "53d8217fde3f516617b9802f998ba9d6b7603637391dd9382730b5937d6e068a"
  },
  {
    "coordinate": "6.6",
    "quote": "犂牛之子骍且角",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "xian",
      "ji"
    ],
    "reason": "以耕牛之子若合祭牲條件仍可用來譬仲弓，按註釋重點在不因父親出身而捨棄合格人才。",
    "noteBoundary": "祭牲是譬喻的來源，不能把人的價值化約為牲畜條件；山川其舍諸是反問，不作神意實證。",
    "canonicalId": 126,
    "historicalIds": [
      126
    ],
    "sourceRowSha256": "9b693d60999e899c1001c499923a0bc2f008c5c44f81cd03d5a41d58d7d37ea8"
  },
  {
    "coordinate": "6.7",
    "quote": "其心三月不违仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "xian"
    ],
    "reason": "以顏回長久不違仁與他人短暫達到作對照，說明維持仁德的持續性，而非一次表現即足。",
    "noteBoundary": "三月與日月按楊注活看為長久和短時；不得換成90天通關或按月計分的課程門檻。",
    "canonicalId": 127,
    "historicalIds": [
      127
    ],
    "sourceRowSha256": "c3ddd778a5a6b32f60db281425faf2aa9d26ba9cfd2d7841f1852e84854b92b2"
  },
  {
    "coordinate": "6.8",
    "quote": "由也果，于从政乎何有",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "zhiren"
    ],
    "reason": "對子路、子貢、冉求分別以果、達、藝說可從政，承認治理工作可由不同能力支持。",
    "noteBoundary": "三位弟子的長處需分別對應，不能合成每人都同時具有全部能力，也未據此許其完整仁德。",
    "canonicalId": 128,
    "historicalIds": [
      128
    ],
    "sourceRowSha256": "c561e73f213d2701c3d0fbda5fa8fcd22de4c1ab7c07a55df1076b654885c1b0"
  },
  {
    "coordinate": "6.9",
    "quote": "善为我辞焉",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "yin"
    ],
    "reason": "閔子騫辭季氏之聘，並說再來則必往汶上，呈現人物拒仕與去留的選擇。",
    "noteBoundary": "原文未明說全部辭官動機；汶上依楊注指汶水之北，不能只按今日地名直接定位。",
    "canonicalId": 129,
    "historicalIds": [
      129
    ],
    "sourceRowSha256": "0afaede748f33a94fcd153098c8eaa903b901fe619d175c745df07b216dcd545"
  },
  {
    "coordinate": "6.10",
    "quote": "斯人也而有斯疾也",
    "primary": "ming",
    "concepts": [
      "ming",
      "si-death",
      "sheng-life"
    ],
    "reason": "孔子探問伯牛之疾、握手而反覆慨嘆，把生命遭遇與命的感受放在師生相惜的情境中。",
    "noteBoundary": "原文未載可供現代確診的病情，不能補造疾病名稱；此處命的感嘆也不是對患者德行的判罰。",
    "canonicalId": 130,
    "historicalIds": [
      130
    ],
    "sourceRowSha256": "b1124a9141d4baaf6de1048b3511a3755b0e658d65ac98a8683359bc241c3d2d"
  },
  {
    "coordinate": "6.11",
    "quote": "回也不改其乐",
    "primary": "xian",
    "concepts": [
      "xian",
      "pin",
      "yue"
    ],
    "reason": "在簡陋飲食居處中仍不改其樂，孔子以此稱顏回賢，呈現生活條件與精神所守的關係。",
    "noteBoundary": "樂為喜樂，不是音樂；稱許顏回的態度不等於要求所有人故意選擇貧困或否定改善生活。",
    "canonicalId": 131,
    "historicalIds": [
      131
    ],
    "sourceRowSha256": "3e6031223fa404a6b7a98d929bfa8b7cfb02fe2b012019364086998bcfc240d7"
  },
  {
    "coordinate": "6.12",
    "quote": "力不足者，中道而废",
    "primary": "xue",
    "concepts": [
      "xue",
      "dao",
      "zhi-aspiration",
      "xing-act"
    ],
    "reason": "冉求以力不足解釋不能從道，孔子區別行至中途不支與尚未前行便自止，重點在是否開始用力。",
    "noteBoundary": "這是具體師生對話，不據此否定所有真實能力限制；畫依楊注作停止，不作圖画。",
    "canonicalId": 132,
    "historicalIds": [
      132
    ],
    "sourceRowSha256": "cf85cbc930038dec1f8183ae6675cf99bdce157d39b9b28ae40d82001ac600c7"
  },
  {
    "coordinate": "6.13",
    "quote": "女为君子儒！无为小人儒",
    "primary": "ru",
    "concepts": [
      "ru",
      "junzi",
      "xiaoren"
    ],
    "reason": "對子夏區分君子儒與小人儒，指出儒的身分並不自動保證成為君子，仍有品格方向的選擇。",
    "noteBoundary": "本章沒有另列兩類儒的完整制度定義，不能把後世儒學分類無標示地補入原話。",
    "canonicalId": 133,
    "historicalIds": [
      133
    ],
    "sourceRowSha256": "d202c62e8c48e799c0ff083e7a6612c671faee54919bcfcd5e6ed8b5a6c5d280"
  },
  {
    "coordinate": "6.14",
    "quote": "非公事，未尝至于偃之室也",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "zhi-upright",
      "zheng"
    ],
    "reason": "子游以澹台滅明行不由徑、非公事不到私室回答得人之問，將觀察日常行為與用人相連。",
    "noteBoundary": "行不由徑首先保留走路不抄小道的原文層面；不得補造私下請託的具體事件，人物是否此前已受業亦依註保留考辨。",
    "canonicalId": 134,
    "historicalIds": [
      134
    ],
    "sourceRowSha256": "628ee980993082f4e83ab53406be5921b4eb7ab813cafa4deff773f046c7aeb9"
  },
  {
    "coordinate": "6.15",
    "quote": "孟之反不伐",
    "primary": "fa",
    "concepts": [
      "fa",
      "yong"
    ],
    "reason": "孟之反在敗退中殿後卻不自夸，將勇敢行為與不居功的表述並看。",
    "noteBoundary": "伐在此為夸功而非征伐；非敢後也、馬不進也放在敘事中理解，不據字面取消他實際殿後的行為。",
    "canonicalId": 135,
    "historicalIds": [
      135
    ],
    "sourceRowSha256": "c6c62e2af39bafe3f7a5c00fc286a02533d765392259dac5dfd80f33606d77e3"
  },
  {
    "coordinate": "6.16",
    "quote": "祝鮀之佞",
    "primary": "ning-eloquence",
    "concepts": [
      "ning-eloquence",
      "chushi",
      "yan"
    ],
    "reason": "以祝鮀之口才、宋朝之美論當世避禍的難易，與5.5何用佞形成需要辨情境的對讀。",
    "noteBoundary": "不有按本注作假若沒有；主譯為無祝鮀口才而僅有宋朝美貌。注另載而作與的讀法並稱語句不順、恐非原意，保留異說及取捨；時世之嘆不是要求善辯或貌美的道德規範。",
    "canonicalId": 136,
    "historicalIds": [
      136
    ],
    "sourceRowSha256": "a98b9f06efd95755277912e2691e2ac221fb5b3eb3c2deae4f9d4041e80dfb54"
  },
  {
    "coordinate": "6.17",
    "quote": "何莫由斯道也",
    "primary": "dao",
    "concepts": [
      "dao",
      "xing-act"
    ],
    "reason": "以出門必由戶的日常譬喻，追問為何不循其道而行，連結對道的理解與實踐。",
    "noteBoundary": "戶的實物作用與道的譬喻層次分開，不能將本章所有道字解作實際道路。",
    "canonicalId": 137,
    "historicalIds": [
      137
    ],
    "sourceRowSha256": "499c8fc16c2ab736a9f41a12f79b73d9c8d32bd42722112605fcb5d2d5dc8d35"
  },
  {
    "coordinate": "6.19",
    "quote": "人之生也直",
    "primary": "zhi-upright",
    "concepts": [
      "zhi-upright",
      "sheng-life"
    ],
    "reason": "把正直與生存的根據相聯，並將不直而得免說為僥倖，提供對生活正當性的倫理判斷。",
    "noteBoundary": "罔按楊注指詭妄不直者，不因古代倫理表述而保證正直者不會遭受現實禍患。",
    "canonicalId": 139,
    "historicalIds": [
      139
    ],
    "sourceRowSha256": "9fb557e26b919d4b6906144ce0f351539e68baa72bf50c7a01a2ffa4c717e825"
  },
  {
    "coordinate": "6.20",
    "quote": "知之者不如好之者，好之者不如乐之者",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "zhi",
      "yue"
    ],
    "reason": "由知、好到樂分出對某種學問或事業的不同投入，作為好學與學習動機的思想關聯。",
    "noteBoundary": "好學是主題延伸，原文之未單指某門學科；樂在此是以之為樂，不能與音樂用法混同或用來預設固定能力等級。",
    "canonicalId": 140,
    "historicalIds": [
      140
    ],
    "sourceRowSha256": "5607aca8ca6a848881177d2e35dbb3cc289e6497aa379ea7ccfd4e4abf9c7b76"
  },
  {
    "coordinate": "6.21",
    "quote": "可以语上也",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "jiao"
    ],
    "reason": "依聽者理解水平決定是否告以高深內容，與循序施教、因人調整的問題相連。",
    "noteBoundary": "原文沒有永久不變的學生分組、測量工具或分數線；不將中人以上以下直接變成不可變的能力身分。",
    "canonicalId": 141,
    "historicalIds": [
      141
    ],
    "sourceRowSha256": "05cfe6b9a57ee78fbb958e38acde5fddd1e3e81f7e6b5d296d29c74b9c7fe491"
  },
  {
    "coordinate": "6.24",
    "quote": "鲁一变，至于道",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "dao",
      "jiao"
    ],
    "reason": "以齊、魯及合道之間的變化次序談政治教育的改進，保留改變而非僅評高下的方向。",
    "noteBoundary": "政治和教育是底本譯文補出的語境；不把古代國家比較套作今日地域居民的優劣判定。",
    "canonicalId": 144,
    "historicalIds": [
      144
    ],
    "sourceRowSha256": "f23b86efb5feae0634793b4d3cea34398cf1b949b8c3d6529da2b94039bf104e"
  },
  {
    "coordinate": "6.25",
    "quote": "觚不觚，觚哉！觚哉",
    "primary": "qi-vessel",
    "concepts": [
      "qi-vessel",
      "ming-name",
      "zhengming"
    ],
    "reason": "觚與所稱為觚的實物不相應，適合與名實、正名問題比較；不把比較主題說成原文明出正名二字。",
    "noteBoundary": "楊注分列器形改變及容量增大的兩種猜想，不能擇一當成已考古證實的現場。",
    "canonicalId": 145,
    "historicalIds": [
      145
    ],
    "sourceRowSha256": "25b8aea93d1d69956d1c8b38ef5069e7a1d095b8ad9cac8c7e764376d02f05f6"
  },
  {
    "coordinate": "6.26",
    "quote": "可欺也，不可罔也",
    "primary": "ren",
    "concepts": [
      "ren",
      "junzi",
      "wisdom"
    ],
    "reason": "宰我以井中有仁者設問，孔子拒絕可任意陷害君子的推論，仁與辨析情理相連。",
    "noteBoundary": "井有仁按本底本指仁人；逝、折的異讀及欺以其方、罔以非其道的區別留在註釋中，不簡化成仁者應盲從任何救人說法。",
    "canonicalId": 146,
    "historicalIds": [
      146
    ],
    "sourceRowSha256": "841c510b4c540b7e0a10c127d0356ef9cb09f5586258a97eb1c76bd577bb1463"
  },
  {
    "coordinate": "6.27",
    "quote": "博学于文，约之以礼",
    "primary": "boxue",
    "concepts": [
      "boxue",
      "xue",
      "wen",
      "li",
      "junzi"
    ],
    "reason": "以廣學文獻和禮的約束共同說明不畔，學習廣度與約束整合並存。",
    "noteBoundary": "約之以禮的之指君子還是文，楊注列有兩說；不得抹去代詞指向的分歧，畔依註通叛。",
    "canonicalId": 147,
    "historicalIds": [
      147
    ],
    "sourceRowSha256": "e09154a9863d711bc0ec66fe124424259dd368472dd3a1cab37a74d819be8ac1"
  },
  {
    "coordinate": "6.28",
    "quote": "予所否者，天厌之",
    "primary": "tian",
    "concepts": [
      "tian",
      "chushi",
      "zheng"
    ],
    "reason": "孔子見南子引起子路不悅，孔子用對天的誓言申明自身，呈現政治交往與弟子評議的張力。",
    "noteBoundary": "所在誓詞中表假設；原章未敘見面全過程，不由一次會見推造私人關係或確定未載的動機。",
    "canonicalId": 148,
    "historicalIds": [
      148
    ],
    "sourceRowSha256": "921eee9ec68aac37462e0cdcbfb6a96d75a26397e332b4df12510ce52411273e"
  },
  {
    "coordinate": "7.1",
    "quote": "述而不作，信而好古",
    "primary": "xue",
    "concepts": [
      "xue",
      "wen",
      "xin"
    ],
    "reason": "孔子以述、信古自述文化傳承的態度，聯到學習與文獻；信在本章是相信古代文化。",
    "noteBoundary": "不作不可讀成孔子學說絕無創造；楊注以7.28不知而作相參，老彭身分保留諸說而不選作定論。",
    "canonicalId": 151,
    "historicalIds": [
      151
    ],
    "sourceRowSha256": "8546886c66c3eeccf6652d1b1cb16c7e800a027cdcd00cf5d2ef563def11e268"
  },
  {
    "coordinate": "7.2",
    "quote": "学而不厌，诲人不倦",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "xue",
      "hui"
    ],
    "reason": "默識、學不厭與誨不倦並列，將自身持續學習和教人連在一起。",
    "noteBoundary": "識讀志，意為記住；何有於我哉有自謙與不難兩解，依底本正文及註保留差異。",
    "canonicalId": 152,
    "historicalIds": [
      152
    ],
    "sourceRowSha256": "4747dfc1883a5f8c4b5fab72be4cd3e725eebfcef4fb8691c7b3b25a13e8880e"
  },
  {
    "coordinate": "7.3",
    "quote": "闻义不能徙，不善不能改",
    "primary": "gai",
    "concepts": [
      "gai",
      "yi",
      "de",
      "xue",
      "xiuyi"
    ],
    "reason": "孔子以修德、講學、赴義、改不善為自身所憂，提出從所知到所行的要求。",
    "noteBoundary": "此處列的是自省的憂慮，不是逐一宣告本人曾犯哪些過錯；不能把聞義直接視為已實踐。",
    "canonicalId": 153,
    "historicalIds": [
      153
    ],
    "sourceRowSha256": "43070e95595cb6a9521ab10af4ee986b7440ec0b4223a40f0895af3cecb76955"
  },
  {
    "coordinate": "7.4",
    "quote": "子之燕居",
    "primary": "xing-act",
    "concepts": [
      "xing-act",
      "yue"
    ],
    "reason": "閒居時的整齊與和舒構成人物日常行止，可與7.38的外在態度對读。",
    "noteBoundary": "申申、夭夭按楊注分別為整敕與和舒；喜樂主題為思想關聯，原章未談音樂。",
    "canonicalId": 154,
    "historicalIds": [
      154
    ],
    "sourceRowSha256": "3723f9569be5ecd99bb8fdbffff47c7380f5d6047d25c5f64bb33ec2ea300bea"
  },
  {
    "coordinate": "7.5",
    "quote": "吾不复梦见周公",
    "primary": "zhi-aspiration",
    "concepts": [
      "zhi-aspiration",
      "sheng-sage",
      "sheng-life"
    ],
    "reason": "由久不夢周公慨嘆衰老，將所景仰的古代人物與自身生命感受相連。",
    "noteBoundary": "周公為孔子敬服的聖人依據在楊注；夢境不作神示，也不能據此推定未載的年齡或具體日期。",
    "canonicalId": 155,
    "historicalIds": [
      155
    ],
    "sourceRowSha256": "141a03a112e58e4d44d0f4b43af4c307342d23475616e2fe4658adbf32c9a0a7"
  },
  {
    "coordinate": "7.6",
    "quote": "志于道，据于德，依于仁，游于艺",
    "primary": "dao",
    "concepts": [
      "dao",
      "de",
      "ren",
      "zhi-aspiration",
      "yi-arts",
      "xue"
    ],
    "reason": "志、據、依、游分別關聯道德仁藝，保留多方面學習實踐的層次。",
    "noteBoundary": "藝依底本指禮樂射御書數，不能縮成今日美術科；游於藝與安學樂師的說明來自楊注引文。",
    "canonicalId": 156,
    "historicalIds": [
      156
    ],
    "sourceRowSha256": "98664527ea697fdb4c1ba1ed957993c19205d4a8ccd913c5c75875a910384790"
  },
  {
    "coordinate": "7.7",
    "quote": "吾未尝无诲焉",
    "primary": "hui",
    "concepts": [
      "hui",
      "jiao",
      "jiaoxue",
      "li"
    ],
    "reason": "以主動行束修者皆教誨說明接納求學者的態度，兼有初見禮節的語境。",
    "noteBoundary": "束修依本底本為十條乾肉的薄禮；不改成現代固定學費，也不把與15.39的比較當作原文明言同一條件。",
    "canonicalId": 157,
    "historicalIds": [
      157
    ],
    "sourceRowSha256": "937e0b4caf40e24ed0cdf6321e1b14fa88ec10ab6e66a5db5c26c9c511b8fbe3"
  },
  {
    "coordinate": "7.9",
    "quote": "子食于有丧者之侧",
    "primary": "sang",
    "concepts": [
      "sang",
      "li",
      "ai"
    ],
    "reason": "在居喪者身旁不飽食，呈現對他人哀情的體察與日常禮節。",
    "noteBoundary": "本章只記特定情境下的行為；不能擴成普遍禁食或認為食量可以直接測量仁德。",
    "canonicalId": 159,
    "historicalIds": [
      159
    ],
    "sourceRowSha256": "8d0ed49b13180ccab045eeb82873475c51cac37214e1097c1ee19a42e2613289"
  },
  {
    "coordinate": "7.10",
    "quote": "子于是日哭，则不歌",
    "primary": "sang",
    "concepts": [
      "sang",
      "yue",
      "li"
    ],
    "reason": "同日哭後不歌，適合與居喪時的行止相參，討論哀樂表達的相稱。",
    "noteBoundary": "原文沒有交代哭的全部原因；喪為相鄰章思想關聯，不能補造當日死者身分。",
    "canonicalId": 160,
    "historicalIds": [
      160
    ],
    "sourceRowSha256": "f35a3baaab9b43d6dc8083c940e5e2435074ca660563494d702c5d02eb4a16e3"
  },
  {
    "coordinate": "7.11",
    "quote": "必也临事而惧，好谋而成者也",
    "primary": "yong",
    "concepts": [
      "yong",
      "chushi",
      "yin",
      "shen"
    ],
    "reason": "前段用舍行藏，後段以臨事慎重、好謀有成區別勇於徒手搏虎涉河，保留出處與共事擇人的兩層。",
    "noteBoundary": "子路的軍事設問與孔子答語分開；河依楊注未必專指黃河，不能把恐懼謹慎解作膽怯。",
    "canonicalId": 161,
    "historicalIds": [
      161
    ],
    "sourceRowSha256": "9f69ecef085198e6cc5d40d28b5271c9fee02723d3b10607ce9b71c5f965e9f3"
  },
  {
    "coordinate": "7.12",
    "quote": "如不可求，从吾所好",
    "primary": "fu",
    "concepts": [
      "fu",
      "chushi",
      "zhi-aspiration"
    ],
    "reason": "求富可否與從所好相連，呈現生計追求和個人志向的選擇。",
    "noteBoundary": "而是假設連詞；執鞭之士依譯為市場守門卒，其他職務解釋保留在註，不補成不擇手段求財。",
    "canonicalId": 162,
    "historicalIds": [
      162
    ],
    "sourceRowSha256": "46b57d977e68084ed34b1c4dca072471b7099965652c15aa8800064c78f1cde5"
  },
  {
    "coordinate": "7.13",
    "quote": "子之所慎",
    "primary": "shen",
    "concepts": [
      "shen",
      "ji",
      "sheng-life"
    ],
    "reason": "齋戒、戰爭、疾病同列慎重之事，涉及祭祀、國家安危與生命。",
    "noteBoundary": "齊在此通齋，不能把全書齊字都轉為齋；疾病的具體病名與治療措施並未載於本章。",
    "canonicalId": 163,
    "historicalIds": [
      163
    ],
    "sourceRowSha256": "0e4a25e8f6865a3d3f8816a6e4debb3f26c4da7942087ec55fa22d2981897452"
  },
  {
    "coordinate": "7.14",
    "quote": "不图为乐之至于斯也",
    "primary": "yue",
    "concepts": [
      "yue"
    ],
    "reason": "聞韶而長時忘肉味，呈現音樂感受及欣賞的深度，可與3.25評韶對讀。",
    "noteBoundary": "此處樂為音樂，三月按譯作很長時間；不能用感受描述替代精確日數或生理測量。",
    "canonicalId": 164,
    "historicalIds": [
      164
    ],
    "sourceRowSha256": "406bfbdf31c1efdc0c99dcb7a2eb61a1478ed9f44d0c2623b92fd66144e0e128"
  },
  {
    "coordinate": "7.15",
    "quote": "求仁而得仁，又何怨",
    "primary": "ren",
    "concepts": [
      "ren",
      "yuan",
      "rang",
      "chushi",
      "jun"
    ],
    "reason": "孔子稱伯夷叔齊求仁得仁，子貢由此推知對衛君的態度，呈現仁、取捨與無怨的政治語境。",
    "noteBoundary": "不為衛君是子貢出後的判斷，不能偽作孔子直接回答；衛君指衛出公輒，親屬關係依註保存。",
    "canonicalId": 165,
    "historicalIds": [
      165
    ],
    "sourceRowSha256": "40cda9c9b2b677fe10b3806c6189ca27b4701d6b71010dae0d373cc604560969"
  },
  {
    "coordinate": "7.16",
    "quote": "不义而富且贵，于我如浮云",
    "primary": "yili",
    "concepts": [
      "yili",
      "yi",
      "buyi",
      "fugui",
      "pin",
      "yue"
    ],
    "reason": "把簡樸生活之樂與不義富貴的取捨並置，重點在求富的方法是否正當。",
    "noteBoundary": "浮雲所指是不義而來的富貴，不是所有財富；樂為喜樂，疏食粗糧與糙米兩解保留。",
    "canonicalId": 166,
    "historicalIds": [
      166
    ],
    "sourceRowSha256": "edd7607a9a7b917bbb3b0cd1129c73dd309fb8576c6fa351cd26f5169d49a94d"
  },
  {
    "coordinate": "7.17",
    "quote": "可以无大过矣",
    "primary": "xue",
    "concepts": [
      "xue",
      "guo",
      "gai"
    ],
    "reason": "以學易與避免大過相連，呈現學習對行為反省的期望。",
    "noteBoundary": "五十等用語依當前底本保留，不由一句推造孔子確定寫作年表，也不聲稱學易保證完全不犯錯。",
    "canonicalId": 167,
    "historicalIds": [
      167
    ],
    "sourceRowSha256": "3e83bd47f91d9bc9c44739f0c7d34b63657fe99be7509f660620106fa045911e"
  },
  {
    "coordinate": "7.18",
    "quote": "皆雅言也",
    "primary": "wen",
    "concepts": [
      "wen",
      "shi-poetry",
      "li",
      "yan"
    ],
    "reason": "讀詩書與執禮用雅言，將文化學習、禮儀及公共語言聯在一起。",
    "noteBoundary": "雅言指當時通行語；譯文用普通話作說明，不能等同今日現代標準漢語的語音規範。",
    "canonicalId": 168,
    "historicalIds": [
      168
    ],
    "sourceRowSha256": "8539ce10e56d002af1cdd38e605533c0e7b5e4a670417f81989c71fd4654f0fb"
  },
  {
    "coordinate": "7.19",
    "quote": "发愤忘食，乐以忘忧",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "yue",
      "sheng-life"
    ],
    "reason": "孔子自述投入、樂與忘老，呈現學習生活中的專注和精神感受。",
    "noteBoundary": "葉公問人、子路不答、孔子教其答的說話層次保留；不把忘食作學生必須犧牲飲食健康的要求。",
    "canonicalId": 169,
    "historicalIds": [
      169
    ],
    "sourceRowSha256": "bf2941d51c2d13932332ed3738c350752c797daadadab2dcfb29e054dfde5002"
  },
  {
    "coordinate": "7.20",
    "quote": "好古，敏以求之者也",
    "primary": "xuezhizhi",
    "concepts": [
      "xuezhizhi",
      "xue",
      "zhi",
      "min-diligence"
    ],
    "reason": "明言不是生知，而由好古與勤敏求得知識，與16.9學知分類可對讀。",
    "noteBoundary": "敏為求學勤敏，不直接等於測驗速度或天賦分數；本章為孔子自述而非對所有人的能力分類。",
    "canonicalId": 170,
    "historicalIds": [
      170
    ],
    "sourceRowSha256": "1672b17b69c93d5fffdf612e71351b6839d828928c9f3bf3192fee38879796c5"
  },
  {
    "coordinate": "7.21",
    "quote": "子不语怪，力，乱，神",
    "primary": "guishen",
    "concepts": [
      "guishen",
      "yan",
      "jiao"
    ],
    "reason": "記孔子不談的四類內容，為鬼神與教學言說界限提供原文參照。",
    "noteBoundary": "不語不等於已論證其不存在；力按譯為勇力，不能擴成不談一切身體活動。",
    "canonicalId": 171,
    "historicalIds": [
      171
    ],
    "sourceRowSha256": "5ad604eb4dfd606a939bfed17cdf79943277ec6a5adf9c87d540a5b741e2de49"
  },
  {
    "coordinate": "7.22",
    "quote": "择其善者而从之，其不善者而改之",
    "primary": "xue",
    "concepts": [
      "xue",
      "guan",
      "gai",
      "sheng"
    ],
    "reason": "從同行者之善取法、由不善反省改己，將知人觀察轉成學習資源。",
    "noteBoundary": "不善者而改之並非命令改造別人；三人依譯可作幾人，不設為恰好三人的教學法則。",
    "canonicalId": 172,
    "historicalIds": [
      172
    ],
    "sourceRowSha256": "ee1574ce4bbf9302f66aef6b97a55fcb724f59491463b1011d860ae089860b5b"
  },
  {
    "coordinate": "7.23",
    "quote": "天生德于予",
    "primary": "tian",
    "concepts": [
      "tian",
      "de",
      "ming"
    ],
    "reason": "以天生德回應桓魋威脅，呈現行道者對自身使命及所守的信念。",
    "noteBoundary": "受威脅及離宋背景來自楊注引史記，原文僅天生德與桓魋如予何之言；不把欲殺寫成已殺。這種信念不構成現實免受傷害的保證。",
    "canonicalId": 173,
    "historicalIds": [
      173
    ],
    "sourceRowSha256": "0b53eecad131537286871813d23ff63d2d3f33e398a18c0fc1e258d34a8520ba"
  },
  {
    "coordinate": "7.24",
    "quote": "吾无隐乎尔",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "jiao",
      "xing-act",
      "cheng"
    ],
    "reason": "孔子向弟子表明行為無所隱瞞，將教學公開與身教相連。",
    "noteBoundary": "隱在此為隱瞞，不是退隱政治；原句沒有授權公開他人隱私或取消所有合理資訊界限。",
    "canonicalId": 174,
    "historicalIds": [
      174
    ],
    "sourceRowSha256": "bd40d5a8fe6971a352ab5ef3939fd84adfdbc5c82ea275ae7a64ca181af4fc91"
  },
  {
    "coordinate": "7.25",
    "quote": "子以四教：文，行，忠，信",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "jiao",
      "wen",
      "xing-act",
      "zhong",
      "xin"
    ],
    "reason": "明列文、行、忠、信四項教育內容，把文獻、實踐與待人態度合觀。",
    "noteBoundary": "行作名詞，按底本譯為社會生活實踐；這四教不直接等同另一章德行政事言語文學的四科人物分類。",
    "canonicalId": 175,
    "historicalIds": [
      175
    ],
    "sourceRowSha256": "46f68c63d3a88a8375af681ab518cdc721e387526cb72fbb6814e6a9c776c54f"
  },
  {
    "coordinate": "7.26",
    "quote": "难乎有恒矣",
    "primary": "heng",
    "concepts": [
      "heng",
      "junzi",
      "sheng-sage",
      "shanren",
      "cheng"
    ],
    "reason": "由難見聖人善人轉向有恆操守，並以虛飾有無、盈虛、貧富為反面。",
    "noteBoundary": "上下兩段子曰分別保留；泰此處用度豪華，不與其他章的安泰同義，聖人君子善人不壓成同級稱號。",
    "canonicalId": 176,
    "historicalIds": [
      176
    ],
    "sourceRowSha256": "871805ee397ecbfca7296c5317b2c7beb9c618da8f4eb61cb2389622efea05d1"
  },
  {
    "coordinate": "7.27",
    "quote": "弋不射宿",
    "primary": "jie",
    "concepts": [
      "jie",
      "xing-act",
      "ai"
    ],
    "reason": "取魚射鳥有所不為，作為行為自限與對生物態度的討論材料。",
    "noteBoundary": "節與愛是思想關聯而非本章原詞；不把此章直接寫成現代生態法規，綱與弋的具體方式依註。",
    "canonicalId": 177,
    "historicalIds": [
      177
    ],
    "sourceRowSha256": "a6dbda8a00f7b765ff5f02a6f4dc924acbe78ab0e9849f3e85ddac4d0ac6d929"
  },
  {
    "coordinate": "7.28",
    "quote": "多闻，择其善者而从之",
    "primary": "xuezhizhi",
    "concepts": [
      "xuezhizhi",
      "wen-hear",
      "zhi",
      "guan"
    ],
    "reason": "多聞擇善、多見而識與不知而作對照，說明知識取得需要見聞和選擇。",
    "noteBoundary": "知之次按楊注與16.9學知相參；識為記住，不能把多見多聞自動宣稱已理解或掌握。",
    "canonicalId": 178,
    "historicalIds": [
      178
    ],
    "sourceRowSha256": "bceee517bc2166419000164a4dd7632063e49ca70ab7d872993c28ac20f34e60"
  },
  {
    "coordinate": "7.29",
    "quote": "人洁己以进，与其洁也",
    "primary": "jiao",
    "concepts": [
      "jiao",
      "gai",
      "jiaoxue"
    ],
    "reason": "接見互鄉童子時看其當下潔己求進，反對以過去一概拒絕，呈現施教與改變可能。",
    "noteBoundary": "互鄉位置不可考，不捏造地圖座標；贊成進步不等於保證其過去行為全無問題或今後必不退步。",
    "canonicalId": 179,
    "historicalIds": [
      179
    ],
    "sourceRowSha256": "31abfebabc8abdaa7060f0f75cd7f4f27dc05916d64a10a9a8ee8b35654eb227"
  },
  {
    "coordinate": "7.30",
    "quote": "我欲仁，斯仁至矣",
    "primary": "ren",
    "concepts": [
      "ren",
      "yu",
      "zhi-aspiration"
    ],
    "reason": "以欲仁回應仁是否遙遠，強調向仁的主動意願及可實踐性。",
    "noteBoundary": "本章的激勵語境不能變成只要點選願望就已完成仁德評價；與持續不違仁的章句應分層對讀。",
    "canonicalId": 180,
    "historicalIds": [
      180
    ],
    "sourceRowSha256": "e1d51bce83578bd1ed436c3d147f7a13409caa840ac6552c9642fd1513916f35"
  },
  {
    "coordinate": "7.31",
    "quote": "苟有过，人必知之",
    "primary": "guo",
    "concepts": [
      "guo",
      "li",
      "junzi",
      "gai"
    ],
    "reason": "從魯昭公知禮的爭議轉到孔子願承受指出過失，關聯禮制、偏袒疑問與受教態度。",
    "noteBoundary": "昭公稱謂時點與陳司敗身分未能確定；楊注另釋為君親諱而歸過於己，不能只留下單一動機。",
    "canonicalId": 181,
    "historicalIds": [
      181
    ],
    "sourceRowSha256": "aef13d1255eaf8e3a0f99c55d76dbcea88fd9744acbfca1d254edb7f787bdaed"
  },
  {
    "coordinate": "7.32",
    "quote": "必使反之，而后和之",
    "primary": "yue",
    "concepts": [
      "yue",
      "xue",
      "he"
    ],
    "reason": "聽人歌善而請重唱再相和，呈現音樂交流中聆聽、欣賞與學習的次序。",
    "noteBoundary": "反指再唱，和指應和歌唱，不與政治倫理上的和而不同無分別混用。",
    "canonicalId": 182,
    "historicalIds": [
      182
    ],
    "sourceRowSha256": "35fb004cafac66b20fe9893543d2e1ded441528b6353798d7b6934b3333e8edb"
  },
  {
    "coordinate": "7.33",
    "quote": "躬行君子，则吾未之有得",
    "primary": "xing-act",
    "concepts": [
      "xing-act",
      "junzi",
      "wen",
      "xue"
    ],
    "reason": "將書本學問與躬行君子區分，突出知識獲得之外仍須實踐。",
    "noteBoundary": "文、莫的斷句依底本所採吳說並保留註中證據限制；自謙語不當作孔子從無任何實踐的斷言。",
    "canonicalId": 183,
    "historicalIds": [
      183
    ],
    "sourceRowSha256": "f5e728c2fb56c53c56a75407aec26ff2f300b53ecf5e2a6ff4e74bf2b30e0854"
  },
  {
    "coordinate": "7.34",
    "quote": "为之不厌，诲人不倦",
    "primary": "hui",
    "concepts": [
      "hui",
      "haoxue",
      "jiaoxue",
      "ren",
      "sheng-sage"
    ],
    "reason": "孔子不敢自居聖仁，轉說為之不厭、誨人不倦，公西華再指出實行之難。",
    "noteBoundary": "聖與仁既有自述也有後人評議，楊注引孟子為外部參照，不能併成孔子在本章自封聖人。",
    "canonicalId": 184,
    "historicalIds": [
      184
    ],
    "sourceRowSha256": "6129ca80165dfcf712a7bc6f0f22a28f7a1d1f0f916245d8f88592abea14912c"
  },
  {
    "coordinate": "7.35",
    "quote": "丘之祷久矣",
    "primary": "guishen",
    "concepts": [
      "guishen",
      "tian",
      "sheng-life"
    ],
    "reason": "重病時子路請禱與孔子答已禱久，呈現師生面對疾病及祈禱的態度。",
    "noteBoundary": "疾病連言為重病；誄依注應作讄為祈禱文，不作祭悼死者的誄，原章未載具體病名。",
    "canonicalId": 185,
    "historicalIds": [
      185
    ],
    "sourceRowSha256": "535cf1f29e01095be511182a2711d1c4e083feecafc6598609de203a6b148e41"
  },
  {
    "coordinate": "7.36",
    "quote": "与其不孙也，宁固",
    "primary": "jian-frugal",
    "concepts": [
      "jian-frugal",
      "she-extravagance",
      "li",
      "gong"
    ],
    "reason": "比較奢的不遜與儉的固陋，明言寧取固，為用度及禮態的權衡提供依據。",
    "noteBoundary": "孫在此通遜，固依底本為固陋寒傖；寧固不是把所有固執不改都當作美德。",
    "canonicalId": 186,
    "historicalIds": [
      186
    ],
    "sourceRowSha256": "eaee86ae2e5ccb13267af2df7e22255be52cbe5a27e48adaab674a25cfc06f66"
  },
  {
    "coordinate": "7.37",
    "quote": "君子坦荡荡，小人长戚戚",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xiaoren"
    ],
    "reason": "以坦蕩與局促憂愁對照君子小人的內在狀態，可與義利取捨和修身章句比較。",
    "noteBoundary": "此為人格理想的對照，不把現實中的焦慮症狀當作小人證據，也不以面部情緒直接評定德行。",
    "canonicalId": 187,
    "historicalIds": [
      187
    ],
    "sourceRowSha256": "384fa86e2bfaaf128cc7fa700ed1f0dc8f1690c2ad0467f579160be974188f66"
  },
  {
    "coordinate": "7.38",
    "quote": "子温而厉，威而不猛，恭而安",
    "primary": "gong",
    "concepts": [
      "gong",
      "jing",
      "zhongyong"
    ],
    "reason": "以溫而厲、威不猛、恭而安並說孔子態度，呈現多種品格兼顧而不過激的形象。",
    "noteBoundary": "中庸是可比較的思想關聯，不是原文明言；厲與猛不可一概當作兇暴，也不消除原有莊重。",
    "canonicalId": 188,
    "historicalIds": [
      188
    ],
    "sourceRowSha256": "c7b238de06b101cc92524026d16a9a8c17b4501465f308738a70a72906b17a4a"
  },
  {
    "coordinate": "8.1",
    "quote": "三以天下让",
    "primary": "rang",
    "concepts": [
      "rang",
      "de",
      "min"
    ],
    "reason": "以泰伯讓位稱至德，將權位取捨與公眾稱譽相連，保存人物敘事的核心。",
    "noteBoundary": "三依底本譯為屢次；天下指當時部落還是預言後世天下，楊注有兩說，不能據此斷言泰伯已統一天下。",
    "canonicalId": 189,
    "historicalIds": [
      189
    ],
    "sourceRowSha256": "9c02b293e6b4548d4f89101a8935c9df0c6aeb5e9de9a84c7ab8a5e76bf0d5c2"
  },
  {
    "coordinate": "8.2",
    "quote": "恭而无礼则劳",
    "primary": "li",
    "concepts": [
      "li",
      "gong",
      "shen",
      "yong",
      "zhi-upright",
      "ren",
      "min",
      "junzi"
    ],
    "reason": "以禮調節恭慎勇直四種態度，後半再論在上者厚親念故對民風的作用。",
    "noteBoundary": "君子此處依底本指在上位者；偷為感情淡薄，不是偷竊，不能把四種德目離開禮的條件單獨作褒貶。",
    "canonicalId": 190,
    "historicalIds": [
      190
    ],
    "sourceRowSha256": "ad8a815d62d30ffca5d5e2626e265f6c4841d64e7d0a9142d40c3949e16c5ffe"
  },
  {
    "coordinate": "8.3",
    "quote": "战战兢兢，如临深渊，如履薄冰",
    "primary": "shen",
    "concepts": [
      "shen",
      "sheng-life",
      "shi-poetry"
    ],
    "reason": "曾子病中引詩並請弟子看手足，將一生謹慎與免於祸刑的感受相連。",
    "noteBoundary": "啟字注所引說文字形在固定來源為空引號，按serial24/SOURCE_ACCEPTANCE_v1.json已接受原狀，不自行補字，也不再列待校訂阻塞。說話人為曾子；詩中臨淵履冰是所引比喻。",
    "canonicalId": 191,
    "historicalIds": [
      191
    ],
    "sourceRowSha256": "78eeacd33bc8aed7d149d4ecd928c8679c56262f7013df7b12cfe1d826c25a4d"
  },
  {
    "coordinate": "8.4",
    "quote": "君子所贵乎道者三",
    "primary": "gong",
    "concepts": [
      "gong",
      "xin",
      "yan",
      "li",
      "dao"
    ],
    "reason": "曾子向孟敬子論容貌、顏色與辭氣，將待人態度的根本與有司處理的禮儀細節區分。",
    "noteBoundary": "遠暴慢依底本含避免別人粗暴懈怠之意；倍通背，不能僅按今日倍數字義理解。",
    "canonicalId": 192,
    "historicalIds": [
      192
    ],
    "sourceRowSha256": "ca1f0e30e7354b301e9614aadec5f39837db3652f4a15853620cae66e25a2d98"
  },
  {
    "coordinate": "8.5",
    "quote": "以能问于不能，以多问于寡",
    "primary": "wen-ask",
    "concepts": [
      "wen-ask",
      "xue",
      "buyichiwen",
      "forbearance"
    ],
    "reason": "曾子稱友人有能仍問、有實若虛、犯而不校，將問學謙遜和不計較並看。",
    "noteBoundary": "吾友原文未明名，顏回為歷代注家判定；不把思想關聯的不恥下問誤作本章直接引文。",
    "canonicalId": 193,
    "historicalIds": [
      193
    ],
    "sourceRowSha256": "5f4ec8a70fd266df444e5decdb34341e88a0af1b378935b37acb1a31f037110d"
  },
  {
    "coordinate": "8.6",
    "quote": "临大节而不可夺也",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xin",
      "zhong",
      "gang",
      "zheng"
    ],
    "reason": "能受託孤兒、國家且臨大節不動搖，將可信任與守志構成君子判斷。",
    "noteBoundary": "六尺依註指年幼者，古代尺度與約齡不可用作現代身高分級；大節指安危存亡關頭。",
    "canonicalId": 194,
    "historicalIds": [
      194
    ],
    "sourceRowSha256": "63a56a11a3600fe051814e72f87a4872bc404c8dc2c5e75be66fee78eeba4a22"
  },
  {
    "coordinate": "8.7",
    "quote": "仁以为己任",
    "primary": "hongyi",
    "concepts": [
      "hongyi",
      "shi",
      "ren",
      "zhi-aspiration",
      "si-death"
    ],
    "reason": "曾子以任重道遠說士須弘毅，將仁的使命延至終身的持續實踐。",
    "noteBoundary": "弘毅依楊注為強毅；終身承擔是志向要求，不以死亡或犧牲本身當作學習成績。",
    "canonicalId": 195,
    "historicalIds": [
      195
    ],
    "sourceRowSha256": "afa198c9ba53a48e6d684a1d5df932aec7b6d0dad9732a0cd1e5f4355f33da50"
  },
  {
    "coordinate": "8.8",
    "quote": "兴于《诗》，立于礼，成于乐",
    "primary": "shijiao",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "li",
      "yue",
      "jiao"
    ],
    "reason": "詩的興發、禮的立身、樂的完成形成互相聯繫的教育敘述。",
    "noteBoundary": "樂此處為音樂而與禮相連；最後階段是楊注對教學的說明，不能直接套成唯一學校年級進度。",
    "canonicalId": 196,
    "historicalIds": [
      196
    ],
    "sourceRowSha256": "0663fac94efbc6c6d73c413c6698bb1f1813c377b733687e624946f05bc37104"
  },
  {
    "coordinate": "8.9",
    "quote": "民可使由之，不可使知之",
    "primary": "min",
    "concepts": [
      "min",
      "zheng",
      "zhi",
      "jiao"
    ],
    "reason": "本底本將民、遵行与知其所以然區分，是理解政治與知識關係及其限制的爭議章。",
    "noteBoundary": "完整保留底本譯文及楊注反對改斷句的理由；不得以改善觀感而改成另一句，也不把古代主張默認為當代教學規則。",
    "canonicalId": 197,
    "historicalIds": [
      197
    ],
    "sourceRowSha256": "3aa9c519f4f6362e65df0e58f2bf8d6633219f15a630f80fafdfe201a33548f2"
  },
  {
    "coordinate": "8.10",
    "quote": "人而不仁，疾之已甚，乱也",
    "primary": "yong",
    "concepts": [
      "yong",
      "pin",
      "ren",
      "yuan"
    ],
    "reason": "好勇而厭貧、痛恨不仁者太甚兩種態度組合，按主譯皆為祸害；用以討論態度及反應的後果，不擴成已明示政治叛亂的章句。",
    "noteBoundary": "疾為厭惡痛恨，非疾病；本章批評的是具體態度組合，不將所有貧困者列為致亂者。",
    "canonicalId": 198,
    "historicalIds": [
      198
    ],
    "sourceRowSha256": "1765e626123ffc59c989310a2bbe25dfb8347ceb9a5134e972e1600ed70788b7"
  },
  {
    "coordinate": "8.11",
    "quote": "使骄且吝，其余不足观也已",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "de",
      "xian"
    ],
    "reason": "以才能可比周公為假設，仍以驕吝否定評價，區分才華與德行。",
    "noteBoundary": "如有是設想，不是指周公本人驕吝；不把名望或才藝的高低替代具體品格判斷。",
    "canonicalId": 199,
    "historicalIds": [
      199
    ],
    "sourceRowSha256": "969972800da0de84996d3d6463e809a88e8399b0cfa51e28cd3850f28d8909f0"
  },
  {
    "coordinate": "8.12",
    "quote": "三年学，不至于谷",
    "primary": "xue",
    "concepts": [
      "xue",
      "shi-office",
      "li-benefit"
    ],
    "reason": "讀書而不以俸祿做官為意念所向被說成難得，連到學習動機與仕進。",
    "noteBoundary": "至、谷依底本分別為意念所至、俸祿，不作到了穀場；也不能推成三年一過便應取得官職。",
    "canonicalId": 200,
    "historicalIds": [
      200
    ],
    "sourceRowSha256": "93bfe7eb8163359d0e7d4e7212f4935f659f1a527255c0efa02c5d5fecacd5f3"
  },
  {
    "coordinate": "8.13",
    "quote": "天下有道则见，无道则隐",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "haoxue",
      "dao",
      "xin",
      "yin",
      "chi",
      "bangyoudao",
      "bangwudao",
      "fugui"
    ],
    "reason": "由篤信好學、守道到入居出隱及貧富之恥，將處境判斷與所守原則相連。",
    "noteBoundary": "見通現，篤信指信道；有道貧賤與無道富貴的對照屬政治語境，不用財富現狀直接判斷今日個人道德。",
    "canonicalId": 201,
    "historicalIds": [
      201
    ],
    "sourceRowSha256": "0eb5689788a2005165ebd83553e501e171d079dfbc7b4ec22cb4cf520a3e4ce3"
  },
  {
    "coordinate": "8.14",
    "quote": "不在其位，不谋其政",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "shi-office",
      "zheng-politics"
    ],
    "reason": "以位與政相應說職責範圍，可與名分、分工及仕進章句比較。",
    "noteBoundary": "謀其政依底本指考慮該職位的政務；原句未詳列公眾議論或監督制度，不擴成禁止所有公共討論。",
    "canonicalId": 202,
    "historicalIds": [
      202
    ],
    "sourceRowSha256": "918ec191fcf7429292d6fd8b8b0b8bcf6a83d3b57340692cf7449a4748a6d1b4"
  },
  {
    "coordinate": "8.15",
    "quote": "《关雎》之乱",
    "primary": "yue",
    "concepts": [
      "yue",
      "shi-poetry"
    ],
    "reason": "由師摯之始到關雎之亂寫完整樂章的聽覺感受，保存詩與音樂合觀。",
    "noteBoundary": "亂在本章為樂曲結尾合奏，非叛亂或雜亂；始、亂、一成的關係依楊注。",
    "canonicalId": 203,
    "historicalIds": [
      203
    ],
    "sourceRowSha256": "47c725c77000b3da7f60241bd08ba4a340f32afa5d27e30d41a2314a8fca9569"
  },
  {
    "coordinate": "8.16",
    "quote": "悾悾而不信",
    "primary": "xin",
    "concepts": [
      "xin",
      "zhi-upright",
      "zhiren"
    ],
    "reason": "孔子對狂不直、侗不愿、悾悾不信的組合表示不解，關聯外在類型與可取品格。",
    "noteBoundary": "愿在本句為老實，不等同後文鄉愿的整體概念；狂、侗、悾悾各依本章譯法，勿一概歸為同一人格標籤。",
    "canonicalId": 204,
    "historicalIds": [
      204
    ],
    "sourceRowSha256": "889d3a470914acdae3107c8cd3065c36b525e70f6fb18ae4a39854418b2016b1"
  },
  {
    "coordinate": "8.17",
    "quote": "学如不及，犹恐失之",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "heng"
    ],
    "reason": "以追趕與怕失去譬喻求學不懈，重點在獲得後仍持續保持。",
    "noteBoundary": "如標示比喻，不能由追赶語氣推定實際競賽或排名制度；有恆為思想關聯，並非本章字面詞。",
    "canonicalId": 205,
    "historicalIds": [
      205
    ],
    "sourceRowSha256": "03632c30c0410f20b229c7731daa2d6581cc0ae29db6001b9d9665ff44cec0a4"
  },
  {
    "coordinate": "8.18",
    "quote": "有天下也而不与焉",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "jun",
      "min",
      "de"
    ],
    "reason": "稱舜禹有天下而不以天下自享，連到在上者服務百姓與自利的區別。",
    "noteBoundary": "與依底本及楊注含私有享受，非不參與治理；禪讓敘事屬注中傳說，不能說已作史料年代核驗。",
    "canonicalId": 206,
    "historicalIds": [
      206
    ],
    "sourceRowSha256": "39ed9a92f909d2ca6862fd9502d14b9fe6b4d2817d9ad59ac42ec2ee768aef42"
  },
  {
    "coordinate": "8.19",
    "quote": "唯天为大，唯尧则之",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "tian",
      "jun",
      "de",
      "wen",
      "min"
    ],
    "reason": "稱尧則天、功成與文章，呈現理想君主的德政、恩惠及禮制。",
    "noteBoundary": "文章按本底本為禮儀制度，不只指寫作作品；頌美性語言不轉作可驗證的精確統治規模。",
    "canonicalId": 207,
    "historicalIds": [
      207
    ],
    "sourceRowSha256": "a6044e9dd48a0b1c4feec37ca21b5a49124923ed78092308ecd80e66ac07d750"
  },
  {
    "coordinate": "8.20",
    "quote": "才难，不其然乎",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "chen",
      "zheng",
      "de",
      "jun"
    ],
    "reason": "由舜與武王之臣論人才難得，後接文王事殷與周德，保留用人和政治德行兩層。",
    "noteBoundary": "亂臣依注取治國之臣，另有親臣說但注者因與才難不吻合而保留否定，非叛臣。婦人不計入九人的原說保留供批判閱讀，不作現代任用原則。九州島等固定注字已由serial24來源接受原狀，不再列待核阻塞。",
    "canonicalId": 208,
    "historicalIds": [
      208
    ],
    "sourceRowSha256": "ab4d6c0c6c0002ef3fda17ad56166b531e492d2968d0d56e0bd37b3c9f9ce806"
  },
  {
    "coordinate": "8.21",
    "quote": "卑宫室而尽力乎沟洫",
    "primary": "jian-frugal",
    "concepts": [
      "jian-frugal",
      "zheng",
      "min",
      "ji",
      "guishen",
      "li"
    ],
    "reason": "稱禹節省自身飲食衣居，將資源用於祭禮與水利，呈現公私用度的取捨。",
    "noteBoundary": "致孝乎鬼神在本章指祭祀，不把鬼神等同在世父母；溝洫依注為農田水利。",
    "canonicalId": 209,
    "historicalIds": [
      209
    ],
    "sourceRowSha256": "e0591316864132d5292c96bf0d5e97fb194a0fecaf0f5ab3956aa6b476b3f49c"
  },
  {
    "coordinate": "9.1",
    "quote": "子罕言利与命与仁",
    "primary": "yan",
    "concepts": [
      "yan",
      "li-benefit",
      "ming",
      "ren"
    ],
    "reason": "罕言利命仁涉及記載頻率與實際談話頻率的區別，為三主題的收錄與解讀提供反思。",
    "noteBoundary": "採本注少主動談的解釋，保存與為許、罕軒等異說及注者不取理由；仁讀夷的缺字引號按固定來源原樣接受，不重啟校訂。注內利六次、命八九次等舊數不是当前索引實算，記載頻率亦不能直接等同平生談話頻率。",
    "canonicalId": 210,
    "historicalIds": [
      210
    ],
    "sourceRowSha256": "9a5910798107bac717224c48af6abfa1d4c657f1d5d91b5b776087c8d08590f7"
  },
  {
    "coordinate": "9.2",
    "quote": "博学而无所成名",
    "primary": "boxue",
    "concepts": [
      "boxue",
      "xue",
      "yi-arts",
      "ming-name"
    ],
    "reason": "達巷人評孔子博學而無專名，孔子以御射回應，將廣學與專門技藝、名聲相對照。",
    "noteBoundary": "評語出自達巷人，不能變成孔子自稱沒有成就；達巷的具體所在地與孔子選御的全部動機未載。",
    "canonicalId": 211,
    "historicalIds": [
      211
    ],
    "sourceRowSha256": "fc9669a5620b6f8992c391cf0dc9a00e1a1f7592e1d4953da1fd908f84edd48c"
  },
  {
    "coordinate": "9.3",
    "quote": "虽违众，吾从下",
    "primary": "li",
    "concepts": [
      "li",
      "jian-frugal",
      "gong",
      "xing-act"
    ],
    "reason": "麻冕可從省而拜禮不從倨傲，顯示對禮的變通需分辨改變所牽涉的意義。",
    "noteBoundary": "絲料省在工費而非簡單等於原料便宜；泰在此為倨傲，不能與7.26奢泰完全同義處理。",
    "canonicalId": 212,
    "historicalIds": [
      212
    ],
    "sourceRowSha256": "8d850e05abda93c189575686469c35469f29b105fb4265c5ce9b00ecd59caafe"
  },
  {
    "coordinate": "9.4",
    "quote": "毋意，毋必，毋固，毋我",
    "primary": "sibi",
    "concepts": [
      "sibi",
      "sheng"
    ],
    "reason": "絕四以不懸揣、不絕對、不拘固、不唯我構成自我限制，適合用於反省判斷方式。",
    "noteBoundary": "原章是記述孔子而非孔子自述四條；四項不可只剩毋意一項，也不能解作沒有意見或取消一切確定知識。",
    "canonicalId": 213,
    "historicalIds": [
      213
    ],
    "sourceRowSha256": "b262fc01fd64563b70440d03ba05b5c84696c125c67fd0fb20fbb9d20564ad3b"
  },
  {
    "coordinate": "9.5",
    "quote": "天之未丧斯文也",
    "primary": "wen",
    "concepts": [
      "wen",
      "tian",
      "ming",
      "chushi"
    ],
    "reason": "被拘匡地時以斯文存續回應危難，聯結文化承擔與天的信念。",
    "noteBoundary": "畏按楊注為拘囚，不是僅心理恐懼；匡城的今址依注仍為可能，不作精確行旅定位。",
    "canonicalId": 214,
    "historicalIds": [
      214
    ],
    "sourceRowSha256": "d0fa89e2960b17abd02533ee07a21a930d8fabfa43efa99682ee73962ca49d4b"
  },
  {
    "coordinate": "9.6",
    "quote": "吾少也贱，故多能鄙事",
    "primary": "yi-arts",
    "concepts": [
      "yi-arts",
      "jian",
      "junzi",
      "sheng-sage",
      "zhiren"
    ],
    "reason": "子貢以聖與多能作答，孔子則以少賤解釋多能鄙事，區分生活技能與君子聖人的判準。",
    "noteBoundary": "太宰身分國別不可考；多能與聖不建立必然等式，亦不把孔子否認多技能為必需解作貶低所有技藝。",
    "canonicalId": 215,
    "historicalIds": [
      215
    ],
    "sourceRowSha256": "ef52fa034984b13046d397499d26efed32ab4e3437f93c1b201de38012ba3edc"
  },
  {
    "coordinate": "9.7",
    "quote": "吾不试，故艺",
    "primary": "yi-arts",
    "concepts": [
      "yi-arts",
      "shi-office",
      "chushi"
    ],
    "reason": "牢轉述孔子以未受用解釋學得技藝，與前章少賤多能形成互參。",
    "noteBoundary": "試按註是任用，不是考試；牢的身分有文獻爭議，不直接與琴張等人合併人物ID。",
    "canonicalId": 216,
    "historicalIds": [
      216
    ],
    "sourceRowSha256": "0ac8809cb2de9115e77a26c83e7024be0b80ce28396b768ad4a2f9aa0a635248"
  },
  {
    "coordinate": "9.8",
    "quote": "我叩其两端而竭焉",
    "primary": "wen-ask",
    "concepts": [
      "wen-ask",
      "zhi",
      "jiaoxue"
    ],
    "reason": "面對提問由兩端追問而盡告，將承認未知與探問方法結合。",
    "noteBoundary": "依底本空空如也主取孔子原不知的語意；原文未交代問題內容，不能捏造具體問答作歷史記錄。",
    "canonicalId": 217,
    "historicalIds": [
      217
    ],
    "sourceRowSha256": "dd69848570520cef538cabfa01e1ef3721c93ad6c2037bf16dcf73092ed744f2"
  },
  {
    "coordinate": "9.9",
    "quote": "凤鸟不至，河不出图",
    "primary": "tianming",
    "concepts": [
      "tianming",
      "tian",
      "chushi"
    ],
    "reason": "以鳳鳥河圖不現慨嘆清明難望，提供使命感與政治處境的表達。",
    "noteBoundary": "鳳鳥河圖是古代祥瑞傳說，楊注明說藉此比喻；不把傳說當實際觀測或預言工具。",
    "canonicalId": 218,
    "historicalIds": [
      218
    ],
    "sourceRowSha256": "9a7dec9f86ede9c781c0b976222b2ead87f768e647e2336ccee4a6827ebe1589"
  },
  {
    "coordinate": "9.10",
    "quote": "虽少，必作",
    "primary": "jing",
    "concepts": [
      "jing",
      "li",
      "sang",
      "gong"
    ],
    "reason": "對居喪者、禮服者與盲者，即使年少亦起身快行示敬，呈現不同交往情境的礼意。",
    "noteBoundary": "齊衰是喪服專名，作為起身、趨為疾行；原文未寫給盲者的語言說明，勿與10.25等章拼作同一次事件。",
    "canonicalId": 219,
    "historicalIds": [
      219
    ],
    "sourceRowSha256": "1844ebf5d4ad7fe5f7d4b43f90c586d6c695b4e8d7a542c91ba3e88e7bfd870c"
  },
  {
    "coordinate": "9.11",
    "quote": "夫子循循然善诱人",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "wen",
      "li",
      "xue",
      "boxue"
    ],
    "reason": "顏回敘老師善於循序引導、博文約禮及自身欲進未得，將教學方法與學習感受連讀。",
    "noteBoundary": "全段為顏回評述；卓爾的具體理解依本底本，不把欲罷不能轉為已完成掌握的紀錄。",
    "canonicalId": 220,
    "historicalIds": [
      220
    ],
    "sourceRowSha256": "39a85c75a5643cfc20a70409d712f5d6684e7adad7a18ad8a06d3af688a0518a"
  },
  {
    "coordinate": "9.12",
    "quote": "无臣而为有臣",
    "primary": "li",
    "concepts": [
      "li",
      "cheng",
      "sang",
      "tian"
    ],
    "reason": "病勢漸好時，孔子反對子路設臣之僭禮，寧由弟子照顧，將禮制身分與不欺相連；不將病間寫成已痊癒。",
    "noteBoundary": "臣與現代治喪處只是譯文類比，工作時點與職分並不相同；病間為病漸好，不是病死。",
    "canonicalId": 221,
    "historicalIds": [
      221
    ],
    "sourceRowSha256": "dcc060edb0b7a7e6c5405a9aeba46a32e74b2f2fbc05bbe90aa986aaa3001f7c"
  },
  {
    "coordinate": "9.13",
    "quote": "我待贾者也",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "zhiren",
      "yin"
    ],
    "reason": "美玉藏或沽的設問，回應為等待識貨者，提供才能待用與主動求用的比較材料。",
    "noteBoundary": "賈讀商人或通價兩說均在楊注；不可把待賈只改成追逐高薪，藏沽首先是玉的譬喻。",
    "canonicalId": 222,
    "historicalIds": [
      222
    ],
    "sourceRowSha256": "59d9ac71ab2fa25296605508684c91771e9717315c78e26e83ad698525f5f5e4"
  },
  {
    "coordinate": "9.14",
    "quote": "君子居之，何陋之有",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "chushi",
      "jiao"
    ],
    "reason": "以君子居處回應九夷簡陋之問，呈現人格與環境相互關聯的主張。",
    "noteBoundary": "九夷位置依底本的考證，未作現代地理驗證；原章的夷與陋不可轉成對現今民族的等級判定。",
    "canonicalId": 223,
    "historicalIds": [
      223
    ],
    "sourceRowSha256": "57c236130318941378173d14847d9340fa40c8c2e7f77aeb57ffc6c28f86e1fb"
  },
  {
    "coordinate": "9.15",
    "quote": "《雅》、《颂》各得其所",
    "primary": "yue",
    "concepts": [
      "yue",
      "shi-poetry",
      "wen"
    ],
    "reason": "返魯後正樂、雅頌得所，關聯詩樂整理與文化傳承。",
    "noteBoundary": "楊注對整理篇章或樂曲有證據限制，且不取刪詩三千為三百的說法；不將正樂擴稱已證實的刪詩。",
    "canonicalId": 224,
    "historicalIds": [
      224
    ],
    "sourceRowSha256": "8fc9850be0c0ffafee4b862069d89de89a47bc3bc9c0e08f8d1c622a750b5ba7"
  },
  {
    "coordinate": "9.16",
    "quote": "丧事不敢不勉",
    "primary": "li",
    "concepts": [
      "li",
      "sang",
      "zheng",
      "fumu",
      "xiongdi",
      "jie"
    ],
    "reason": "事公卿父兄、盡喪禮、不為酒困並列，將公私角色及飲酒自制一併呈現。",
    "noteBoundary": "父兄可能偏指兄或泛長者；何有於我哉自謙與不難兩解保留，不能把父親仍在世當作本章已證實。",
    "canonicalId": 225,
    "historicalIds": [
      225
    ],
    "sourceRowSha256": "8bdadb86a844a27dbb0322ed8877c40268310e6f5644655a9c10b6c7c2b99308"
  },
  {
    "coordinate": "9.17",
    "quote": "逝者如斯夫",
    "primary": "sheng-life",
    "concepts": [
      "sheng-life",
      "heng"
    ],
    "reason": "川流不舍晝夜引發時光消逝的感嘆，提供生命時間與持續性的閱讀入口。",
    "noteBoundary": "恆為比較主題而非章中直接德目；楊注提醒後世闡發未必孔子本意，不將宇宙論補作原話。",
    "canonicalId": 226,
    "historicalIds": [
      226
    ],
    "sourceRowSha256": "3fa5440c6da68a71cf38d2dc73ce96739f22e7c202027bada46d83e387daca6a"
  },
  {
    "coordinate": "9.18",
    "quote": "吾未见好德如好色者也",
    "primary": "de",
    "concepts": [
      "de",
      "yu"
    ],
    "reason": "比較好德與好色的動機強弱，將道德追求與欲望傾向相對照。",
    "noteBoundary": "吾未見是孔子的觀察語氣，不是覆蓋所有人的調查；色在此按譯為美貌，不能泛化為所有感官經驗。",
    "canonicalId": 227,
    "historicalIds": [
      227
    ],
    "sourceRowSha256": "5f12d1c3603e1fc0eacc4e026e99ba1ac57cb2d62a91b01e27393978f69b9b5f"
  },
  {
    "coordinate": "9.19",
    "quote": "止，吾止也",
    "primary": "heng",
    "concepts": [
      "heng",
      "xing-act",
      "zhi-aspiration",
      "yi"
    ],
    "reason": "用堆土成山和平地覆土說進止由己，主讀持續努力，同時保留依義進退的可能。",
    "noteBoundary": "底本楊注列出堅持與唯義進止兩解，不能只據勵志讀法抹去後者，也未指明具體工程。",
    "canonicalId": 228,
    "historicalIds": [
      228
    ],
    "sourceRowSha256": "967d63ac5f5321d55312eae491c2f973137548f56762a87ce37a8ea2b732222f"
  },
  {
    "coordinate": "9.20",
    "quote": "语之而不惰者",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "min-diligence"
    ],
    "reason": "以聽講始終不惰稱顏回，將學習態度落在持續投入。",
    "noteBoundary": "不惰不等於從無提問或永遠服從；其回也與保留感嘆語氣，不從一句推造其他弟子全部表現。",
    "canonicalId": 229,
    "historicalIds": [
      229
    ],
    "sourceRowSha256": "abda899d35f882b361aa89fdbc53eac9a66a3f7ccb878d61a24b7935bcc7ce1d"
  },
  {
    "coordinate": "9.21",
    "quote": "吾见其进也，未见其止也",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "heng",
      "si-death"
    ],
    "reason": "惋惜顏回並稱其不斷進步，呈現師生關係及對學習持續性的評語。",
    "noteBoundary": "顏回已死由譯文補明情境，原句未提供日期；進步不轉寫為可量化的舊成績。",
    "canonicalId": 230,
    "historicalIds": [
      230
    ],
    "sourceRowSha256": "732b683d63f3adc098cb87f03127617a46f5626be9dbb5cb888fd597d1dca244"
  },
  {
    "coordinate": "9.22",
    "quote": "苗而不秀者有矣夫",
    "primary": "sheng-life",
    "concepts": [
      "sheng-life"
    ],
    "reason": "莊稼生長、吐穗與結實的不同階段提供生命生長比較入口；原文所指未定，不直接收作學習或恆德論斷。",
    "noteBoundary": "原文未明言教育或指名人物，楊注明確反對妄測其所指；不能把顏回斷定為唯一對象。",
    "canonicalId": 231,
    "historicalIds": [
      231
    ],
    "sourceRowSha256": "fadb12b2ee88b09d21203f27a938ac944e5cc71c750cd4d046c6ca11dfc2c6e4"
  },
  {
    "coordinate": "9.23",
    "quote": "后生可畏",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "ming-name"
    ],
    "reason": "提醒不可預先否定後來者的發展，同時談年長而無聞，涉及人才判斷與成長時間。",
    "noteBoundary": "可畏為值得敬重戒慎；四十五十的古代評語不直接變成今日人的價值截止年齡或必須成名的標準。",
    "canonicalId": 232,
    "historicalIds": [
      232
    ],
    "sourceRowSha256": "9e28c7c710db21dfba73c60a3c32395975c3cae748e8a80fda9544e221a626cc"
  },
  {
    "coordinate": "9.24",
    "quote": "改之为贵",
    "primary": "gai",
    "concepts": [
      "gai",
      "si",
      "yan",
      "xing-act"
    ],
    "reason": "接受規勸須實改，聽到順耳話須分析，將表面態度與實際反應區分。",
    "noteBoundary": "說讀悅，繹依譯為分析；從而不改指出行動缺位，不能只記點頭或表示喜歡便判已受教。",
    "canonicalId": 233,
    "historicalIds": [
      233
    ],
    "sourceRowSha256": "1cd9ca5db1eab7b8284b1d0142dd67b17438393f74a79db573ee97329ba14c59"
  },
  {
    "coordinate": "9.25",
    "quote": "主忠信，毋友不如己者，过则勿惮改",
    "primary": "zhongxin",
    "concepts": [
      "zhongxin",
      "zhong",
      "xin",
      "you",
      "guo",
      "gai"
    ],
    "reason": "忠信、交友與改過重現於本章，可回溯1.8共享思想關係而保留本章座標。",
    "noteBoundary": "現有譯文欄重複原句，固定底本已接受原樣保留，舊待核門檻已被取代；重見章句不刪ID或重複計分，不以能力或財富高低簡化不如己。",
    "canonicalId": 234,
    "historicalIds": [
      234
    ],
    "sourceRowSha256": "fa852c43cef476c9300b58d35f936a20cb021291f7819808ee480287f58467d1"
  },
  {
    "coordinate": "9.26",
    "quote": "匹夫不可夺志也",
    "primary": "zhi-aspiration",
    "concepts": [
      "zhi-aspiration",
      "gang"
    ],
    "reason": "以軍隊可失主帥對照個人志向不可被奪，強調內在所守。",
    "noteBoundary": "三軍是古代軍隊通稱，非當代三種軍種；匹夫依時代語義閱讀，不把志當作任何主張都不容反思。",
    "canonicalId": 235,
    "historicalIds": [
      235
    ],
    "sourceRowSha256": "c19cc21117d94b775d4c3af682258648f9f96e429d395635aac352b8c963f1b3"
  },
  {
    "coordinate": "9.27",
    "quote": "是道也，何足以臧",
    "primary": "pin",
    "concepts": [
      "pin",
      "chi",
      "yu",
      "shi-poetry"
    ],
    "reason": "稱子路不以衣敝為恥，隨後指出僅不嫉不求尚未足，保留稱許與進一步要求。",
    "noteBoundary": "子路反覆誦詩與孔子後語不可截斷；終身按底本作常念，缊袍材質兩解保留。",
    "canonicalId": 236,
    "historicalIds": [
      236
    ],
    "sourceRowSha256": "3324b2b314c1a3e6657d8a511a468b4096dfdc685ca12ef1eb6fe1cd423d269e"
  },
  {
    "coordinate": "9.28",
    "quote": "岁寒，然后知松柏之后雕也",
    "primary": "heng",
    "concepts": [
      "heng",
      "gang",
      "zhiren"
    ],
    "reason": "以寒時方見松柏後凋的現象，作為遇困境才顯所守的比較材料。",
    "noteBoundary": "恆與剛是思想關聯而非原文明出的德目；雕通凋，不聲稱松柏從不落葉。",
    "canonicalId": 237,
    "historicalIds": [
      237
    ],
    "sourceRowSha256": "2c9421ab3d0da2619c640372c072cbe41bcfe9da31a32ded565583867e027c4b"
  },
  {
    "coordinate": "9.29",
    "quote": "知者不惑，仁者不忧，勇者不惧",
    "primary": "wisdom",
    "concepts": [
      "wisdom",
      "zhi",
      "ren",
      "yong"
    ],
    "reason": "智、仁、勇分別對應不惑、不憂、不懼，保留三者不同面向的理想描述。",
    "noteBoundary": "知在此通智，不能將全書知字統一改為智慧；理想德目不等於用憂懼症狀診斷學生品格。",
    "canonicalId": 238,
    "historicalIds": [
      238
    ],
    "sourceRowSha256": "9c178e1bf5aa7f0bab3a0040faf43ef0717f7a915854896c21e1bec4a6c23b53"
  },
  {
    "coordinate": "9.30",
    "quote": "可与立，未可与权",
    "primary": "quan",
    "concepts": [
      "quan",
      "xue",
      "dao",
      "li"
    ],
    "reason": "共學、適道、立與權逐層區分，顯示共同學習不自動等於能通權達變。",
    "noteBoundary": "立依楊注常含立於禮；譯文事事依體而行與註依禮不一致按已接受固定底本保留，舊待核門檻已取代；權非政治權力。",
    "canonicalId": 239,
    "historicalIds": [
      239
    ],
    "sourceRowSha256": "318f759ccfae3ab9726a687f14c4239e95d6c0debc48a95846689609024dc8e3"
  },
  {
    "coordinate": "9.31",
    "quote": "未之思也，夫何远之有",
    "primary": "si",
    "concepts": [
      "si",
      "shi-poetry",
      "zhi-aspiration",
      "ren"
    ],
    "reason": "由逸詩的思念與遠近引出孔子反問，可與9.11及7.30比較追求之道是否可及。",
    "noteBoundary": "唐棣植物有兩說；道或仁的解讀在楊注屬可能推測，不能當作詩句明說仁，逸詩亦不冒作今詩經篇目。",
    "canonicalId": 240,
    "historicalIds": [
      240
    ],
    "sourceRowSha256": "59dc1a95e1dfaeb3eb611fb7dcb43d0dcf760600e8ca4ff7c85b2b7b53740fc0"
  },
  {
    "coordinate": "10.1",
    "quote": "其在宗庙朝廷，便便言，唯谨尔",
    "primary": "yan",
    "concepts": [
      "yan",
      "gong",
      "jin",
      "li"
    ],
    "reason": "鄉黨與宗廟朝廷的言語態度不同，呈現場合、表達與慎言的關係。",
    "noteBoundary": "恂恂為恭順，便便依注讀駢；底本將謹譯為說得少，保留此譯法，不把似不能言解作失去表達能力。",
    "canonicalId": 241,
    "historicalIds": [
      241
    ],
    "sourceRowSha256": "f366adca120993d7a2adf1b2c4ce13deb124907df952b85db7c6845d3c7e87d0"
  },
  {
    "coordinate": "10.2",
    "quote": "君在，踧踖如也，与与如也",
    "primary": "jing",
    "concepts": [
      "jing",
      "gong",
      "li",
      "jun",
      "yan"
    ],
    "reason": "與上下大夫交談及君在場時呈不同態度，關聯禮制角色和交往分寸。",
    "noteBoundary": "各疊詞依本章譯文分辨，侃侃不直接套現代成語侃侃而談；本章記古代朝廷禮態。",
    "canonicalId": 242,
    "historicalIds": [
      242
    ],
    "sourceRowSha256": "d872beecece11a9ab660484f6db612766db6c370a9a937f4052c97895a86dceb"
  },
  {
    "coordinate": "10.3",
    "quote": "宾退，必复命",
    "primary": "li",
    "concepts": [
      "li",
      "jing",
      "jun",
      "xing-act"
    ],
    "reason": "受命接賓、作揖趨進與賓退復命構成接待流程，呈現禮在具體行動中的次序。",
    "noteBoundary": "襜正文標號⑶而註文重複⑵，按已接受固定底本保留，舊待核门槛已取代；呈現時保完整注釋，不可僅按錯號配成前後之注。",
    "canonicalId": 243,
    "historicalIds": [
      243
    ],
    "sourceRowSha256": "e89169a8ccc25e7defe7737a92d6488eec17b409b55dadb12f6161827210b149"
  },
  {
    "coordinate": "10.4",
    "quote": "立不中门，行不履阈",
    "primary": "li",
    "concepts": [
      "li",
      "gong",
      "jing",
      "shen"
    ],
    "reason": "入門、過位、升降階與復位的態度連成朝廷禮儀，呈現對場合與位置的重視。",
    "noteBoundary": "鞠躬如依楊注為恭謹態，不能逕當今日彎身動作；所過君位當時為空，齊為衣下擺。",
    "canonicalId": 244,
    "historicalIds": [
      244
    ],
    "sourceRowSha256": "869c61477149b660e847d7448cd526e418d50bdce6dbad14a57d76f18b07f56f"
  },
  {
    "coordinate": "10.5",
    "quote": "私觌，愉愉如也",
    "primary": "li",
    "concepts": [
      "li",
      "jing",
      "gong",
      "yue"
    ],
    "reason": "執圭聘問、享獻、私覿各有相應神態，分別顯示正式儀節與私人相見的分寸。",
    "noteBoundary": "享禮與私覿不是同一環節；如有循譯補一條線只供理解，不能當作實際地面劃線的記錄。",
    "canonicalId": 245,
    "historicalIds": [
      245
    ],
    "sourceRowSha256": "5a0ab10bf6a4c5cfc41118bdaa0b9b0b5c58a7c4db59fffc08391b9abe5aad24"
  },
  {
    "coordinate": "10.6",
    "quote": "羔裘玄冠不以吊",
    "primary": "li",
    "concepts": [
      "li",
      "sang",
      "gong",
      "jian-frugal"
    ],
    "reason": "衣色、內外配搭、居家便宜及吉凶場合呈衣服之禮，兼顧身份、情境與實用。",
    "noteBoundary": "固定底本後半譯文回抄原文，已接受原樣保存，舊缺譯待核門檻已取代；寝衣為被、殺為裁減。吉月注否定兩說但所從程說未展示，不代補缺文或推定日期。",
    "canonicalId": 246,
    "historicalIds": [
      246
    ],
    "sourceRowSha256": "81ce5eb3e8d1c4d511d88e9b154b05a58c93456fb01ad5ee55600d985838bf85"
  },
  {
    "coordinate": "10.7",
    "quote": "齐必变食",
    "primary": "ji",
    "concepts": [
      "ji",
      "li",
      "shen"
    ],
    "reason": "齋戒時浴衣、飲食與居處的改變，呈現祭前身心整備。",
    "noteBoundary": "齊為齋；變食三說與布料各說依註保留，不一概譯成現代素食或棉布。",
    "canonicalId": 247,
    "historicalIds": [
      247
    ],
    "sourceRowSha256": "7c304b680b477aad35431a72ad01b98cb022b100a127afb825c0fd6a398ec743"
  },
  {
    "coordinate": "10.8",
    "quote": "唯酒无量，不及乱",
    "primary": "jie",
    "concepts": [
      "jie",
      "shen",
      "li",
      "xing-act"
    ],
    "reason": "食物、切割、時候、肉飯比例及飲酒有具體限制，關聯日常自制與禮俗。",
    "noteBoundary": "古代飲食行止不當作今日醫療或營養建議；不時與割不正各依楊注，亂為神志昏亂而非樂章結尾。",
    "canonicalId": 248,
    "historicalIds": [
      248
    ],
    "sourceRowSha256": "92382d62e07e64d1d5e9b21b56c331d62019825fb1d22c4cbc47387a5c3ae6f8"
  },
  {
    "coordinate": "10.9",
    "quote": "祭肉不出三日",
    "primary": "ji",
    "concepts": [
      "ji",
      "li",
      "shen"
    ],
    "reason": "祭於公的肉與其他祭肉保留時間有別，需連同祭禮流程理解。",
    "noteBoundary": "不宿肉的理由依楊注與領肉前已過夜有關；古代天數記載不能直接作所有現代食物的保存期限。",
    "canonicalId": 249,
    "historicalIds": [
      249
    ],
    "sourceRowSha256": "b25da6b84c0cdc2c46b393ddc6cca7f37bbbe6693a0dfda34e4e667aa2d93148"
  },
  {
    "coordinate": "10.10",
    "quote": "食不语，寝不言",
    "primary": "xing-act",
    "concepts": [
      "xing-act",
      "yan",
      "li"
    ],
    "reason": "以飲食與寢息時不交談記日常行止，可與同篇場合化的言語禮態合讀。",
    "noteBoundary": "這是特定活動的記述，非一概禁止交談；章中沒有說明現代醫學因果，不代補原因。",
    "canonicalId": 250,
    "historicalIds": [
      250
    ],
    "sourceRowSha256": "ab20a182ac7a9ef235fa5a18e732cef2fe9f1e3c06721ea9ed31574568073b43"
  },
  {
    "coordinate": "10.11",
    "quote": "瓜祭，必齐如也",
    "primary": "ji",
    "concepts": [
      "ji",
      "jing",
      "li"
    ],
    "reason": "簡單飯菜仍先祭且恭敬，突出祭食態度不隨飲食豐簡而取消。",
    "noteBoundary": "瓜祭的瓜楊注疑為必字訛，原文與註同時保存；齊如為如齋戒般，不斷定此餐實已完整齋戒。",
    "canonicalId": 251,
    "historicalIds": [
      251
    ],
    "sourceRowSha256": "6d659814491cbd69df83970afeb0102afd20c99378fe673cb2355095f7d65803"
  },
  {
    "coordinate": "10.12",
    "quote": "席不正，不坐",
    "primary": "li",
    "concepts": [
      "li",
      "zheng-upright",
      "xing-act"
    ],
    "reason": "就座前看席是否正，將坐席安排與禮的秩序相連。",
    "noteBoundary": "不正有不端正和不合禮制方向兩解，底本取後者；正在此與席位相應，不直接判坐者道德正直。",
    "canonicalId": 252,
    "historicalIds": [
      252
    ],
    "sourceRowSha256": "682b3995bed8fb3b749aab39c212ff25e1b0cebfc458f984b7815cb05bed8522"
  },
  {
    "coordinate": "10.13",
    "quote": "杖者出，斯出矣",
    "primary": "jing",
    "concepts": [
      "jing",
      "li",
      "rang"
    ],
    "reason": "鄉飲酒禮中等年長者先出，呈現敬老與次序。",
    "noteBoundary": "杖者依上下文為老年人，非持武器者；讓為思想關聯，原文没有讓字而不是字面命中。",
    "canonicalId": 253,
    "historicalIds": [
      253
    ],
    "sourceRowSha256": "2fd2329497c01269ceaa4cfd2885f1b958e987818b422689d05c11969b4ce5d7"
  },
  {
    "coordinate": "10.14",
    "quote": "朝服而立于阼阶",
    "primary": "guishen",
    "concepts": [
      "guishen",
      "li",
      "gong"
    ],
    "reason": "鄉人行傩時著朝服立阼階，保存地方禮俗與孔子在場態度。",
    "noteBoundary": "傩為迎神逐疫古俗，不作療效主張；註頭滩與正文傩不一致按已接受固定底本原樣保留，舊待核門檻已取代。",
    "canonicalId": 254,
    "historicalIds": [
      254
    ],
    "sourceRowSha256": "96cdb66c1bc0ca041eec27c6bf6ce0f111c1a8dee3a73d797b3c6012319ecbc2"
  },
  {
    "coordinate": "10.15",
    "quote": "再拜而送之",
    "primary": "pengyou",
    "concepts": [
      "pengyou",
      "li",
      "jing"
    ],
    "reason": "託人向他邦友人問好送禮，對受託者再拜送行，連到朋友情意與禮節。",
    "noteBoundary": "問在此為問訊，不是問學；送禮由楊注所引用例補明，再拜是兩拜並非再來一次的泛稱。",
    "canonicalId": 255,
    "historicalIds": [
      255
    ],
    "sourceRowSha256": "cd5fa0ba65b269c2d24a3a9de87b5505766145b4369c12bdf625c4c50d60cf51"
  },
  {
    "coordinate": "10.16",
    "quote": "丘未达，不敢尝",
    "primary": "shen",
    "concepts": [
      "shen",
      "zhi",
      "li"
    ],
    "reason": "受藥仍明言不知其藥性而不嘗，將接受他人好意與承認未知分開。",
    "noteBoundary": "本章未寫藥名、處方或服後結果，不能補作醫療建議；拜受不等於同意使用。",
    "canonicalId": 256,
    "historicalIds": [
      256
    ],
    "sourceRowSha256": "d3d4732a6c3af361e49b193f66a7d30ae02b2303ca8d19b23fb13b0b140560a7"
  },
  {
    "coordinate": "10.17",
    "quote": "伤人乎",
    "primary": "airen",
    "concepts": [
      "airen",
      "ai",
      "ren"
    ],
    "reason": "馬廄失火後先問傷人而不問馬，作為關懷人的具體事例。",
    "noteBoundary": "仁與愛人為思想關聯，原句沒有這些德目字詞；不問馬不能推成孔子從不關心動物。",
    "canonicalId": 257,
    "historicalIds": [
      257
    ],
    "sourceRowSha256": "2c96dc90223fd70a4365c082bb7a0bd67caff8fcd396458db63a812c78fac562"
  },
  {
    "coordinate": "10.18",
    "quote": "君赐生，必畜之",
    "primary": "li",
    "concepts": [
      "li",
      "jun",
      "jing"
    ],
    "reason": "君賜熟食、生肉、活物及侍食各有不同應對，保存禮制情境和受賜處置。",
    "noteBoundary": "薦依楊注為進奉祖先而不能看作祭祀；不將未指名的祖先直接歸作父母。熟食、生肉、活物分別處置，侍食君祭另有情境。",
    "canonicalId": 258,
    "historicalIds": [
      258
    ],
    "sourceRowSha256": "90604f2eff34bfbe6d25bcea43d36f970afcad84f71ca8d46627539ff907c75d"
  },
  {
    "coordinate": "10.19",
    "quote": "疾，君视之",
    "primary": "jing",
    "concepts": [
      "jing",
      "li",
      "jun"
    ],
    "reason": "病中受國君探視仍以面向、朝服與紳表敬，呈現身體處境中的禮態。",
    "noteBoundary": "孔子仍臥床，朝服是加蓋在身上，不能誤寫成穿好衣冠起身迎接；東首理由依註。",
    "canonicalId": 259,
    "historicalIds": [
      259
    ],
    "sourceRowSha256": "2f1d97985517784ed681f156e30d8acf3668fc958b873d6a75a1094efaf380a3"
  },
  {
    "coordinate": "10.20",
    "quote": "不俟驾行矣",
    "primary": "jing",
    "concepts": [
      "jing",
      "jun",
      "xing-act"
    ],
    "reason": "受君召不待車駕便先行，記對召命的迅速回應。",
    "noteBoundary": "原章未提供召見事由或到達時間；不能轉作所有當代上下級關係都應無條件即刻回應。",
    "canonicalId": 260,
    "historicalIds": [
      260
    ],
    "sourceRowSha256": "80ff5993ace0d71356477cd0ba84bf98f6efefc42b11a42011f2ca1de4bb8feb"
  },
  {
    "coordinate": "10.21",
    "quote": "入太庙，每事问",
    "primary": "wen-ask",
    "concepts": [
      "wen-ask",
      "li"
    ],
    "reason": "太廟每事問與3.15互參，將禮的學習落在具體探問。",
    "noteBoundary": "譯文回抄原句按已接受固定底本原樣保存，舊待核門檻已取代；與3.15交叉閱讀但各保完整語境、ID和歷史紀錄，不自行合併。",
    "canonicalId": 261,
    "historicalIds": [
      261
    ],
    "sourceRowSha256": "7c726dfefd0948e37ebfd644471e5b978aca31727f1a146c7bfce9977a22a78c"
  },
  {
    "coordinate": "10.22",
    "quote": "于我殡",
    "primary": "pengyou",
    "concepts": [
      "pengyou",
      "sang",
      "ai"
    ],
    "reason": "朋友死而無人負責，孔子主動承擔殯葬，呈現朋友關係中的實際照料。",
    "noteBoundary": "殯依楊注在此涵蓋喪葬事務，不限停柩；原文沒寫死者姓名，不補造人物關係。",
    "canonicalId": 262,
    "historicalIds": [
      262
    ],
    "sourceRowSha256": "fae971f5c7daa45541f71839e9b08b2d4a5132d66a0b7a010b7ac4a4dea77999"
  },
  {
    "coordinate": "10.23",
    "quote": "非祭肉，不拜",
    "primary": "li",
    "concepts": [
      "li",
      "pengyou",
      "ji"
    ],
    "reason": "朋友饋贈是否行拜禮取決於祭肉的禮義而非車馬價值，與禮物大小作對照。",
    "noteBoundary": "不拜不是沒有友誼或從無致謝；只記本章特定接受禮節，不擴成所有禮物的通則。",
    "canonicalId": 263,
    "historicalIds": [
      263
    ],
    "sourceRowSha256": "7287d45bc3a65def07b2a89641ca4990fa5c1dfc5a295cfbb970f30f321f06df"
  },
  {
    "coordinate": "10.24",
    "quote": "寝不尸，居不客",
    "primary": "xing-act",
    "concepts": [
      "xing-act",
      "li"
    ],
    "reason": "寢與日常坐姿有別於待客姿態，呈現居家與正式場合的分際。",
    "noteBoundary": "居不客採底本校訂，注載容與客異文；平日坐式只是可能像蹲，不說已確證，也不將簡與儉混同。",
    "canonicalId": 264,
    "historicalIds": [
      264
    ],
    "sourceRowSha256": "e11420c6ba1f94ace5fbbdceaddf67763c46387c9d64c5b6b4a7ad11f3466132"
  },
  {
    "coordinate": "10.25",
    "quote": "虽狎，必变",
    "primary": "jing",
    "concepts": [
      "jing",
      "li",
      "sang",
      "gong"
    ],
    "reason": "熟悉的居喪者仍表同情禮貌，並列乘車致敬、盛饌與雷風時的態度，保存情境差別。",
    "noteBoundary": "式為扶軾動作，非公式；本章265與268為歷史別名，編輯只加一條，兩ID原有證據保持可回溯。",
    "canonicalId": 265,
    "historicalIds": [
      265,
      268
    ],
    "sourceRowSha256": "ecafd64c6b7b2cdc44b5a0d798ce9bb188b4b34c1a22245a0b4a8683e2d6af6d"
  },
  {
    "coordinate": "10.26",
    "quote": "车中，不内顾，不疾言，不亲指",
    "primary": "li",
    "concepts": [
      "li",
      "gong",
      "yan",
      "xing-act"
    ],
    "reason": "升車及車中姿態、言語和指示動作相連，展現出行禮節的具體要求。",
    "noteBoundary": "綏是扶手帶；本章266與269保留別名映射，不因重見文本生成兩次學習完成。",
    "canonicalId": 266,
    "historicalIds": [
      266,
      269
    ],
    "sourceRowSha256": "7000a6d50618b872dc32dd95226654cef642ef3959aaf1e58b02fb5015263972"
  },
  {
    "coordinate": "10.27",
    "quote": "时哉时哉",
    "primary": "shi-time",
    "concepts": [
      "shi-time",
      "guan",
      "xing-act"
    ],
    "reason": "見雌雉飛集而嘆得時，提供時與情境的字詞入口，保留觀察敘事。",
    "noteBoundary": "本章楊注明言文字費解、疑有脫誤；不強加確定的政治進退寓意，嗅依注疑狊，267與270歷史ID不丟失。",
    "canonicalId": 267,
    "historicalIds": [
      267,
      270
    ],
    "sourceRowSha256": "a9a297b260776f5463b845c296d27302010898bf6257bce806501a3546dda45d"
  },
  {
    "coordinate": "11.1",
    "quote": "如用之，则吾从先进",
    "primary": "xue",
    "concepts": [
      "xue",
      "shi-office",
      "li",
      "yue",
      "zhiren"
    ],
    "reason": "底本以先學禮樂後仕與先有官位後學區分先進後進，孔子主張選用先學者。",
    "noteBoundary": "君子此處為卿大夫子弟的身分稱呼，野人為一般未有爵祿者；不可直接改寫成道德君子與小人的對立。",
    "canonicalId": 271,
    "historicalIds": [
      271,
      297
    ],
    "sourceRowSha256": "41f35908bdb6929068dfa004bfa9c2b13109aa6484dba0058d58b5e73a9d5c24"
  },
  {
    "coordinate": "11.2",
    "quote": "从我于陈、蔡者，皆不及门也",
    "primary": "xue",
    "concepts": [
      "xue"
    ],
    "reason": "依底本所取受業之門解，回憶曾共歷陳蔡困厄而如今不在門下的弟子，作為求學經歷與師生記憶入口；不採被注否定的仕進之門解作主要分類。",
    "noteBoundary": "不及門依底本指不在此受業，註另列仕進之門等異說；不能推定下一章十人全部參與陳蔡困厄。",
    "canonicalId": 272,
    "historicalIds": [
      272,
      298
    ],
    "sourceRowSha256": "44ff2a2ddfa490e06433195cd0e60c72b8c82ba2f2b87ea80324a026e8409ba6"
  },
  {
    "coordinate": "11.3",
    "quote": "德行：颜渊，闵子骞，冉伯牛，仲弓",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "de",
      "yan",
      "zheng-politics",
      "wen"
    ],
    "reason": "德行、言語、政事、文學四科分記十位弟子所長，是人物與學習領域關聯的直接來源。",
    "noteBoundary": "文學指古代文獻，不限現代文學創作；四科十人與11.2不可強接為同一隨行名單，也不壓成統一成績排名。",
    "canonicalId": 273,
    "historicalIds": [
      273,
      299
    ],
    "sourceRowSha256": "f01427ebb0607e99cc89dcddbd2a0349df2fdfd5ead50db64c92f7482c8502b5"
  },
  {
    "coordinate": "11.4",
    "quote": "于吾言无所不说",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "yan",
      "xue",
      "si"
    ],
    "reason": "對顏回無所不悅的評語，提供師生間回應與幫助的思考材料。",
    "noteBoundary": "說讀悅；本章未記具體爭辯內容或明說顏回不會思考，不能以非助我否定其他章對顏回的評價。",
    "canonicalId": 274,
    "historicalIds": [
      274,
      300
    ],
    "sourceRowSha256": "08fc8fb8fffc0dea8f88262f54fdf3e836e55c1497c0250e2732589b5412abee"
  },
  {
    "coordinate": "11.5",
    "quote": "孝哉闵子骞",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu",
      "xiongdi",
      "zhiren"
    ],
    "reason": "閔子騫孝的評價由父母兄弟之言及他人無異議共同呈現，保留評語來源層次。",
    "noteBoundary": "間在此為非議；本章未敘後世蘆衣等故事，不把傳說補作這一章的實際記錄。",
    "canonicalId": 275,
    "historicalIds": [
      275,
      301
    ],
    "sourceRowSha256": "20be61810bbec4ab4f0d422ec57bed46073316a4da1b0d4966ae4b5c76102ea0"
  },
  {
    "coordinate": "11.6",
    "quote": "南容三复白圭",
    "primary": "jin",
    "concepts": [
      "jin",
      "yan",
      "shi-poetry",
      "zhiren"
    ],
    "reason": "反覆讀白圭詩以重言語不可輕玷，孔子據此以侄女妻之，與5.2人物行止互參。",
    "noteBoundary": "三復依譯為反覆讀而非恰三次；詩全文在譯注，不偽作正文直接出現，婚配不作當代教學獎賞。",
    "canonicalId": 276,
    "historicalIds": [
      276,
      302
    ],
    "sourceRowSha256": "84b1bb9bc2bd1974880366c4a6274ef53465be854ba276a162df951b462a1738"
  },
  {
    "coordinate": "11.7",
    "quote": "有颜回者好学",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "si-death"
    ],
    "reason": "季康子問好學而孔子稱顏回並悼其死，與6.3回答魯哀公的詳說相連。",
    "noteBoundary": "兩次提問者與詳略分別保存，不拼成同一次問答；亡通無，不用缺少其他稱許推定全體弟子都不學。",
    "canonicalId": 277,
    "historicalIds": [
      277,
      303
    ],
    "sourceRowSha256": "0a537e5b3a9c8c27c7f3b310120ab999d8a1862c83c91a6018dfd7bcd7f666c3"
  },
  {
    "coordinate": "11.8",
    "quote": "有棺而无椁",
    "primary": "sang",
    "concepts": [
      "sang",
      "li",
      "fumu",
      "jian-frugal"
    ],
    "reason": "顏路求賣車置椁，孔子以親子喪葬及大夫身分說明不從，呈情感、財力與禮制的取捨。",
    "noteBoundary": "正文⑸與注頭⑹不一致按已接受固定底本保留，舊待核門檻已取代；拒賣車不等不哀顏回，生卒年不自行修正。",
    "canonicalId": 278,
    "historicalIds": [
      278,
      304
    ],
    "sourceRowSha256": "15d58fd5612bf148ebd332559ac2bb13cab2c960a6c9d356dd9c5cab5e29c15f"
  },
  {
    "coordinate": "11.9",
    "quote": "天丧予",
    "primary": "sang",
    "concepts": [
      "sang",
      "tian",
      "si-death",
      "ai"
    ],
    "reason": "反覆天喪予表達顏回死帶來的深重哀痛，連到師生關係與天的言說。",
    "noteBoundary": "楊注明譯文僅就字面；此處感嘆不是孔子即將死亡的日期預告，也不是學生過失的判罰。",
    "canonicalId": 279,
    "historicalIds": [
      279,
      305
    ],
    "sourceRowSha256": "a33fd93a507c9f940f7fbe0cc549f7b59163ebaedf4ffd09cefdc0c003245bae"
  },
  {
    "coordinate": "11.10",
    "quote": "非夫人之为恸而谁为",
    "primary": "sang",
    "concepts": [
      "sang",
      "ai",
      "li"
    ],
    "reason": "孔子為顏回慟哭並回應從者，呈現哀情表達與他人對其程度的評議。",
    "noteBoundary": "夫人指那個人即顏回，不是妻子；恸有容貌變動與哀過兩解，底本取後者。",
    "canonicalId": 280,
    "historicalIds": [
      280,
      306
    ],
    "sourceRowSha256": "ea36369e000db08befd1c2837c915c3b5d06c8a3fc0a78497691f7c9c772d477"
  },
  {
    "coordinate": "11.11",
    "quote": "门人欲厚葬之",
    "primary": "sang",
    "concepts": [
      "sang",
      "li",
      "jian-frugal",
      "fumu"
    ],
    "reason": "孔子反對厚葬而弟子仍行，顯示喪葬應稱家之有無，並非越厚越能表達情義。",
    "noteBoundary": "非我也明說厚葬不是孔子主張；看待如父子的情感與喪禮決策分開，不能把弟子做法算作孔子授意。",
    "canonicalId": 281,
    "historicalIds": [
      281,
      307
    ],
    "sourceRowSha256": "73bbb4d8127812903d11a805adc8df9c6428a33130147248e4b48595a1a449f3"
  },
  {
    "coordinate": "11.12",
    "quote": "未知生，焉知死",
    "primary": "sheng-life",
    "concepts": [
      "sheng-life",
      "si-death",
      "guishen",
      "zhi"
    ],
    "reason": "以事人先於事鬼、知生先於知死回應子路，將實際人事與死亡鬼神問題的次序突出。",
    "noteBoundary": "未能、未知是反問前提，不等於已證明鬼神不存在；敢為表敬詞，不能當作大膽冒險。",
    "canonicalId": 282,
    "historicalIds": [
      282,
      308
    ],
    "sourceRowSha256": "429cac214866388340f4a3caf27773f0521a8d54a0225082a480f538af234e84"
  },
  {
    "coordinate": "11.13",
    "quote": "若由也，不得其死然",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "gong",
      "gang",
      "si-death"
    ],
    "reason": "並記幾位弟子侍側神態，孔子喜而對子路剛強有所憂，連結識人與性格後果。",
    "noteBoundary": "行行讀hàng表剛強；不得其死是不得善終的擔憂，不能把後來死亡細節當作本章已敘述。",
    "canonicalId": 283,
    "historicalIds": [
      283,
      309
    ],
    "sourceRowSha256": "e8945cb1aabc69778ecdf65e87ff8a03d498f2931d4a2acb8e63214c20a35751"
  },
  {
    "coordinate": "11.14",
    "quote": "言必有中",
    "primary": "yan",
    "concepts": [
      "yan",
      "jian-frugal",
      "zheng",
      "zhiren"
    ],
    "reason": "閔子骞質疑翻造長府，孔子稱其言中，關聯審慎發言與公共工程的必要性。",
    "noteBoundary": "魯人依註為執政大臣，不是所有百姓；仍舊貫是此事的主張，不能擴成反對一切必要改建。",
    "canonicalId": 284,
    "historicalIds": [
      284,
      310
    ],
    "sourceRowSha256": "440a7e0b1dbd21ea03926d1bc4c4b452dde9b3208912f203b6a4efdfb1981d09"
  },
  {
    "coordinate": "11.15",
    "quote": "由也升堂矣，未入于室也",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "xue",
      "yue",
      "jing"
    ],
    "reason": "先評子路瑟音，後制止弟子輕視而指出學習已有階段，呈現具體批評與承認進步。",
    "noteBoundary": "堂室為學問深淺的比喻，非實際通關房間；孔子不悅的是音調，不能說他禁止子路學瑟。",
    "canonicalId": 285,
    "historicalIds": [
      285,
      311
    ],
    "sourceRowSha256": "668764dad6ec0bdc91ad48f01c481ae620c994b925d8cc4a4e40cc72a8b8d3fc"
  },
  {
    "coordinate": "11.17",
    "quote": "为之聚敛而附益之",
    "primary": "yili",
    "concepts": [
      "yili",
      "yi",
      "fu",
      "min",
      "chen",
      "zheng"
    ],
    "reason": "冉求為富季氏聚斂受嚴厲批評，將臣屬行為與民生、義利取捨相連。",
    "noteBoundary": "周公所指兩說保留；原譯是大張旗鼓來攻擊。教學解讀限於對聚斂行為的公開批評，不冒稱原譯直接写公開批評，也不作允許施暴指令。",
    "canonicalId": 287,
    "historicalIds": [
      287,
      313
    ],
    "sourceRowSha256": "260fcfadf84eb485c84fd2f854704bcef427208fe8a13ce8a0536fca745ff61a"
  },
  {
    "coordinate": "11.18",
    "quote": "柴也愚，参也鲁，师也辟，由也喭",
    "primary": "zhiren",
    "concepts": [
      "zhiren"
    ],
    "reason": "對四位弟子的不同評語為人物差異提供一組原始材料，可與各自其他章句互證。",
    "noteBoundary": "辟為偏激，魯為遲鈍；這些單章評語不能固定成學生永久能力或心理診斷，也不替代其一生表現。",
    "canonicalId": 288,
    "historicalIds": [
      288,
      314
    ],
    "sourceRowSha256": "548f0d5c21d368e8964df18b13b781464d38a9f0ceff29a8d9f6ad832873aa42"
  },
  {
    "coordinate": "11.19",
    "quote": "回也其庶乎，屡空",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "pin",
      "fu",
      "ming",
      "zhi"
    ],
    "reason": "顏回學德近而屢空、子貢貨殖而屢中並說，呈現才能、生活與評價的多面。",
    "noteBoundary": "命為天命還是官命等說未決，底本譯作不安本分；不得將億屢中當成金融預測能力的確證。",
    "canonicalId": 289,
    "historicalIds": [
      289,
      315
    ],
    "sourceRowSha256": "f99db9429be7ef0360769155a751f8115a98c9347a815a32c105f177f8e6eda2"
  },
  {
    "coordinate": "11.20",
    "quote": "不践迹，亦不入于室",
    "primary": "shanren",
    "concepts": [
      "shanren",
      "xue",
      "dao"
    ],
    "reason": "問善人之道，答不踐迹又不入室，為善人與學德進深的關係提供比較章。",
    "noteBoundary": "依本底本保留難以到家的解釋，勿將善人直接等同聖人；室仍是比喻，可與7.26、13.11對讀。",
    "canonicalId": 290,
    "historicalIds": [
      290,
      316
    ],
    "sourceRowSha256": "129b5adff3db97c239748656e50d21c38b8b1af8cb9c272d8fdf8a6022e887e8"
  },
  {
    "coordinate": "11.21",
    "quote": "君子者乎？色庄者乎",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "junzi",
      "yan",
      "cheng"
    ],
    "reason": "對言論篤實者仍問是真君子或色莊，要求由表面言貌再辨實質。",
    "noteBoundary": "論篤是與為推許論篤者的倒裝；問句保留判斷未定，不據莊重表情直接確診虛偽。",
    "canonicalId": 291,
    "historicalIds": [
      291,
      317
    ],
    "sourceRowSha256": "d080cea15cb4d2093ae772de5277763762766dd136c94135a13d22a6ffc52dfc"
  },
  {
    "coordinate": "11.23",
    "quote": "吾以女为死矣",
    "primary": "ai",
    "concepts": [
      "ai",
      "sheng-life",
      "si-death"
    ],
    "reason": "孔子困匡後見顏回遲來，言語流露師生間牽掛，與9.5的困厄情境互參。",
    "noteBoundary": "何敢死為情感回應，不是自控生死的承諾；章中沒有說明顏回遲來的確切原因。",
    "canonicalId": 293,
    "historicalIds": [
      293,
      319
    ],
    "sourceRowSha256": "7a4625d769859dae7776d861d1e80979f7833a53f5a7b28ab12b5c7171e2af14"
  },
  {
    "coordinate": "11.24",
    "quote": "以道事君，不可则止",
    "primary": "chen",
    "concepts": [
      "chen",
      "jun",
      "dao",
      "chushi",
      "zhiren"
    ],
    "reason": "以道事君且不行則止界定大臣，再與具臣及有所不從區分，保存臣屬原則。",
    "noteBoundary": "具臣按本譯為有相當才能之臣，不直接等於毫無用處；子路冉求非凡事盲從，末句保留拒絕界限。",
    "canonicalId": 294,
    "historicalIds": [
      294,
      320
    ],
    "sourceRowSha256": "d7e72687c368fe5edbc9963dabc6c3a33540e4376a9eddd7dcc31d75502a42fb"
  },
  {
    "coordinate": "11.25",
    "quote": "何必读书，然后为学",
    "primary": "xue",
    "concepts": [
      "xue",
      "shi-office",
      "jiaoxue",
      "ning-eloquence"
    ],
    "reason": "子路以實際治理反駁讀書要求而遭孔子斥佞，呈現學習準備、仕任與辯說的爭議。",
    "noteBoundary": "子路的話不是孔子主張；本章不能簡化成取消實踐或只讀書即可治理，子羔初任的具體能力未充分敘明。",
    "canonicalId": 295,
    "historicalIds": [
      295,
      321
    ],
    "sourceRowSha256": "a4be90987709c0adc1857b2899d2b0c320685c4edbe2fc2b6cf8d2afc6635c0c"
  },
  {
    "coordinate": "11.26",
    "quote": "为国以礼，其言不让",
    "primary": "zhi-aspiration",
    "concepts": [
      "zhi-aspiration",
      "zheng",
      "li",
      "rang",
      "jiaoxue",
      "yue",
      "min"
    ],
    "reason": "四人侍坐各言志，孔子與點並解釋哂由因言不讓，將政治理想、禮態及施教對話共同呈現。",
    "noteBoundary": "不截成孔子否定一切從政；首句與曾點語依當前底本保留，不改成未核對譯法，方六七十為邊長而非面積數。",
    "canonicalId": 296,
    "historicalIds": [
      296,
      322
    ],
    "sourceRowSha256": "c8213e325f4c91ce32e45def10e261fae3050dec73c8d89376fc7c2bc9125aba"
  },
  {
    "coordinate": "12.3",
    "quote": "为之难，言之得无讱乎",
    "primary": "ren",
    "concepts": [
      "ren",
      "yan",
      "xing-act",
      "jin"
    ],
    "reason": "司馬牛問仁，孔子以行難所以言讱回應，將發言謹慎與實踐負擔相連。",
    "noteBoundary": "楊注引史記說答語針對司馬牛多言而躁；不能把口齒遲緩本身等同仁德，亦不省去問者的再問。",
    "canonicalId": 325,
    "historicalIds": [
      325
    ],
    "sourceRowSha256": "fa60c7a3b646e7b6924b1834c057897aa7d6047c7ae5c987876b9d4033e6b9aa"
  },
  {
    "coordinate": "12.4",
    "quote": "内省不疚，夫何忧何惧",
    "primary": "sheng",
    "concepts": [
      "sheng",
      "junzi"
    ],
    "reason": "以內省無疚說明君子不憂不懼的依據，重點是反省後的內在狀態。",
    "noteBoundary": "先有司馬牛質疑再有理由，不截取首句作無條件心理保證；有憂懼也不能反推一個人必有道德過錯。",
    "canonicalId": 326,
    "historicalIds": [
      326
    ],
    "sourceRowSha256": "f7595017ca9ae55b84ce2b7e5a57e450f22815e77a0c3b3893a4c9fb7b7e7fa6"
  },
  {
    "coordinate": "12.5",
    "quote": "君子敬而无失，与人恭而有礼",
    "primary": "xiongdi",
    "concepts": [
      "xiongdi",
      "jing",
      "gong",
      "li",
      "tianming",
      "fugui"
    ],
    "reason": "子夏用敬、恭、禮及四海兄弟勸慰司馬牛，將親族匱缺感與廣泛交往相連。",
    "noteBoundary": "此為子夏引所聞，不改成孔子直接答語；楊注對兩個司馬牛是否同一人持疑，不合併人物身份或親屬資料。",
    "canonicalId": 327,
    "historicalIds": [
      327
    ],
    "sourceRowSha256": "f151ecb65d148336a757570dcfbe583c02277fe39efbbd465f3576bac5a65194"
  },
  {
    "coordinate": "12.6",
    "quote": "浸润之谮，肤受之愬，不行焉",
    "primary": "wisdom",
    "concepts": [
      "wisdom",
      "zhi",
      "zhiren",
      "shen"
    ],
    "reason": "漸積讒言與切身急迫的誣告均不行，作為明與遠的判準，關聯辨察言語。",
    "noteBoundary": "原文論辨明而非主張忽略所有求助與控訴；讒、愬有其負面語義，不能把未核實訊息一律判成誣告。",
    "canonicalId": 328,
    "historicalIds": [
      328
    ],
    "sourceRowSha256": "fcacc817fd08df021c8c30fc298abfa0164505358f0d1940621fc09aa30e0dd6"
  },
  {
    "coordinate": "12.8",
    "quote": "文犹质也，质犹文也",
    "primary": "wenzhi",
    "concepts": [
      "wenzhi",
      "wen",
      "zhi-quality",
      "junzi",
      "yan"
    ],
    "reason": "棘子成只重質，子貢以獸皮譬喻反駁，明說文質同樣重要。",
    "noteBoundary": "夫子此指棘子成而非孔子；君子句的斷句有異說，正文與楊注均保留，不把對話合成單一說話人。",
    "canonicalId": 330,
    "historicalIds": [
      330
    ],
    "sourceRowSha256": "9cb60c70e81f40721ee3297b166e5811bade709d52395933f4853cc8c57808f3"
  },
  {
    "coordinate": "12.9",
    "quote": "百姓不足，君孰与足",
    "primary": "min",
    "concepts": [
      "min",
      "zheng",
      "jun",
      "li-benefit"
    ],
    "reason": "有若以百姓足與君用度相依回應荒年減稅，將國家資用放回民生條件。",
    "noteBoundary": "十分抽一、抽二屬本章古代稅制解讀，不作現代最適稅率建議；答者為有若，不是孔子。",
    "canonicalId": 331,
    "historicalIds": [
      331
    ],
    "sourceRowSha256": "e74d85c07509b0de9c665b4eeac03afedaff46dfaddadb76a6d1c30254a69610"
  },
  {
    "coordinate": "12.10",
    "quote": "主忠信，徙义，崇德也",
    "primary": "chongde",
    "concepts": [
      "chongde",
      "bianhuo",
      "zhongxin",
      "yi",
      "ai",
      "yu"
    ],
    "reason": "崇德以忠信徙義為途徑，辨惑以愛惡轉變而欲人生死為反例，保留兩問兩答。",
    "noteBoundary": "所引詩句在此義難通、錯簡說無證據，按底本意譯且留疑；不把推測改作已校定原文。",
    "canonicalId": 332,
    "historicalIds": [
      332
    ],
    "sourceRowSha256": "dc481be972b3a1aad8e4d1110ea84f594fab0c0961d96c2c2ff8c1946a129880"
  },
  {
    "coordinate": "12.11",
    "quote": "君君，臣臣，父父，子子",
    "primary": "zhengming",
    "concepts": [
      "zhengming",
      "zheng",
      "jun",
      "chen",
      "fumu"
    ],
    "reason": "問政以君臣父子各如其角色作答，與名分及相應責任的問題相連。",
    "noteBoundary": "正名為思想比較而非本章直接字詞；保留齊景公的回應，不只剩對下位者的單向要求。",
    "canonicalId": 333,
    "historicalIds": [
      333
    ],
    "sourceRowSha256": "f07d780f3fd3abfb86157143f9d7463de57eeda9d410179892e61e99da89195b"
  },
  {
    "coordinate": "12.12",
    "quote": "子路无宿诺",
    "primary": "xin",
    "concepts": [
      "xin",
      "cheng",
      "zhi-upright",
      "zheng"
    ],
    "reason": "片言折獄與不拖延諾言同載子路，提供誠信直率評價及其表現的材料。",
    "noteBoundary": "楊注明說不能以單方言辭作一般裁判規則；兩句邏輯關係未明且古本有分章說，不自行補出因果。",
    "canonicalId": 334,
    "historicalIds": [
      334
    ],
    "sourceRowSha256": "d6406ceacac6d1cd7538c179d33e6599ed464d73fa937ad66bafca35dd5ab73d"
  },
  {
    "coordinate": "12.13",
    "quote": "必也使无讼乎",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "min",
      "xing"
    ],
    "reason": "從聽訟與人相同轉到使無訟的期望，關聯治理如何減少爭端。",
    "noteBoundary": "原文沒有具體措施，不能把無訟說成壓制合法申訴；說話日期僅注中猜測，不據此確定行旅年表。",
    "canonicalId": 335,
    "historicalIds": [
      335
    ],
    "sourceRowSha256": "e73d55f105ffe15bf9802f6a0ab669579b867f8a0fff1a81d699608c6cc815d6"
  },
  {
    "coordinate": "12.14",
    "quote": "居之无倦，行之以忠",
    "primary": "zhong",
    "concepts": [
      "zhong",
      "zheng",
      "xing-act",
      "heng"
    ],
    "reason": "居位不倦與施行盡心並列，將治理責任落在持續性和忠實執行。",
    "noteBoundary": "無倦不是抹去人的休息需要；忠須連問政語境，不直接等同对私人上級無条件服從。",
    "canonicalId": 336,
    "historicalIds": [
      336
    ],
    "sourceRowSha256": "e666d5effd87f9eef33c86aa09cf81a0aaab7a99cfabff69dae3ff7e4536e972"
  },
  {
    "coordinate": "12.15",
    "quote": "博学于文，约之以礼",
    "primary": "boxue",
    "concepts": [
      "boxue",
      "xue",
      "wen",
      "li"
    ],
    "reason": "博文約禮再現，與6.27比較其共同內容而保留各章來源。",
    "noteBoundary": "當前譯文欄誤載註釋轉引，不能說已有完整譯文；不因相似文字而刪除歷史章句或改寫進度。",
    "canonicalId": 337,
    "historicalIds": [
      337
    ],
    "sourceRowSha256": "5a1ad8b84f96c606bd77e6a4545f723575ea039001086c00596bf3bc8a213593"
  },
  {
    "coordinate": "12.16",
    "quote": "君子成人之美，不成人之恶",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xiaoren",
      "ai",
      "yi"
    ],
    "reason": "以助人成美、不助成惡區別君子小人，呈現幫助他人仍需判別所成之事。",
    "noteBoundary": "成人在此為成全別人，不與14.12成人的人格稱號等同；不將朋友所求無條件視為美。",
    "canonicalId": 338,
    "historicalIds": [
      338
    ],
    "sourceRowSha256": "dfb89a14400df8b93dbad43ab5bb3b8e227966f6a58fded6a4e639c6b4e78383"
  },
  {
    "coordinate": "12.18",
    "quote": "苟子之不欲",
    "primary": "yu",
    "concepts": [
      "yu",
      "zheng",
      "min",
      "de"
    ],
    "reason": "對季康子患盜，孔子將問題指回在上者的貪求，與德行示範相連。",
    "noteBoundary": "雖賞不竊是此答的強調，不是實驗結果或保證所有犯罪自然消失；須保留提問者的政治位置。",
    "canonicalId": 340,
    "historicalIds": [
      340
    ],
    "sourceRowSha256": "4a9ae0a8ca41cf563fdd8682fb51a35ce8d98fbbbb064d36e150a8ebf6cba72c"
  },
  {
    "coordinate": "12.19",
    "quote": "君子之德风，小人之德草",
    "primary": "zhengyide",
    "concepts": [
      "zhengyide",
      "de",
      "zheng",
      "min",
      "xing"
    ],
    "reason": "反對藉殺無道來親有道，轉以在上者向善影響百姓，呈現治理方式的取捨。",
    "noteBoundary": "君子小人依底本此處為領導者與百姓，不當作兩類德行高下；風草為譬喻，不取消個人行為的差異。",
    "canonicalId": 341,
    "historicalIds": [
      341
    ],
    "sourceRowSha256": "2a83857599c8431132a985c3e2f06e04d10fabfbb5ed963aaaba743ce359b261"
  },
  {
    "coordinate": "12.20",
    "quote": "是闻也，非达也",
    "primary": "da-attainment",
    "concepts": [
      "da-attainment",
      "wen-hear",
      "shi",
      "zhi-upright",
      "yi",
      "zhiren"
    ],
    "reason": "固定底本現存原文已有聞達區別、質直好義及察言，譯文續述觀色退讓與聞者表裏；據各自來源層建立比較入口，保留古文截斷。",
    "noteBoundary": "原文字段止於察言，譯文保有後半，按serial24固定底本接受原樣保留；sourceCompleteness旧review-required及概念草案補源前置門檻已被取代。不從譯文反造古文，也不宣稱缺段已恢復；全主題對談須取完整已接受集合並標此來源缺口。",
    "sourceCompleteness": "original-truncated-review-required",
    "canonicalId": 342,
    "historicalIds": [
      342
    ],
    "sourceRowSha256": "6ac2f02607a3ab604a6b5653ab8a051d27bde9e6425c7d178939a7e47503fcbd"
  },
  {
    "coordinate": "12.21",
    "quote": "先事后得",
    "primary": "chongde",
    "concepts": [
      "chongde",
      "xiute",
      "bianhuo",
      "sheng",
      "yili",
      "fumu"
    ],
    "reason": "先做事後收穫、反求自身惡及戒一朝之忿分答崇德修慝辨惑，將取捨、自省與親身後果相連。",
    "noteBoundary": "修慝的譯文依本底本作消除不露面的怨恨；保留原語攻其惡，不擅換成未核對版本的解釋。",
    "canonicalId": 343,
    "historicalIds": [
      343
    ],
    "sourceRowSha256": "e23d31994fbfff06e11b87ab43ba951c7f5c494a65b6403c31979818b5ef5faa"
  },
  {
    "coordinate": "12.23",
    "quote": "忠告而善道之，不可则止",
    "primary": "you",
    "concepts": [
      "you",
      "pengyou",
      "zhong",
      "yan",
      "jie"
    ],
    "reason": "對朋友忠心勸告善加引導，若不聽則止，兼顧友誼責任與勸說界限。",
    "noteBoundary": "道在此作引導，不單指抽象道理；不可則止不是從此取消一切朋友關係的明令。",
    "canonicalId": 345,
    "historicalIds": [
      345
    ],
    "sourceRowSha256": "ad2604ef8ddadeef42a04820d9f6680f50ad0583e9a4cdead73251854081b64a"
  },
  {
    "coordinate": "12.24",
    "quote": "君子以文会友，以友辅仁",
    "primary": "pengyou",
    "concepts": [
      "pengyou",
      "you",
      "wen",
      "ren",
      "junzi"
    ],
    "reason": "曾子以文會友、由友輔仁，把共同學習與相互成德相連。",
    "noteBoundary": "此為曾子語；文依底本為文章學問，不能把參加聚會或加入社群本身當作已達仁德。",
    "canonicalId": 346,
    "historicalIds": [
      346
    ],
    "sourceRowSha256": "2c6587e86fdc3df0e8816091347f8e8d6af95fa9a880224eed6fff4a0ba32cc5"
  },
  {
    "coordinate": "13.1",
    "quote": "先之劳之",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "xing-act",
      "heng",
      "min"
    ],
    "reason": "自己帶頭再使民勤勞，續以無倦補充，將治理要求放在在上者先行。",
    "noteBoundary": "先之依註與下一章先有司互參；不能只保留要求百姓勞作而刪去領導者先行的條件。",
    "canonicalId": 347,
    "historicalIds": [
      347
    ],
    "sourceRowSha256": "f6be1a8a94783e24e5d67dbbeeedc68c9eb6a20b9a55e9b015fc2c0bd3622dbb"
  },
  {
    "coordinate": "13.2",
    "quote": "赦小过，举贤才",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "xian",
      "guo",
      "zheng"
    ],
    "reason": "為政須帶頭、容小過、舉賢才，仲弓再問如何知賢，形成用人與認知範圍的對話。",
    "noteBoundary": "舉所知不等於只任私人親友；原文同時說未知人才可由他人舉出，不編造任用結果。",
    "canonicalId": 348,
    "historicalIds": [
      348
    ],
    "sourceRowSha256": "ff03740aa832d09d5c10849daa79a01d482c5be35c94d95db53304f9efd3ca76"
  },
  {
    "coordinate": "13.4",
    "quote": "上好礼，则民莫敢不敬",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "li",
      "yi",
      "xin",
      "xue",
      "min",
      "xiaoren"
    ],
    "reason": "樊遲求學稼圃而孔子轉論在上者禮義信的政治作用，呈學習專門技術與治理職責的張力。",
    "noteBoundary": "小人哉為孔子此處評語，完整保留但不轉成對當代農業工作者的貶抑；吾不如老農也不可省去。",
    "canonicalId": 350,
    "historicalIds": [
      350
    ],
    "sourceRowSha256": "04be7dbebdb70590c3f17458ea51a9acffc7e207605f4dd445554465345e3d66"
  },
  {
    "coordinate": "13.6",
    "quote": "其身正，不令而行",
    "primary": "zheng-upright",
    "concepts": [
      "zheng-upright",
      "zheng",
      "xing-act",
      "de"
    ],
    "reason": "用自身行為正與否說明命令能否被遵從，將示範與治理效能聯結。",
    "noteBoundary": "這是倫理政治的對比表述，不是可免除所有制度程序的實證規律；身正不能只以自我聲稱成立。",
    "canonicalId": 352,
    "historicalIds": [
      352
    ],
    "sourceRowSha256": "30f1262949f7adb90ac0a5bd24d751c02c15cf6ff8ceead3c50b48f8f3cf60be"
  },
  {
    "coordinate": "13.7",
    "quote": "鲁、卫之政，兄弟也",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "bang"
    ],
    "reason": "魯衛政治被比為兄弟，提供政治比較而非單國孤立閱讀的材料。",
    "noteBoundary": "兄弟在此為相近的譬喻，不列成親族倫理的已核關聯；原文未細列相似政策或評定高低。",
    "canonicalId": 353,
    "historicalIds": [
      353
    ],
    "sourceRowSha256": "75b31b75b5c553113330728d739570eaa1b4a13b82a466d04554c573710c8b17"
  },
  {
    "coordinate": "13.8",
    "quote": "善居室",
    "primary": "jian-frugal",
    "concepts": [
      "jian-frugal",
      "fu",
      "yu"
    ],
    "reason": "公子荊隨家業增長而每以足、完、美知足，連到用度與欲望。",
    "noteBoundary": "居室依註為經營家業，非監獄或婚居；以儉諷奢只列為注中可備一說，不能聲稱原文已點名批評某臣。",
    "canonicalId": 354,
    "historicalIds": [
      354
    ],
    "sourceRowSha256": "ee20d8cedec5ce441b43e15cf64d90f9f1a695287e9c2ff65cb7cf1fb0ee32e4"
  },
  {
    "coordinate": "13.9",
    "quote": "富之",
    "primary": "min",
    "concepts": [
      "min",
      "fu",
      "jiao",
      "zheng"
    ],
    "reason": "由庶到富再到教，將人口、民生及教育相繼提出，保留冉有的逐層追問。",
    "noteBoundary": "庶為人口眾多，不是庶幾；先富後教依楊注理解，不能推成任何貧困者都應被剝奪受教機會。",
    "canonicalId": 355,
    "historicalIds": [
      355
    ],
    "sourceRowSha256": "449006c250fd2ac6325ecde7ab47578f459efc68593bf447439112aba9291649"
  },
  {
    "coordinate": "13.10",
    "quote": "期月而已可也，三年有成",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "shi-office",
      "zhi-aspiration",
      "shi-time"
    ],
    "reason": "孔子假設受用而提出治理可見成效的期望，表達仕任與行道志向。",
    "noteBoundary": "期月依註讀jī為一年，非一個月；假設的三年有成不能當作已實現的歷史成果。",
    "canonicalId": 356,
    "historicalIds": [
      356
    ],
    "sourceRowSha256": "1eb6a9fa0c184048230ff9b0738b845628812248a8d60bc86bddc6284e6100ba"
  },
  {
    "coordinate": "13.11",
    "quote": "亦可以胜残去杀矣",
    "primary": "shanren",
    "concepts": [
      "shanren",
      "zheng",
      "xing",
      "min",
      "heng"
    ],
    "reason": "引用善人久治可以勝殘去殺，關聯治理延續與減少暴虐。",
    "noteBoundary": "整句依楊注是引他人之言，孔子加以稱許；百年不直接作精確政策生效期限。",
    "canonicalId": 357,
    "historicalIds": [
      357
    ],
    "sourceRowSha256": "05b744e0f267f23ac5eccf733416dbee590fd98e5bbe2bb9c6ff4e131631ac33"
  },
  {
    "coordinate": "13.12",
    "quote": "必世而后仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "zheng",
      "shi-time",
      "heng"
    ],
    "reason": "王者治理仍需一世而後仁，說明政治教化不會立即完成。",
    "noteBoundary": "世依底本為三十年；如有是假設，不能據此斷言所有治理措施需恰好三十年。",
    "canonicalId": 358,
    "historicalIds": [
      358
    ],
    "sourceRowSha256": "6b27db236a745b34251c0626b6edc93b896e50c5a52790fdeafd4f283bbcdcc8"
  },
  {
    "coordinate": "13.13",
    "quote": "不能正其身，如正人何",
    "primary": "xiuyi",
    "concepts": [
      "xiuyi",
      "zheng-upright",
      "zheng",
      "xing-act"
    ],
    "reason": "以正身先於正人說從政，與13.6示範和命令的關係相互補充。",
    "noteBoundary": "正其身不是原文直接寫修己，需標為思想關聯；反問不能當作單憑私人德行便足以治理所有事務。",
    "canonicalId": 359,
    "historicalIds": [
      359
    ],
    "sourceRowSha256": "5dcd1019b2b7b9da1f47947fdc0a3b47ceb10f192ae8b23698b80faa90f9ac09"
  },
  {
    "coordinate": "13.14",
    "quote": "其事也。如有政",
    "primary": "zheng-politics",
    "concepts": [
      "zheng-politics",
      "zheng",
      "wen-hear"
    ],
    "reason": "冉有以有政解釋晚歸，孔子辨政與事的用詞，呈現政治事務範圍的區別。",
    "noteBoundary": "與聞為參與聽聞；楊注不取其他過度深解，不能據一句推造政變或陰謀。",
    "canonicalId": 360,
    "historicalIds": [
      360
    ],
    "sourceRowSha256": "7761cff232d43fb588d15e6134c835355bf07a830da8a2d72a1fa48f2626127f"
  },
  {
    "coordinate": "13.15",
    "quote": "如不善而莫之违也",
    "primary": "yan",
    "concepts": [
      "yan",
      "jun",
      "chen",
      "zheng",
      "shen"
    ],
    "reason": "先否定機械的一言興喪邦，再以知君難及無人反對不善之言說政治言語的影響。",
    "noteBoundary": "興邦喪邦是接近的可能推論，不是單句自動造成國運；若善而無違與若不善而無違須同時保留。",
    "canonicalId": 361,
    "historicalIds": [
      361
    ],
    "sourceRowSha256": "d3c5fa4161757d91f7602b7f4379a73279e208a22faff1fa158f7343bddb29aa"
  },
  {
    "coordinate": "13.16",
    "quote": "近者悦，远者来",
    "primary": "min",
    "concepts": [
      "min",
      "zheng"
    ],
    "reason": "以境內歡悅、境外願來回答問政，連結民眾感受與治理吸引力。",
    "noteBoundary": "遠來不能直接換算現代移民或人口KPI；原文未列措施，不能據此虛構已實施的制度。",
    "canonicalId": 362,
    "historicalIds": [
      362
    ],
    "sourceRowSha256": "a35d37ec93ddcd96dd8bcab7d5dbe2ca648a75134907214539ab016f29cca6c7"
  },
  {
    "coordinate": "13.17",
    "quote": "欲速，则不达；见小利，则大事不成",
    "primary": "li-benefit",
    "concepts": [
      "li-benefit",
      "yu",
      "da-attainment",
      "zheng",
      "shen"
    ],
    "reason": "對子夏問政警戒求速與小利，說明眼前取捨可能妨害較大的成事。",
    "noteBoundary": "達在此為達到目的，與12.20聞達之辨相關而不同；莒父今址注稱不能確知，不作精確地圖定位。",
    "canonicalId": 363,
    "historicalIds": [
      363
    ],
    "sourceRowSha256": "cc0ad82a0a4af0180bd788cdbc9b7d92f74c1116ec9767e370f021a8de4a0292"
  },
  {
    "coordinate": "13.18",
    "quote": "父为子隐，子为父隐",
    "primary": "zhi-upright",
    "concepts": [
      "zhi-upright",
      "xiao",
      "fumu",
      "yi"
    ],
    "reason": "葉公與孔子對告發父親或父子相隱持不同直的理解，保留親情與公義之間可討論的張力。",
    "noteBoundary": "隱此處為隱瞞，不是退隱；證按注為告發，古代倫理主張不直接當作現代法律指引。",
    "canonicalId": 364,
    "historicalIds": [
      364
    ],
    "sourceRowSha256": "a34c35b22b7cf4af210704ca996419830038babab232645eb35350b3c17a54e8"
  },
  {
    "coordinate": "13.19",
    "quote": "居处恭，执事敬，与人忠",
    "primary": "ren",
    "concepts": [
      "ren",
      "gong",
      "jing",
      "zhong"
    ],
    "reason": "樊遲問仁得到居處、辦事與待人三方面回答，並說換了地域亦不可棄。",
    "noteBoundary": "之為到；恭敬忠分別對應情境，不把一句泛化成全部仁德只需三個外在動作。",
    "canonicalId": 365,
    "historicalIds": [
      365
    ],
    "sourceRowSha256": "f94e44a00f92b4e3eeded0cd7b8b0bfbc9a5d5a5a2d59f688f4034492dddfa89"
  },
  {
    "coordinate": "13.20",
    "quote": "行己有耻，使于四方，不辱君命",
    "primary": "shi",
    "concepts": [
      "shi",
      "chi",
      "xiao",
      "ti",
      "xin",
      "xing-act",
      "jun"
    ],
    "reason": "子貢追問士的不同層次，從知恥與使職到孝弟、信果，且批評硁硁與斗筲。",
    "noteBoundary": "言必信行必果在此帶局限，不截作不問是非的最高標準；斗筲注引說文字形缺漏按已接受固定底本保留，舊待核門檻已取代。",
    "canonicalId": 366,
    "historicalIds": [
      366
    ],
    "sourceRowSha256": "81081f3c84213154f2f634279f583875bde09d3c354ac4cf09cfd07db6dee4b8"
  },
  {
    "coordinate": "13.21",
    "quote": "狂者进取，狷者有所不为也",
    "primary": "kuangjuan",
    "concepts": [
      "kuangjuan",
      "kuang",
      "juan",
      "zhongyong"
    ],
    "reason": "不得中行才取狂狷，以進取與有所不為保留兩種各有所長的傾向。",
    "noteBoundary": "孟子補述可參考而楊注明未必盡合孔子本意；狂在此不作現代精神疾病診斷。",
    "canonicalId": 367,
    "historicalIds": [
      367
    ],
    "sourceRowSha256": "aacb8a806cfa8bb748cb305a0d636d019f544b349507741d750c2f446a419d5d"
  },
  {
    "coordinate": "13.22",
    "quote": "不恒其德",
    "primary": "heng",
    "concepts": [
      "heng",
      "de",
      "chi",
      "guishen"
    ],
    "reason": "引南人語及易辭稱有恆，將持續與操守連到不必依賴占問的提醒。",
    "noteBoundary": "巫醫依注為一詞，不分兩職；不恆兼時作時輟與無定操守，不推成古代禳禱已有療效。",
    "canonicalId": 368,
    "historicalIds": [
      368
    ],
    "sourceRowSha256": "d1885dea63573aac015903275ffb2311cd0296dba3858688ad4fe43afe31718b"
  },
  {
    "coordinate": "13.24",
    "quote": "不如乡人之善者好之，其不善者恶之",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "guan",
      "xiangyuan"
    ],
    "reason": "全鄉皆好或皆惡都不足決斷，還須辨好惡者的品格，將人氣與識人分開。",
    "noteBoundary": "鄉愿為楊注比較的概念，非正文直接用詞；不能用多數投票或聲量自動代替善惡與事實核查。",
    "canonicalId": 370,
    "historicalIds": [
      370
    ],
    "sourceRowSha256": "fcdc2e29d39e0b1ec677e0de331ece6e298706c90c89549c4f57c607f3ef1fbe"
  },
  {
    "coordinate": "13.25",
    "quote": "及其使人也，器之",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "junzi",
      "xiaoren",
      "qi-vessel",
      "dao"
    ],
    "reason": "君子以道為悅人條件、用人按才德，小人易悅卻求備，將取悅和工作分配相反對照。",
    "noteBoundary": "器在此為按材任用的動詞譬用，不是實際器皿；說讀悅，難說不是難以說明。",
    "canonicalId": 371,
    "historicalIds": [
      371
    ],
    "sourceRowSha256": "8e49acf3c60fac54a445ad11429da36c7f603b3d1be153a602c132eeb5c5b725"
  },
  {
    "coordinate": "13.26",
    "quote": "君子泰而不骄",
    "primary": "tai",
    "concepts": [
      "tai",
      "junzi",
      "xiaoren",
      "gong"
    ],
    "reason": "君子的舒泰與小人的驕慢相辨，將內在安定和凌人姿態分開。",
    "noteBoundary": "泰依此章注為安詳舒泰，不能把7.26豪華或9.3倨傲義直接代入；同字檢索須保留詞義區別。",
    "canonicalId": 372,
    "historicalIds": [
      372
    ],
    "sourceRowSha256": "72120a73cbc13d30d296f1f3850f7a4ee028d6b719d642657325865933bdb6b7"
  },
  {
    "coordinate": "13.27",
    "quote": "刚、毅、木、讷近仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "gang",
      "ne",
      "zhi-quality"
    ],
    "reason": "剛毅木訥被稱近仁，將堅定、朴質及不輕言聯到仁的修養方向。",
    "noteBoundary": "近仁不是已完全等同仁；木依譯為朴質，訥為不輕出口，不以反應快慢或口語流暢度直接評德。",
    "canonicalId": 373,
    "historicalIds": [
      373
    ],
    "sourceRowSha256": "832dccf9a9734ab2ed708daa1f9ff4c6e23534e7b97ecf5b5c683855bffca940"
  },
  {
    "coordinate": "13.28",
    "quote": "朋友切切偲偲，兄弟怡怡",
    "primary": "shi",
    "concepts": [
      "shi",
      "pengyou",
      "xiongdi",
      "he",
      "yan"
    ],
    "reason": "問士以朋友責善、兄弟和順回答，保留不同親友關係的交往方式。",
    "noteBoundary": "切切偲偲為相互責善，非辱罵；怡怡不是對所有不當行為一概附和，偲依註讀sī。",
    "canonicalId": 374,
    "historicalIds": [
      374
    ],
    "sourceRowSha256": "977f0ffe42849f0fe9e127b50d479fd19a9cf0339c335489d77bcc66a6f3b0ad"
  },
  {
    "coordinate": "13.29",
    "quote": "善人教民七年",
    "primary": "jiao",
    "concepts": [
      "jiao",
      "min",
      "shanren"
    ],
    "reason": "教民多年方可即戎，呈現軍事行動前需要長期教導準備。",
    "noteBoundary": "七年為本章表述，不套為現代訓練時限；戎在此兵事，非民族稱呼。",
    "canonicalId": 375,
    "historicalIds": [
      375
    ],
    "sourceRowSha256": "3c385b4d71a02d9aed950346cce07fb947acadb1dfb8881f67507743ecce756a"
  },
  {
    "coordinate": "13.30",
    "quote": "以不教民战，是谓弃之",
    "primary": "min",
    "concepts": [
      "min",
      "jiao",
      "sheng-life"
    ],
    "reason": "把未经训练者用于战争说成弃之，关联治理者对民众生命及準備的責任。",
    "noteBoundary": "不教民是未受教之民的名詞結構，不是不要教育人民；不得反向讀作應放棄未學會者。",
    "canonicalId": 376,
    "historicalIds": [
      376
    ],
    "sourceRowSha256": "01cd7d1d820535c07142c235174ee2faf812d43145247327a9d312163286f786"
  },
  {
    "coordinate": "14.1",
    "quote": "仁则吾不知也",
    "primary": "chi",
    "concepts": [
      "chi",
      "chushi",
      "bangyoudao",
      "bangwudao",
      "ren",
      "fa",
      "yuan",
      "yu"
    ],
    "reason": "先問不辨有道無道皆取祿之恥，再問克伐怨欲不行能否為仁，孔子分難能與仁德。",
    "noteBoundary": "克此處為好勝，不直接等同克己；原憲後問雖形似肯定，依註及上下文應保留問句。",
    "canonicalId": 377,
    "historicalIds": [
      377
    ],
    "sourceRowSha256": "9430dc660308cea6fa8cc5deb1de928145bbf140534baeef20bf6ed991923ad4"
  },
  {
    "coordinate": "14.2",
    "quote": "士而怀居",
    "primary": "shi",
    "concepts": [
      "shi",
      "zhi-aspiration",
      "chushi"
    ],
    "reason": "以留戀安逸不足為士提醒其行道志向，與出仕退隱選擇相連。",
    "noteBoundary": "居依註為安居、懷為留戀，不等於有房屋便非士；不能據此否定人必要的居住與休息。",
    "canonicalId": 378,
    "historicalIds": [
      378
    ],
    "sourceRowSha256": "efb40d3fe515c0db1a311467ccfd866132dbe8515fb05225d2f5d94c86789e6a"
  },
  {
    "coordinate": "14.3",
    "quote": "危行言孙",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "zhi-upright",
      "yan",
      "xing-act",
      "bangyoudao",
      "bangwudao"
    ],
    "reason": "政治清明與黑暗時皆正行，言語則有所調整，呈現守行與表達方式的分別。",
    "noteBoundary": "危依本註為正，不是故意危險；孫通遜，不可把言遜解作可以背棄行為原則。",
    "canonicalId": 379,
    "historicalIds": [
      379
    ],
    "sourceRowSha256": "038f9b54a35499316659134debba35bece6b7c635fb397f84b0e6be7229ddd48"
  },
  {
    "coordinate": "14.4",
    "quote": "勇者不必有仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "yong",
      "de",
      "yan"
    ],
    "reason": "德與言、仁與勇各建立單向而非雙向關係，防止以善言或勇敢直接推定完整德行。",
    "noteBoundary": "兩組不必的限制不能刪去；有言依底本為有名言，不是僅有說話能力。",
    "canonicalId": 380,
    "historicalIds": [
      380
    ],
    "sourceRowSha256": "61d78dc452722269181888b54eb611b75f3e785ed1a7eadb4eec3bcd2001094a"
  },
  {
    "coordinate": "14.5",
    "quote": "尚德哉若人",
    "primary": "de",
    "concepts": [
      "de",
      "junzi",
      "zheng",
      "xing-act"
    ],
    "reason": "南宮適以尚力者不得善終、禹稷躬稼有天下相問，孔子稱其尚德。",
    "noteBoundary": "羿奡事跡依注為古代傳說，不能混合三位羿的身分；孔子未當面作完整因果回答。",
    "canonicalId": 381,
    "historicalIds": [
      381
    ],
    "sourceRowSha256": "ccdfec3b8a4b5e108b1b1e9d91428418f601e8816cd82290d192bcc38d987fec"
  },
  {
    "coordinate": "14.6",
    "quote": "君子而不仁者有矣夫",
    "primary": "ren",
    "concepts": [
      "ren",
      "junzi",
      "xiaoren"
    ],
    "reason": "把君子與仁分說，是辨別身分稱謂和德目關係的重要難章。",
    "noteBoundary": "楊注明說君子小人涵義不清，僅疑指在位者與百姓；不能把此猜測定為所有同字章句通則。",
    "canonicalId": 382,
    "historicalIds": [
      382
    ],
    "sourceRowSha256": "d5ce7e88f7cb9b48f5d98868a913622cf6c84d4c10e03801e608040f1b6a1b21"
  },
  {
    "coordinate": "14.7",
    "quote": "忠焉，能勿诲乎",
    "primary": "hui",
    "concepts": [
      "hui",
      "ai",
      "zhong",
      "jiao"
    ],
    "reason": "愛與使之勞、忠與教誨相連，說關懷不只讓人安逸或順從。",
    "noteBoundary": "勞的注引有古代教化立場；不可將其擴成任意勞役或以傷害證明愛，原文未授此權。",
    "canonicalId": 383,
    "historicalIds": [
      383
    ],
    "sourceRowSha256": "8c22e0820e1d4e6a425548db7f0e5314d79f445712afea679b39caf2bc7241bf"
  },
  {
    "coordinate": "14.8",
    "quote": "为命",
    "primary": "yan",
    "concepts": [
      "yan",
      "wen",
      "zheng",
      "chen",
      "ming"
    ],
    "reason": "鄭國外交辭令經草創、討論、修飾、潤色，呈現分工及反覆斟酌。",
    "noteBoundary": "命在此依註為外交辭令，不是天命；討論為一人研究提意見，不能直接畫成現代多人會議。",
    "canonicalId": 384,
    "historicalIds": [
      384
    ],
    "sourceRowSha256": "d2038b0cedb0be060a2962aa27bf89b8d5f6e40a060af76673d9da3da73ba3ca"
  },
  {
    "coordinate": "14.9",
    "quote": "惠人也",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "hui-kindness",
      "yuan",
      "zheng"
    ],
    "reason": "依次評子產、子西、管仲，將慈惠、才幹與他人反應放在具體人物事例中。",
    "noteBoundary": "三人評語不可移配；子西身分依底本考辨、伯氏名亦存疑，不以同名自動合併人物資料。",
    "canonicalId": 385,
    "historicalIds": [
      385
    ],
    "sourceRowSha256": "46bce1baa6787690302032abb5e27727271621d2275e8b9c9af8edcbd75ff4be"
  },
  {
    "coordinate": "14.10",
    "quote": "贫而无怨难",
    "primary": "pin",
    "concepts": [
      "pin",
      "fu",
      "yuan",
      "de"
    ],
    "reason": "比較貧而無怨與富而無驕的難易，讓不同處境的修養要求分別可見。",
    "noteBoundary": "難易是倫理經驗評語，並非測量統計；不由貧或富本身推定某人德行高低。",
    "canonicalId": 386,
    "historicalIds": [
      386
    ],
    "sourceRowSha256": "edcc6919e60d019894a36ae17447264a409dab574e30a0ee2af85261de369c65"
  },
  {
    "coordinate": "14.11",
    "quote": "不可以为滕、薛大夫",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "chen",
      "shi-office",
      "qi-vessel"
    ],
    "reason": "孟公綽任家臣有餘卻不足任小國大夫，說明用人應看工作性質而非職位表面大小。",
    "noteBoundary": "老為家臣稱號，優為有餘力；原文沒有羅列他的全部技能，不補造具體不足。",
    "canonicalId": 387,
    "historicalIds": [
      387
    ],
    "sourceRowSha256": "46ad313f21eb683303fbb29f44053ef443c69e0c49603100d2384aef9f3fab90"
  },
  {
    "coordinate": "14.12",
    "quote": "见利思义，见危授命",
    "primary": "chengren",
    "concepts": [
      "chengren",
      "wisdom",
      "yong",
      "yi-arts",
      "li",
      "yue",
      "yi",
      "li-benefit",
      "xin"
    ],
    "reason": "成人先綜合多人才德再文以禮樂，續以見利思義、危難守義與久困不忘言說較具體要求。",
    "noteBoundary": "成人為全人非生理成年；命此性命、久要為久困。正文⑶與久要注重複⑵按已接受固定底本保留，舊待核門檻已取代。",
    "canonicalId": 388,
    "historicalIds": [
      388
    ],
    "sourceRowSha256": "2997e9f33a53e1c8b3663dbeecf9ae29fcf799a0ff535b82ca7867ed50fda1fa"
  },
  {
    "coordinate": "14.14",
    "quote": "虽曰不要君，吾不信也",
    "primary": "xin",
    "concepts": [
      "xin",
      "jun",
      "chushi",
      "yi"
    ],
    "reason": "臧武仲依防邑求後，孔子不信其非要君，呈現判斷政治行為實質與說辭的差別。",
    "noteBoundary": "要讀yāo為要挾，非不要的要；信在此相信某項說法，不能直接當守信德目。",
    "canonicalId": 390,
    "historicalIds": [
      390
    ],
    "sourceRowSha256": "a448b28e27e937703afec59d59a677f123c93c851d1b269fce4f42e1e10c76f7"
  },
  {
    "coordinate": "14.15",
    "quote": "晋文公谲而不正",
    "primary": "zheng-upright",
    "concepts": [
      "zheng-upright",
      "zhiren",
      "jun",
      "cheng"
    ],
    "reason": "比較晉文公與齊桓公的正與譎，將政治成就之外的手段納入評價。",
    "noteBoundary": "譎依註為欺詐權術；單章評語不等於兩人全部生平已被逐事考證，須與其他章的功績評價並讀。",
    "canonicalId": 391,
    "historicalIds": [
      391
    ],
    "sourceRowSha256": "0015148df1aa9172193ea29d8be9c862e878d1bdd9f0233244d13bbe5c71dff4"
  },
  {
    "coordinate": "14.16",
    "quote": "管仲之力也。如其仁，如其仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "zheng",
      "min"
    ],
    "reason": "子路疑管仲不死為未仁，孔子以九合諸侯不以兵車的作用答之，將仁的評價連到公共效果。",
    "noteBoundary": "九合為多次而非固定九次；如其仁依註取如猶乃，不能截掉質問情境只存功績口號。",
    "canonicalId": 392,
    "historicalIds": [
      392
    ],
    "sourceRowSha256": "a8e12ad439b905616953f8bab757fb8efe2492b010da8d8516199e411313c621"
  },
  {
    "coordinate": "14.17",
    "quote": "民到于今受其赐",
    "primary": "ren",
    "concepts": [
      "ren",
      "min",
      "zheng",
      "xin",
      "si-death"
    ],
    "reason": "子貢再問管仲，孔子以一匡天下與百姓受惠回應，將廣泛利益與狹小信諒區別。",
    "noteBoundary": "被髮左衽的古代文化立場及譯文用語應保留並辨時代，不能转為對現今民族的優劣判斷；不把自經作學習要求。",
    "canonicalId": 393,
    "historicalIds": [
      393
    ],
    "sourceRowSha256": "8c5025fa4d28a2952e81fd8033249ce93fba386ff8ba254eaed3c03c5dc12cf9"
  },
  {
    "coordinate": "14.18",
    "quote": "可以为‘文’矣",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "wen",
      "chen",
      "xian"
    ],
    "reason": "文子家臣同升為國臣而受文的稱許，連到推薦人才與名實評價。",
    "noteBoundary": "文在此是諡號，不是文學寫作；臣、大夫的句讀有異說，僎與文子不可併成一人。",
    "canonicalId": 394,
    "historicalIds": [
      394
    ],
    "sourceRowSha256": "91962a37e135d8540caa03da29c8a050f15661334619f2c72527d4b973a0d393"
  },
  {
    "coordinate": "14.19",
    "quote": "仲叔圉治宾客",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "chen",
      "zhiren",
      "ji"
    ],
    "reason": "衛靈公無道卻有善任各務之臣，說國家存續還須察具體用人與分工。",
    "noteBoundary": "未亡不等於君主行為全被認可；三人職分逐一對應，不能因一項成功推成全面有道。",
    "canonicalId": 395,
    "historicalIds": [
      395
    ],
    "sourceRowSha256": "c6ea0ed0bae6622a0277eb57662b5700f3da4676579f232adea8ed450219837c"
  },
  {
    "coordinate": "14.20",
    "quote": "其言之不怍，则为之也难",
    "primary": "yan",
    "concepts": [
      "yan",
      "xing-act",
      "chi"
    ],
    "reason": "說話大而不慚往往難以實行，將言語分寸與行動負擔相連。",
    "noteBoundary": "原文未指名其人，不補造責備對象；難為不等於已證明每個說大話者永遠無法改變。",
    "canonicalId": 396,
    "historicalIds": [
      396
    ],
    "sourceRowSha256": "bbb3fe797c8c4a0a6a181a02220d050fe5b2baa17a3e770951beafaa32607669"
  },
  {
    "coordinate": "14.21",
    "quote": "以吾从大夫之后，不敢不告也",
    "primary": "chen",
    "concepts": [
      "chen",
      "jun",
      "yi",
      "zheng",
      "chushi"
    ],
    "reason": "陳恒弒君後孔子循君與三子告討，雖未獲允仍說盡告之責，呈現角色與政治主張。",
    "noteBoundary": "未獲討伐不寫成戰爭已實施；譯文三子名單與3.2不一致按已接受固定底本保留，舊待核門檻已取代。原文退朝再告次序與注中可議的勝算推估保留。",
    "canonicalId": 397,
    "historicalIds": [
      397
    ],
    "sourceRowSha256": "52af0a0d0afd7c5e440e993ca1aec8dfaa2b8c9fec545ee451d185f038093c6b"
  },
  {
    "coordinate": "14.22",
    "quote": "勿欺也，而犯之",
    "primary": "zhong",
    "concepts": [
      "zhong",
      "chen",
      "jun",
      "cheng",
      "yan"
    ],
    "reason": "事君不可欺而可當面觸犯，將忠誠與直言反對相連。",
    "noteBoundary": "犯在此為進言觸犯，不是授權任意犯罪或攻擊；反對意見本身不被等同不忠。",
    "canonicalId": 398,
    "historicalIds": [
      398
    ],
    "sourceRowSha256": "cddecaef654c58de61f412f286c9e584e8731952056c7be804d2c17e3159735a"
  },
  {
    "coordinate": "14.23",
    "quote": "君子上达，小人下达",
    "primary": "da-attainment",
    "concepts": [
      "da-attainment",
      "junzi",
      "xiaoren",
      "yili"
    ],
    "reason": "以上達下達對照君子小人，依底本將所通達的內容分為仁義與財利。",
    "noteBoundary": "上下達有不同古今解釋，底本取皇侃說；不可當作空間位置高低或社會身分的自動判定。",
    "canonicalId": 399,
    "historicalIds": [
      399
    ],
    "sourceRowSha256": "b27d9ce12a9b0ceb0746570a0fc3d3447f6ff2788caa23b767ac6b0c1bd1d551"
  },
  {
    "coordinate": "14.24",
    "quote": "古之学者为己，今之学者为人",
    "primary": "xue",
    "concepts": [
      "xue",
      "xiuyi",
      "cheng",
      "ming-name"
    ],
    "reason": "比較學習旨在修己或裝飾給人看，揭示求學目的與外在聲名的區別。",
    "noteBoundary": "今相對孔子時代而非今日；為己不是自私逐利，為人也不是此處褒義的服務他人。",
    "canonicalId": 400,
    "historicalIds": [
      400
    ],
    "sourceRowSha256": "d72a4aec1d61dc1cb546f04ec216e875eb0c0d150aa2834211ab29f419b3733c"
  },
  {
    "coordinate": "14.25",
    "quote": "欲寡其过而未能也",
    "primary": "gai",
    "concepts": [
      "gai",
      "guo",
      "sheng",
      "yan"
    ],
    "reason": "使者以欲寡過而未能述蘧伯玉，孔子稱使，兼見改過追求與傳話得體。",
    "noteBoundary": "未能不可被改寫為已無過；夫子在此指蘧伯玉，注引各年齡說不能合成同一次事件。",
    "canonicalId": 401,
    "historicalIds": [
      401
    ],
    "sourceRowSha256": "b064cf49c2abd13ccf1ebee51109e505067c4a33ef3c523e5022a956d50a0d73"
  },
  {
    "coordinate": "14.26",
    "quote": "君子思不出其位",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "shi-office",
      "si"
    ],
    "reason": "孔子不在位不謀政與曾子思不出位並列，保留兩位說話人對職責範圍的相關表述。",
    "noteBoundary": "譯文只給曾子句，孔子句需參8.14而不可冒充本欄已完整；不能將兩章歷史作答合併覆寫。",
    "canonicalId": 402,
    "historicalIds": [
      402
    ],
    "sourceRowSha256": "4724256ff9909cd5a733bc7cb90d61ef15563613af3a5041d64ce93612b18de3"
  },
  {
    "coordinate": "14.27",
    "quote": "君子耻其言而过其行",
    "primary": "chi",
    "concepts": [
      "chi",
      "yan",
      "xing-act",
      "junzi"
    ],
    "reason": "把言多行少當作君子之恥，促使言語承諾與實踐相稱。",
    "noteBoundary": "而依本註同之，不能按轉折或順接讀成君子故意說過其行；異本作之保留於註。",
    "canonicalId": 403,
    "historicalIds": [
      403
    ],
    "sourceRowSha256": "8adef663a4609553c91c23492f7440eab9e2a55dfe533d75cc89a259455c41f2"
  },
  {
    "coordinate": "14.28",
    "quote": "君子道者三，我无能焉",
    "primary": "ren",
    "concepts": [
      "ren",
      "wisdom",
      "yong",
      "junzi"
    ],
    "reason": "孔子自謙三道未能，子貢卻說正是夫子自道，保留自述與弟子評議的兩層。",
    "noteBoundary": "與9.29同德目而說話情境不同；不將孔子自謙或子貢稱許任何一面截作唯一客觀定論。",
    "canonicalId": 404,
    "historicalIds": [
      404
    ],
    "sourceRowSha256": "264987234933b78283195080a747b99a9c8788ec4ad48eea444281db5b9ecac7"
  },
  {
    "coordinate": "14.29",
    "quote": "夫我则不暇",
    "primary": "sheng",
    "concepts": [
      "sheng",
      "zhiren",
      "yan"
    ],
    "reason": "孔子回應子貢方人，將議論別人拉回自身是否足夠及時間所用。",
    "noteBoundary": "方依註取譏評，也有品評用例；原文不必然禁止有根據的辨人，勿與其他知人章句簡單對消。",
    "canonicalId": 405,
    "historicalIds": [
      405
    ],
    "sourceRowSha256": "a1769a314df4f3b92a30869f0d3c2b7fbdb7e084bb64effabdf4a22a4a82f20d"
  },
  {
    "coordinate": "14.30",
    "quote": "患其不能也",
    "primary": "xiuyi",
    "concepts": [
      "xiuyi",
      "zhiren",
      "ming-name"
    ],
    "reason": "把關切由他人是否知己轉回自身能力，突出可以著力的自我修養。",
    "noteBoundary": "原文未定義能力為考試排名；不患人不知不是要求放棄一切必要的溝通與公正評價。",
    "canonicalId": 406,
    "historicalIds": [
      406
    ],
    "sourceRowSha256": "6a97b430bd06942bf609d5df8c4aa3d8315c2c7751ab989f837664429d3c3aa6"
  },
  {
    "coordinate": "14.31",
    "quote": "不逆诈，不亿不信",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "xian",
      "xin",
      "wisdom"
    ],
    "reason": "不先入懷疑而能及早覺察欺詐，區別無據猜測和有見識的警覺。",
    "noteBoundary": "億為猜測，不是數字；不信在此指不誠實的可能，不能反讀成對所有人毫無保留地信任。",
    "canonicalId": 407,
    "historicalIds": [
      407
    ],
    "sourceRowSha256": "fe5e368b0fc7ac2976a3821ca460caa1616ba08889229fbfe5d0901710dc2c2e"
  },
  {
    "coordinate": "14.32",
    "quote": "非敢为佞也，疾固也",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "ning-eloquence",
      "yan",
      "dao"
    ],
    "reason": "微生畝疑孔子忙碌為佞，孔子以疾固回答，呈現行道奔走與旁觀批評的張力。",
    "noteBoundary": "栖栖按底本忙碌；此處出處/道是行道自處的比較入口，不是本文已記出仕退隱或具體周遊行程。疾固依本章答語保留，固陋與守原則須分。",
    "canonicalId": 408,
    "historicalIds": [
      408
    ],
    "sourceRowSha256": "191a6d4fd5fc1d97e70148acbafdddaad1db0f5145c5166374915c6be3808f19"
  },
  {
    "coordinate": "14.33",
    "quote": "骥不称其力，称其德也",
    "primary": "de",
    "concepts": [
      "de",
      "zhi-quality",
      "zhiren"
    ],
    "reason": "駿馬受稱在德而非力，提供評價重點與外在力量的比較材料。",
    "noteBoundary": "德在此依底本指馬的品質，不能直接把人的德行全部套入動物；知人僅為比較主題而非原句對象。",
    "canonicalId": 409,
    "historicalIds": [
      409
    ],
    "sourceRowSha256": "30ed1bab35e11da8e8bb5198350350e3595633d32e805268f062465679ce450e"
  },
  {
    "coordinate": "14.34",
    "quote": "以直报怨，以德报德",
    "primary": "zhi-upright",
    "concepts": [
      "zhi-upright",
      "yuan",
      "de",
      "yi",
      "hui-kindness"
    ],
    "reason": "對以德報怨的提議，以直回怨、以德回德作區別，探討回應他人行為的尺度。",
    "noteBoundary": "德在這兩句為恩惠，非所有道德的抽象總稱；直為公平正直，不是以怨報怨。",
    "canonicalId": 410,
    "historicalIds": [
      410
    ],
    "sourceRowSha256": "69670ba501aec1680ad97fad7041d713b46603d663fe80ec4a509755880a4a39"
  },
  {
    "coordinate": "14.35",
    "quote": "不怨天，不尤人，下学而上达",
    "primary": "xue",
    "concepts": [
      "xue",
      "da-attainment",
      "tian",
      "yuan",
      "zhi"
    ],
    "reason": "由莫我知的感嘆轉到不怨不尤、下學上達，保留求知行道與知己問題。",
    "noteBoundary": "上下達具體意義諸說未定，楊注所取僅供參考；不能把人事天命的完整體系當成本章已明說。",
    "canonicalId": 411,
    "historicalIds": [
      411
    ],
    "sourceRowSha256": "cd4cbe37a4e29a20f29c1a80e9ca2a60cfcdbdffdfe421ac56337bd2250767d9"
  },
  {
    "coordinate": "14.36",
    "quote": "道之将行也与，命也",
    "primary": "ming",
    "concepts": [
      "ming",
      "dao",
      "chushi"
    ],
    "reason": "面對公伯寮讒言與景伯殺人建議，孔子將道之行廢歸於命，呈現回應政治挫折的態度。",
    "noteBoundary": "肆市朝是景伯提出的做法，孔子未授意執行；不能把命的語言等同已確認外在事件不可改變。",
    "canonicalId": 412,
    "historicalIds": [
      412
    ],
    "sourceRowSha256": "651996f19d987fe88625350c4511e02794b894ffcc0f451b12001877bb35dcfb"
  },
  {
    "coordinate": "14.37",
    "quote": "贤者辟世",
    "primary": "yin",
    "concepts": [
      "yin",
      "chushi",
      "xian",
      "yan"
    ],
    "reason": "辟世、辟地、辟色、辟言分列退避層次，為出處路線提供不同處境的比較。",
    "noteBoundary": "辟通避；作者七人本章未逐一列名，不由後文人物湊成已確證的名單。",
    "canonicalId": 413,
    "historicalIds": [
      413
    ],
    "sourceRowSha256": "4983a5fc0b0542a9da5acc655c3c772bcda4f4e7884f581374817c1d3cb4e1df"
  },
  {
    "coordinate": "14.39",
    "quote": "莫己知也，斯己而已矣",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "yin",
      "yue",
      "shi-poetry"
    ],
    "reason": "荷蕢者由磬聲勸孔子不見知則止，並引詩作深淺譬喻，與孔子行道形成不同立場。",
    "noteBoundary": "勸止是荷蕢者之言，不是孔子直接主張；末之難依底本為難以說服，不把片段消成一致結論。",
    "canonicalId": 415,
    "historicalIds": [
      415
    ],
    "sourceRowSha256": "636ca0a08c9e3a8e149138fa55e53ce326bc3505df151ff22cca555109eed55c"
  },
  {
    "coordinate": "14.40",
    "quote": "百官总己以听于冢宰三年",
    "primary": "sang",
    "concepts": [
      "sang",
      "li",
      "jun",
      "chen",
      "zheng"
    ],
    "reason": "以高宗諒陰之問引出君喪期間的政務安排，連結喪禮與臣屬職責。",
    "noteBoundary": "三年不言依答語不是從不發任何聲音，而是不問政治；諒陰為居喪之屋，保留古制語境。",
    "canonicalId": 416,
    "historicalIds": [
      416
    ],
    "sourceRowSha256": "5f7456811841b709389d8547e83fb49126161c012c8d3715ef2a2b33c70d6dad"
  },
  {
    "coordinate": "14.41",
    "quote": "上好礼，则民易使也",
    "primary": "li",
    "concepts": [
      "li",
      "zheng",
      "min",
      "de"
    ],
    "reason": "在上者好禮與百姓易於配合相連，補充治理須自身遵禮的方向。",
    "noteBoundary": "易使不代表人民沒有自主判斷；原句未列具体制度，不能据此證明任何統治命令都正當。",
    "canonicalId": 417,
    "historicalIds": [
      417
    ],
    "sourceRowSha256": "bf22ef821b2872a805a07930008fa0331652e1a04c400e5594a7e6884d846599"
  },
  {
    "coordinate": "14.42",
    "quote": "修己以安百姓",
    "primary": "xiuyi",
    "concepts": [
      "xiuyi",
      "jing",
      "anren",
      "anbaixing",
      "junzi",
      "min"
    ],
    "reason": "子路追問使修己由敬擴至安人、安百姓，且連尧舜亦未盡，呈現由己及人的層次與難度。",
    "noteBoundary": "人依楊注狹指上層人物而與百姓區分，不能把兩階直接併成同義；病為以為難，非疾病。",
    "canonicalId": 418,
    "historicalIds": [
      418
    ],
    "sourceRowSha256": "c56e822c05925dbee516db0c313ec733f6ed5899cef92dc84b13295b53436c31"
  },
  {
    "coordinate": "14.43",
    "quote": "幼而不孙弟",
    "primary": "li",
    "concepts": [
      "li",
      "ti",
      "pengyou",
      "xing-act"
    ],
    "reason": "原壤箕踞待孔子引出對其少長行止的斥責，保存禮態與人物衝突。",
    "noteBoundary": "孫弟通遜悌，正文⑶與註頭⑵不一致按已接受固定底本保留，舊待核門檻已取代。敲脛為古代敘事，不作今日體罰或泛貶老年人的規則。",
    "canonicalId": 419,
    "historicalIds": [
      419
    ],
    "sourceRowSha256": "7c3227bf258640419fa9f8e7f1d71dfe775ba552c2ad81884062237346a1f0fe"
  },
  {
    "coordinate": "14.44",
    "quote": "非求益者也，欲速成者也",
    "primary": "xue",
    "concepts": [
      "xue",
      "li",
      "yu"
    ],
    "reason": "由童子居位、與長輩並行判其急於成人，對比求益和求速成的學習態度。",
    "noteBoundary": "判斷依當日禮節與此具體觀察，不直接套成現代兒童永久人格標籤；將命是傳話，不是天命。",
    "canonicalId": 420,
    "historicalIds": [
      420
    ],
    "sourceRowSha256": "763de280a722adbfcf85093d0cd500f54de522eb59f94f8e20616ea2ac6a2606"
  },
  {
    "coordinate": "15.1",
    "quote": "俎豆之事，则尝闻之矣",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "li",
      "wen-hear",
      "shi-office"
    ],
    "reason": "衛靈公問陣而孔子答禮並次日離去，保留對仕任處境的選擇。",
    "noteBoundary": "陳通陣，非陳國的陳；俎豆借指禮事，原文沒有交代所有離開原因，不由一句推成孔子從未涉及軍事議論。",
    "canonicalId": 421,
    "historicalIds": [
      421
    ],
    "sourceRowSha256": "7b35a35668315db897e28aa2f761de8747b3dea201986fa040c69fd384f72215"
  },
  {
    "coordinate": "15.2",
    "quote": "君子固穷",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "xiaoren",
      "pin",
      "heng",
      "chushi"
    ],
    "reason": "陳地絕糧中子路問窮，孔子以困厄中能否持守區分君子小人。",
    "noteBoundary": "固窮為窮中能固守，不是刻意保持貧窮；困境與行為反應分開，不由缺糧就判失德。",
    "canonicalId": 422,
    "historicalIds": [
      422
    ],
    "sourceRowSha256": "0041471a59697adf42057c1227cf144e71b15b9bf3c1cc9152b2c005a6e2c7d5"
  },
  {
    "coordinate": "15.3",
    "quote": "予一以贯之",
    "primary": "yiguan",
    "concepts": [
      "yiguan",
      "xue",
      "zhi",
      "shu"
    ],
    "reason": "孔子不只以多學多識自述，而以一貫說自己的學行，與4.15相參。",
    "noteBoundary": "忠恕為楊注參4.15的解釋，本章未直接寫忠恕；不能把一貫簡化成只記一條口號便不用廣學。",
    "canonicalId": 423,
    "historicalIds": [
      423
    ],
    "sourceRowSha256": "c150c55290c7fd564b23a46aff612b711f20794cbbba043e1160295361e0b6af"
  },
  {
    "coordinate": "15.4",
    "quote": "知德者鲜矣",
    "primary": "de",
    "concepts": [
      "de",
      "zhi"
    ],
    "reason": "向子路感嘆知德者少，將對德的理解作為獨立學習問題。",
    "noteBoundary": "知德不是原文明定的考試能力等級；鮮為少，不作從未有人懂得德。",
    "canonicalId": 424,
    "historicalIds": [
      424
    ],
    "sourceRowSha256": "f5ff84f1911b078b3af608387be7e54ae7b1d4586f04c7ceb3bf3695437847e4"
  },
  {
    "coordinate": "15.5",
    "quote": "无为而治者其舜也与",
    "primary": "wuwei",
    "concepts": [
      "wuwei",
      "zheng",
      "gong",
      "zhiren"
    ],
    "reason": "以舜恭己南面說無為而治，楊注以任人得當說明其可能條件。",
    "noteBoundary": "不能把無為讀成完全不負責任；與其他思想中的無為可比較，但不預先宣稱各家概念完全同義。",
    "canonicalId": 425,
    "historicalIds": [
      425
    ],
    "sourceRowSha256": "118a7816992b1dd837fb5f9bd9013663d52470042ba5c3857cd3a207a5e126c2"
  },
  {
    "coordinate": "15.6",
    "quote": "言忠信，行笃敬",
    "primary": "zhongxin",
    "concepts": [
      "zhongxin",
      "zhong",
      "xin",
      "jing",
      "yan",
      "xing-act"
    ],
    "reason": "在不同地域能行與否繫於言忠信行篤敬，子張書紳提醒持續實踐。",
    "noteBoundary": "行兼行為與行得通兩層，不作同一語法；眼前車衡如見其言是譬喻式記念，不是實際刻字記錄。",
    "canonicalId": 426,
    "historicalIds": [
      426
    ],
    "sourceRowSha256": "5f460d143aed4c941578e0c9cdc473ca05cf22b01385001e652dc718f9bb4cc5"
  },
  {
    "coordinate": "15.7",
    "quote": "邦无道，则可卷而怀之",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "zhi-upright",
      "shi-office",
      "yin",
      "junzi",
      "bangyoudao",
      "bangwudao"
    ],
    "reason": "史魚守直與蘧伯玉視世道仕藏均受稱許，保留不同人物的行道方式。",
    "noteBoundary": "不可把兩人的出處選擇強制合成單一路徑；卷懷為收藏本領，尸諫故事只在注引。",
    "canonicalId": 427,
    "historicalIds": [
      427
    ],
    "sourceRowSha256": "20c65c875cba04ce791d137fe943ba7ea561722846ba971067f26eb17bf004e4"
  },
  {
    "coordinate": "15.8",
    "quote": "知者不失人，亦不失言",
    "primary": "yan",
    "concepts": [
      "yan",
      "zhiren",
      "wisdom"
    ],
    "reason": "能否與某人談話要作判斷，錯過人才與浪費言語分別構成失人、失言；重點在對象辨識與言語分寸。",
    "noteBoundary": "失人依底本為錯過人才，非失去人口；原文未定永久不可言者名單，不能由一次交談把人固定分類。",
    "canonicalId": 428,
    "historicalIds": [
      428
    ],
    "sourceRowSha256": "07456dea1ad5f9ff8ee61802dd3475b4fc86bd6fadcf8b9648d95904e3e62a8e"
  },
  {
    "coordinate": "15.10",
    "quote": "工欲善其事，必先利其器",
    "primary": "ren",
    "concepts": [
      "ren",
      "qi-vessel",
      "you",
      "xian",
      "shi"
    ],
    "reason": "以工與器之譬說為仁需要親賢友仁的環境，保留譬喻到人際實踐的承接。",
    "noteBoundary": "士此處與大夫並言可能是職位身分；利是使器銳利，非逐財利，不能只留工具名句而刪去問仁語境。",
    "canonicalId": 430,
    "historicalIds": [
      430
    ],
    "sourceRowSha256": "c2a40a447ef01f0c9b10d77a0a0720d1d9580c039ea194d3ddc58b3bb7c5b5d1"
  },
  {
    "coordinate": "15.11",
    "quote": "放郑声，远佞人",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "li",
      "yue",
      "jian-frugal",
      "ning-eloquence"
    ],
    "reason": "治邦從三代各取所宜，再辨樂聲與佞人，呈制度取捨及文化政治的關係。",
    "noteBoundary": "鄭聲為樂曲而非鄭詩文辭，舞通武依註；周冕的註號重複待核，不能把捨鄭聲改作刪詩證據。",
    "canonicalId": 431,
    "historicalIds": [
      431
    ],
    "sourceRowSha256": "83ad6c78509b2fe30cd66a5ae6f4fa5d2bd09e7bd0cd9725930329bd98bbab58"
  },
  {
    "coordinate": "15.12",
    "quote": "人无远虑，必有近忧",
    "primary": "si",
    "concepts": [
      "si",
      "shen",
      "shi-time"
    ],
    "reason": "以遠慮與近憂相連，提醒眼前處境需有長遠思考。",
    "noteBoundary": "這是勸思的概括，未列具體預測方法；不能用憂患是否出現反向保證每次遠慮都準確。",
    "canonicalId": 432,
    "historicalIds": [
      432
    ],
    "sourceRowSha256": "1c37a8f578be54adcbffed1fff2dcb1477ab673d1ffe0d6799f3247a21fe0d59"
  },
  {
    "coordinate": "15.13",
    "quote": "吾未见好德如好色者也",
    "primary": "de",
    "concepts": [
      "de",
      "yu"
    ],
    "reason": "好德與好色的比較再次出現，結合感嘆與注中衛靈公車行背景閱讀。",
    "noteBoundary": "事件背景由楊注引史記，正文未完整敘事；與9.18相似但保持兩章原有語氣、ID及歷史紀錄。",
    "canonicalId": 433,
    "historicalIds": [
      433
    ],
    "sourceRowSha256": "9a1264edd9ef681d2e1e573b1ad505088a78469ec93823bfba4c29cbdf4340d4"
  },
  {
    "coordinate": "15.14",
    "quote": "知柳下惠之贤而不与立也",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "xian",
      "shi-office",
      "zheng"
    ],
    "reason": "臧文仲知賢卻不使立位而被稱竊位，將識人與實際舉用的責任連起。",
    "noteBoundary": "正文寫藏、注頭寫臧，人物識別需精確別名而非全局藏臧替換；立通位，不是站起。",
    "canonicalId": 434,
    "historicalIds": [
      434
    ],
    "sourceRowSha256": "164c7cf408aafee531f545f0ece9ce890bf516cbc1eee23ee6dd092c5e5ab81e"
  },
  {
    "coordinate": "15.15",
    "quote": "躬自厚而薄责于人",
    "primary": "sheng",
    "concepts": [
      "sheng",
      "yuan",
      "gai",
      "forbearance"
    ],
    "reason": "重責己而薄責人以遠怨，將人際怨恨與自我要求聯繫。",
    "noteBoundary": "厚後省責依楊注補讀，不是厚待自己；薄責不等於對任何傷害都取消事實查核與必要責任。",
    "canonicalId": 435,
    "historicalIds": [
      435
    ],
    "sourceRowSha256": "c93cf6de1ad27a83cd24a6056393965f800b5334a19ce57225138a73b17baf04"
  },
  {
    "coordinate": "15.16",
    "quote": "不曰‘如之何，如之何’者",
    "primary": "si",
    "concepts": [
      "si",
      "wen-ask",
      "jiaoxue"
    ],
    "reason": "自己不思如何處理，老師也難相助，突出求學者須投入思考。",
    "noteBoundary": "如之何依註為動腦筋的表現，不是要求機械重複話語；教師的感嘆不可轉成永遠放棄學生。",
    "canonicalId": 436,
    "historicalIds": [
      436
    ],
    "sourceRowSha256": "2e1d4049c35555300e1137333863ed5e6e029dd864010e41837ecc1f5c3d7a9e"
  },
  {
    "coordinate": "15.17",
    "quote": "言不及义，好行小慧",
    "primary": "yi",
    "concepts": [
      "yi",
      "yan",
      "wisdom",
      "jiaoxue"
    ],
    "reason": "群居終日卻只耍小慧、言不及義，區分相聚交談與有意義的學習。",
    "noteBoundary": "小慧是賣弄小聰明，不等同全部智慧；羣居本身未被否定，問題在所言所行。",
    "canonicalId": 437,
    "historicalIds": [
      437
    ],
    "sourceRowSha256": "46f1425cdd19f37c10b2df079c6cb4d43de37c81acf5cd78f642ecd9cfc9d5d9"
  },
  {
    "coordinate": "15.18",
    "quote": "君子义以为质",
    "primary": "yi",
    "concepts": [
      "yi",
      "li",
      "xin",
      "junzi",
      "zhi-quality",
      "yan",
      "xing-act"
    ],
    "reason": "義作內在原則，禮以行、遜以出、信以成，將事業的各環節聯成君子行動。",
    "noteBoundary": "孫通遜、出依註為出言；質指根本而非所有外在形式皆可丟棄，需保留四項配合。",
    "canonicalId": 438,
    "historicalIds": [
      438
    ],
    "sourceRowSha256": "c3ea992610f30519d1dddda4cc27e7111f664b201938082ab91319f562c0e458"
  },
  {
    "coordinate": "15.19",
    "quote": "君子病无能焉",
    "primary": "xiuyi",
    "concepts": [
      "xiuyi",
      "junzi",
      "ming-name"
    ],
    "reason": "君子關切能力不足而非他人不知己，與14.30自我著力相互參照。",
    "noteBoundary": "病在此為以為患或惭，不作疾病；不能將外部不公評價一概歸為個人沒有能力。",
    "canonicalId": 439,
    "historicalIds": [
      439
    ],
    "sourceRowSha256": "8f8eea1b7abada634d7e2dd2dda3299021b112fd0b492fcea4f7318193281e41"
  },
  {
    "coordinate": "15.20",
    "quote": "疾没世而名不称焉",
    "primary": "ming-name",
    "concepts": [
      "ming-name",
      "junzi",
      "zhi-aspiration"
    ],
    "reason": "對身後無名的憂慮顯出成就被稱述的面向，可與不患人不知的章句比較張力。",
    "noteBoundary": "名在此為聲名而非名分；不強行把相鄰章合成毫無差異，也不據此取消為己之學。",
    "canonicalId": 440,
    "historicalIds": [
      440
    ],
    "sourceRowSha256": "15e5fc4b206d668f331fb7fbd8129e10fe76e36da13f956530bcbcb18b040ac7"
  },
  {
    "coordinate": "15.21",
    "quote": "君子求诸己，小人求诸人",
    "primary": "sheng",
    "concepts": [
      "sheng",
      "junzi",
      "xiaoren",
      "xiuyi"
    ],
    "reason": "求己與求人的對照將自我要求置於前，提供反省行動責任的入口。",
    "noteBoundary": "諸是之於的合用，要求自己不等於拒絕一切合作；單句不能取代具体責任辨別。",
    "canonicalId": 441,
    "historicalIds": [
      441
    ],
    "sourceRowSha256": "9008d9cdf96c4b2008d41b9292a1a88028551781f14cc14cb7395c3855181387"
  },
  {
    "coordinate": "15.22",
    "quote": "羣而不党",
    "primary": "he",
    "concepts": [
      "he",
      "junzi",
      "hebutong"
    ],
    "reason": "莊矜不爭、合群不黨，將自持與群體交往並看。",
    "noteBoundary": "羣群為已核字形對應；周而不比、和而不同是楊注可能的互參，不宣稱三句完全等義。",
    "canonicalId": 442,
    "historicalIds": [
      442
    ],
    "sourceRowSha256": "e59e85fd12ce876d8f799b0c9c8bf7b23075c23a4b9644ee016dcdaa7629e05f"
  },
  {
    "coordinate": "15.23",
    "quote": "不以言举人，不以人废言",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "yan",
      "xian"
    ],
    "reason": "識人不可只憑一言，而評言亦不能只憑說話人的好壞，區分人物與論說證據。",
    "noteBoundary": "兩半必須同讀；不是所有話都同樣可信，仍須分辨言論本身的理由與事實。",
    "canonicalId": 443,
    "historicalIds": [
      443
    ],
    "sourceRowSha256": "5664c8e1d2d0f2afa7f3248ae1403558f123417d4aa4c71e07793496afbdc8dc"
  },
  {
    "coordinate": "15.25",
    "quote": "如有所誉者，其有所试矣",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "cheng",
      "zhi-upright",
      "guan"
    ],
    "reason": "稱譽有試驗觀察作依據，將對人的評價與實際經驗相連。",
    "noteBoundary": "試為考驗，不等於現代標準化測驗；三代之民的概括保留原文，不當作逐人歷史查核已完成。",
    "canonicalId": 445,
    "historicalIds": [
      445
    ],
    "sourceRowSha256": "364c2d10850b52eeb41bdaa98c7e93ea711e49a48cd5b731657c5fdbba263620"
  },
  {
    "coordinate": "15.26",
    "quote": "史之阙文也",
    "primary": "zhi",
    "concepts": [
      "zhi",
      "cheng",
      "wen"
    ],
    "reason": "史書留闕與有馬借人兩事並記，可由前項讀對未知存疑的態度。",
    "noteBoundary": "兩事關係楊注明言難解，衍文說亦無定論；保留原文不靠想像縫合因果，馬句不直接變成同一認識論命題。",
    "canonicalId": 446,
    "historicalIds": [
      446
    ],
    "sourceRowSha256": "63cceb742c9ee9fd523728b7bea7c32e5d69f10bd00836dd6273547475a33136"
  },
  {
    "coordinate": "15.27",
    "quote": "巧言乱德",
    "primary": "jin",
    "concepts": [
      "jin",
      "yan",
      "de",
      "shen",
      "yu"
    ],
    "reason": "巧言足以亂德，小不忍足以亂大謀，呈言語與眼前欲念對大局的影響。",
    "noteBoundary": "小不忍在楊注不限忿怒，還含小恩小利等取捨；不是要求對所有不當行為保持沉默。",
    "canonicalId": 447,
    "historicalIds": [
      447
    ],
    "sourceRowSha256": "d11be945caa5b490164674e10bd998ebed2dbbcd28aa60c8823f01de2380594a"
  },
  {
    "coordinate": "15.28",
    "quote": "众恶之，必察焉",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "guan",
      "wisdom"
    ],
    "reason": "群眾好惡都須再察，將人氣或反感與經查證的識人區分。",
    "noteBoundary": "與13.24互參，原文不要求先選定大家對或錯；察的結果不可由群體聲量預設。",
    "canonicalId": 448,
    "historicalIds": [
      448
    ],
    "sourceRowSha256": "1153265f800b5112f3e07197e77e0c1cac23ed5b902f46e7332e3eabd5c746fe"
  },
  {
    "coordinate": "15.29",
    "quote": "人能弘道，非道弘人",
    "primary": "dao",
    "concepts": [
      "dao",
      "xing-act"
    ],
    "reason": "將人與道的主動關係作正反表述，提供行道與人的作用的核心問題。",
    "noteBoundary": "楊注明言真意難體會、只就字面譯，不把後世完整理論裝成確定原意；可討論而不得抹去不確定。",
    "canonicalId": 449,
    "historicalIds": [
      449
    ],
    "sourceRowSha256": "8f64f9022b4cc5488a776a2609229a88ef50b762527c50b29bec9fc2a8538717"
  },
  {
    "coordinate": "15.30",
    "quote": "过而不改，是谓过矣",
    "primary": "gai",
    "concepts": [
      "gai",
      "guo",
      "sheng"
    ],
    "reason": "重點落在有過之後是否改，將錯誤視為須有後續行動的問題。",
    "noteBoundary": "改過不等於抹掉已發生的原始紀錄；不因新規則或後來改正而覆寫歷史作答與評閱證據。",
    "canonicalId": 450,
    "historicalIds": [
      450
    ],
    "sourceRowSha256": "e699006b189a24ef529313378c2eab7619203e1a57befe8a1d91b8fdf9903d2d"
  },
  {
    "coordinate": "15.31",
    "quote": "以思，无益，不如学也",
    "primary": "xue",
    "concepts": [
      "xue",
      "si",
      "xueersi"
    ],
    "reason": "整日整夜空思不如學習，以自述提醒思考需要學習材料支持。",
    "noteBoundary": "不是全面否定思；與2.15學思相配合比較，終日不食不寢不作學生的學習方式示範。",
    "canonicalId": 451,
    "historicalIds": [
      451
    ],
    "sourceRowSha256": "f788f6224e2fd1f1586ebde85554e72898eebd89a3e20d6eb94dc4d7dfc1959a"
  },
  {
    "coordinate": "15.32",
    "quote": "君子忧道不忧贫",
    "primary": "dao",
    "concepts": [
      "dao",
      "xue",
      "pin",
      "shi-office",
      "junzi"
    ],
    "reason": "以謀道而非謀食說君子取向，並把耕與學可能帶來的結果並列。",
    "noteBoundary": "祿在其中不是學習必定得官的承諾；依註可與13.4對讀，不將農民或貧困者一概貶低。",
    "canonicalId": 452,
    "historicalIds": [
      452
    ],
    "sourceRowSha256": "9a86af825353cf8b3cd6be586352af7eeb7f894c0d149efb982385853634e674"
  },
  {
    "coordinate": "15.33",
    "quote": "动之不以礼，未善也",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "wisdom",
      "ren",
      "li",
      "min"
    ],
    "reason": "知得、仁守、莊臨及以禮動民逐項增加條件，顯示治理不可只憑一種長處。",
    "noteBoundary": "之的對象原文未說，祿位或國家是楊注推測；不得把推測當作確定職位或事件。",
    "canonicalId": 453,
    "historicalIds": [
      453
    ],
    "sourceRowSha256": "64b45b480725121a1ee3d9635a0b97a982bbd3840980e729db43ead92ec677c0"
  },
  {
    "coordinate": "15.34",
    "quote": "君子不可小知而可大受也",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "junzi",
      "xiaoren",
      "qi-vessel"
    ],
    "reason": "小事考驗與大任承受相區別，提醒評價人才須看任務層次。",
    "noteBoundary": "小知、大受依本底本理解；原章未提供具體測驗和分數，不能直接建立固定學生分級。",
    "canonicalId": 454,
    "historicalIds": [
      454
    ],
    "sourceRowSha256": "1511a6302cff6ab83f1d60296b44cf4b40d96f62b3e24f0e87c2209d6d2d558f"
  },
  {
    "coordinate": "15.35",
    "quote": "民之于仁也，甚于水火",
    "primary": "ren",
    "concepts": [
      "ren",
      "min",
      "sheng-life",
      "si-death"
    ],
    "reason": "以水火對照仁，凸顯百姓對仁的需要及其價值。",
    "noteBoundary": "需要由譯注補明；未見蹈仁而死為此章論述，不能推成仁者生理上不死，須與15.9的生死取捨分語境讀。",
    "canonicalId": 455,
    "historicalIds": [
      455
    ],
    "sourceRowSha256": "a96281a3c0dfc033bb4cc9a84648f27a586ff2fb7e0b11934b44dafd9a30fc07"
  },
  {
    "coordinate": "15.36",
    "quote": "当仁，不让于师",
    "primary": "ren",
    "concepts": [
      "ren",
      "rang",
      "xing-act"
    ],
    "reason": "面臨為仁，即使對老師也不讓，將實行仁的責任放在身分禮讓之前。",
    "noteBoundary": "不讓限定當仁的情境，非全面取消敬師；不以任何自稱正確的行動都作仁來免於檢驗。",
    "canonicalId": 456,
    "historicalIds": [
      456
    ],
    "sourceRowSha256": "0e153ce8c4998e943c4061f8dbc2e49ac2f819cfa6860cb62f9317ff3bb65a0f"
  },
  {
    "coordinate": "15.37",
    "quote": "君子贞而不谅",
    "primary": "zhen",
    "concepts": [
      "zhen",
      "liang",
      "xin",
      "junzi"
    ],
    "reason": "依底本以大信小信區別貞諒，說君子守信仍須辨所守的層次。",
    "noteBoundary": "楊注所引假借勍說未取，不作全局字形替換；貞的言行抱一與僵硬守諾不可不分。",
    "canonicalId": 457,
    "historicalIds": [
      457
    ],
    "sourceRowSha256": "b58eadcaeea4d7dde6c753664bfd15c7e1807827b2322afdcb14fe5eb9ca184e"
  },
  {
    "coordinate": "15.38",
    "quote": "敬其事而后其食",
    "primary": "jing",
    "concepts": [
      "jing",
      "chen",
      "jun",
      "li-benefit"
    ],
    "reason": "事君先敬其職而後俸食，將工作責任與報酬次序相連。",
    "noteBoundary": "食為俸祿，不是拒絕正常吃飯；蜀石經異文保留於注，不能以此主張勞動不需合理報酬。",
    "canonicalId": 458,
    "historicalIds": [
      458
    ],
    "sourceRowSha256": "4a5603dc4028477c77e42cfdad76d8a3c6cc40b901f41d1569ce868ec274e3c9"
  },
  {
    "coordinate": "15.40",
    "quote": "道不同，不相为谋",
    "primary": "dao",
    "concepts": [
      "dao",
      "you"
    ],
    "reason": "主張不同難共謀，呈現合作與所守之道的關係。",
    "noteBoundary": "原章未說所有意見差異都不能交往；可與和而不同、以友輔仁比較，不以片語排除必要討論。",
    "canonicalId": 460,
    "historicalIds": [
      460
    ],
    "sourceRowSha256": "e51296eab244c9415b0e2c2834bd0aefc5806f25448ea52f5f223ae47ec20156"
  },
  {
    "coordinate": "15.41",
    "quote": "辞达而已矣",
    "primary": "yan",
    "concepts": [
      "yan",
      "wen",
      "zhi-quality"
    ],
    "reason": "言辭以達意為重，與文勝質的失衡比較，重視表達實際作用。",
    "noteBoundary": "達此為通達意思，非12.20的仕行通達；不把反對浮華等同不需精確用詞或結構。",
    "canonicalId": 461,
    "historicalIds": [
      461
    ],
    "sourceRowSha256": "32336e6145ae234c33650f18b37655cd8b61914589e7d8e78808fe17ed5969f1"
  },
  {
    "coordinate": "15.42",
    "quote": "固相师之道也",
    "primary": "jing",
    "concepts": [
      "jing",
      "ai",
      "li",
      "yan"
    ],
    "reason": "為盲樂師提示階席及在座者，提供以言語補足環境資訊的具體相助方式。",
    "noteBoundary": "師是樂師、冕是其名，相是幫助；不能誤成孔子向教師一般介紹座位，亦不把人名冕當禮帽。",
    "canonicalId": 462,
    "historicalIds": [
      462
    ],
    "sourceRowSha256": "1c0128aba8a131ef483d48af93a71f526431d05dee3bea54ce200a48500a067e"
  },
  {
    "coordinate": "16.1",
    "quote": "修文德以来之。既来之，则安之",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "chen",
      "yi",
      "min",
      "he",
      "anren",
      "jun-equity",
      "wen",
      "de"
    ],
    "reason": "反對伐顓臾並追問輔相責任，由財富人口、均和安到修文德安遠人，保留整段論證。",
    "noteBoundary": "原文混入寡當作貧、貧當作寡的校語，不能當經文送作完整來源；蕭牆依楊注特指魯君，不能只剩泛指內患的成語。",
    "sourceCompleteness": "original-editorial-contamination-review-required",
    "canonicalId": 463,
    "historicalIds": [
      463
    ],
    "sourceRowSha256": "d6b5de726fd500562efd04aa11fef9cac07dbb97d28082f4c80af0df11a353e6"
  },
  {
    "coordinate": "16.2",
    "quote": "天下有道，则礼乐征伐自天子出",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "li",
      "yue",
      "jun",
      "chen",
      "bangyoudao",
      "bangwudao"
    ],
    "reason": "以禮樂征伐的權力來源分有道無道，述諸侯、大夫、陪臣專政與延續的關係。",
    "noteBoundary": "十世五世三世是古代歷史概括，非精確普遍定律；庶人不議須讀原政治立場，不改作當代禁言要求。",
    "canonicalId": 464,
    "historicalIds": [
      464
    ],
    "sourceRowSha256": "e57b21d0bd837e5cd4fb0773324c18f2bc13c38abee62ca3ac5f6afbbc94d677"
  },
  {
    "coordinate": "16.3",
    "quote": "政逮于大夫四世矣",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "jun",
      "chen",
      "bang"
    ],
    "reason": "從魯公室與季氏兩條世系分說政權移轉，再說三桓衰微，保留不同計數基準。",
    "noteBoundary": "五世四世不是互相矛盾的同一世系；三桓依註為仲孟孫、叔孫、季孫，與14.21譯名錯配需分開處理。",
    "canonicalId": 465,
    "historicalIds": [
      465
    ],
    "sourceRowSha256": "f02f6df470501c8d9b13187a0a743d0913a91df6c6a4fa7adacd097f90553e1c"
  },
  {
    "coordinate": "16.4",
    "quote": "益者三友，损者三友",
    "primary": "sanyou",
    "concepts": [
      "sanyou",
      "you",
      "pengyou",
      "zhi-upright",
      "liang",
      "wen-hear",
      "ning-eloquence"
    ],
    "reason": "三益友與三損友各有性格言行依據，提供交友時可辨的不同影響。",
    "noteBoundary": "諒在此依楊注與信同義，不能照14.17小信義全盤貶斥；六類亦不以口才強弱一項便判人。",
    "canonicalId": 466,
    "historicalIds": [
      466
    ],
    "sourceRowSha256": "597ba7b5277e07ee0eb8ceaa898b747396c109d9922341ae5a04f2b0bbde07f6"
  },
  {
    "coordinate": "16.5",
    "quote": "益者三乐，损者三乐",
    "primary": "sanle",
    "concepts": [
      "sanle",
      "yue",
      "li",
      "xian",
      "you",
      "jie"
    ],
    "reason": "以礼樂調節、道人善、多賢友和驕佚晏樂作益損之分，討論快樂的內容與方向。",
    "noteBoundary": "樂兼以某事為樂與音樂等語法，不能把全句各樂字都歸入樂教；保留所樂之事才能解其益損。",
    "canonicalId": 467,
    "historicalIds": [
      467
    ],
    "sourceRowSha256": "cc990913e4b2258bb5e57ff1286f95927297395536c2f23095312a5e5cb30334"
  },
  {
    "coordinate": "16.6",
    "quote": "侍于君子有三愆",
    "primary": "yan",
    "concepts": [
      "yan",
      "jin",
      "shi-time",
      "guan"
    ],
    "reason": "言未及而先言、言及而不言、不察色而言分別為躁隱瞽，指出交談時機及觀察。",
    "noteBoundary": "瞽在此為譬喻的看不清情境，不當作對盲者能力的事實描述；隱為隱瞞不是退隱。",
    "canonicalId": 468,
    "historicalIds": [
      468
    ],
    "sourceRowSha256": "7f4dc89f3eccb3b4de89a8bb36cfc8e7214bacd1c1de216bbe8bf5c4f402e3c1"
  },
  {
    "coordinate": "16.7",
    "quote": "君子有三戒",
    "primary": "sanjie",
    "concepts": [
      "sanjie",
      "junzi",
      "yu",
      "shen"
    ],
    "reason": "少壮老各提醒色、鬥、得，將人生階段與欲望自制相連。",
    "noteBoundary": "血氣屬古代身心說法，不作現代生理診斷；得依楊注含名位財貨，不能只縮為金錢。",
    "canonicalId": 469,
    "historicalIds": [
      469
    ],
    "sourceRowSha256": "b1a11edcf4bf1f78f5930dfa8d05cd2f34bdca02ea177e61f22b83941eddce0c"
  },
  {
    "coordinate": "16.8",
    "quote": "君子有三畏",
    "primary": "sanwei",
    "concepts": [
      "sanwei",
      "junzi",
      "xiaoren",
      "tianming",
      "jing",
      "sheng-sage",
      "yan"
    ],
    "reason": "天命、大人與聖言構成君子的三畏，與小人的不知、狎、侮對照。",
    "noteBoundary": "大人依本註為高位者，聖人為有德者，不能混作一類；畏在此保留敬畏和古代秩序的語境。",
    "canonicalId": 470,
    "historicalIds": [
      470
    ],
    "sourceRowSha256": "ef273b47fbd3ad1cbb07e14dba6150d2e9b5487954432ac64680b287cb640c2f"
  },
  {
    "coordinate": "16.10",
    "quote": "君子有九思",
    "primary": "jiusi",
    "concepts": [
      "jiusi",
      "si",
      "gong",
      "zhong",
      "jing",
      "wen-ask",
      "yi",
      "li-benefit"
    ],
    "reason": "視聽色貌言事疑忿得九處各有思考目標，將反省落在日常感知、交往和取捨。",
    "noteBoundary": "九項分別對應原文，不能縮成只看字頻的九個詞；記住九思或點閱條目不等於已實踐。",
    "canonicalId": 472,
    "historicalIds": [
      472
    ],
    "sourceRowSha256": "7e60a463cef29ed2e60e041dea27b3da6855f0461a67c38fe518e2645cfd3f12"
  },
  {
    "coordinate": "16.11",
    "quote": "隐居以求其志，行义以达其道",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "yin",
      "zhi-aspiration",
      "yi",
      "dao"
    ],
    "reason": "見善避惡之人已見，隱居求志行義達道之人未見，兩組聞見對比保留。",
    "noteBoundary": "探湯是沸水譬喻；未見不是說不存在，不能把兩段都改成孔子已親見的人物名單。",
    "canonicalId": 473,
    "historicalIds": [
      473
    ],
    "sourceRowSha256": "c82632f8f3110dc3847c043a6588f2e68a671a064980cf33e2d6846ba16efd8f"
  },
  {
    "coordinate": "16.12",
    "quote": "民到于今称之",
    "primary": "de",
    "concepts": [
      "de",
      "fu",
      "pin",
      "ming-name"
    ],
    "reason": "齊景公多馬與伯夷叔齊困餓的身後評價對照，將財富與值得稱述的行為區別。",
    "noteBoundary": "首陽位置未定；章無子曰、結語上承不明且有闕文疑說，不能移入12.10詩句當作已修復。",
    "canonicalId": 474,
    "historicalIds": [
      474
    ],
    "sourceRowSha256": "8a5888b5170dfcfee82dd7943e1b587a9b9f20d041adbaa13689d157d3c7f3c0"
  },
  {
    "coordinate": "16.13",
    "quote": "不学礼，无以立",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "shi-poetry",
      "li",
      "yan",
      "fumu"
    ],
    "reason": "伯魚述兩次過庭問詩禮，陳亢所得又含君子不私教其子，連結家庭、學習內容及施教態度。",
    "noteBoundary": "問一得三為陳亢的結論，非孔子自述；遠其子不是斷絕親情，原文未載特殊秘密傳授。",
    "canonicalId": 475,
    "historicalIds": [
      475
    ],
    "sourceRowSha256": "aebb679772a378e2030b99adce1fc79824b62e8945c01bfcd00bb1fae5f1a032"
  },
  {
    "coordinate": "16.14",
    "quote": "邦君之妻",
    "primary": "ming-name",
    "concepts": [
      "ming-name",
      "li",
      "jun",
      "zhengming"
    ],
    "reason": "依國君、夫人自稱、本國及異邦人分列稱謂，提供角色與語用場合的名分材料。",
    "noteBoundary": "本章無子曰，楊注只是推測可能孔子言；正名是比較主題，不能憑現代疑心將全章刪為後人增補。",
    "canonicalId": 476,
    "historicalIds": [
      476
    ],
    "sourceRowSha256": "9a1298eb81ad27b7d008fe72662cf4e08f7d3ae34d05a0524d12a6dacdfb1d0b"
  },
  {
    "coordinate": "17.1",
    "quote": "吾将仕矣",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "li",
      "ren",
      "wisdom",
      "shi-time"
    ],
    "reason": "陽貨借饋豚禮俗促見並以仁知問仕，孔子答將仕，呈出處選擇與政治催迫。",
    "noteBoundary": "中段不可兩答依楊注屬陽貨自問自答，不能誤歸孔子；孔子未實仕陽貨，陽貨註名與曰註號有誤另列。",
    "canonicalId": 477,
    "historicalIds": [
      477
    ],
    "sourceRowSha256": "0287118e5bf039d73281bc4ca7e2f05c021632748c5028cf1a6e25144d40e22f"
  },
  {
    "coordinate": "17.2",
    "quote": "性相近也，习相远也",
    "primary": "xing-nature",
    "concepts": [
      "xing-nature",
      "xi"
    ],
    "reason": "把性之相近與習之相遠並列，提供先有條件與後來習染的比較。",
    "noteBoundary": "習在此不只是課堂複習；原文未給遺傳百分比或現代測量，不能據此替学生固定能力。",
    "canonicalId": 478,
    "historicalIds": [
      478
    ],
    "sourceRowSha256": "6a80541ac30bf35fc65208b859f8fc7d07d7de677fbaa92d58bcb81a89f706fd"
  },
  {
    "coordinate": "17.3",
    "quote": "唯上知与下愚不移",
    "primary": "xing-nature",
    "concepts": [
      "xing-nature",
      "wisdom",
      "xue"
    ],
    "reason": "以上知下愚不移與前章性習相參，保留思想內部需要討論的限制。",
    "noteBoundary": "楊注列品質、知識等不同解釋而僅字面譯；不把古代分類變成當代學生不可教的先驗標籤。",
    "canonicalId": 479,
    "historicalIds": [
      479
    ],
    "sourceRowSha256": "1f5ba168b5906b65230996a171acebf8294899c53e29dd4ccfabced34294f861"
  },
  {
    "coordinate": "17.4",
    "quote": "前言戏之耳",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "jiao",
      "yue",
      "airen",
      "zheng"
    ],
    "reason": "孔子以牛刀戲問武城弦歌，子游以學道之用答，孔子承認其說，呈地方教育與對話修正。",
    "noteBoundary": "割雞牛刀不是孔子最後反對教育的結論；君子小人按底本在此為官民角色。",
    "canonicalId": 480,
    "historicalIds": [
      480
    ],
    "sourceRowSha256": "5c0c2bdf877f3b26d9b4194d483ffdadc641abea2d59527ec42334ce81e728d7"
  },
  {
    "coordinate": "17.5",
    "quote": "吾其为东周乎",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "zhi-aspiration",
      "dao"
    ],
    "reason": "欲應公山之召引子路反對，孔子以復興周道答，保存行道與所依政治力量的張力。",
    "noteBoundary": "公山弗擾與不狃身分及史事存在衝突，楊注要求存疑；欲往不等於已往，不能寫成確定行旅。",
    "canonicalId": 481,
    "historicalIds": [
      481
    ],
    "sourceRowSha256": "57b6b3fc05c76728e032684f3c02581850b17a1fc959db3ff5346a718fb66c68"
  },
  {
    "coordinate": "17.6",
    "quote": "恭，宽，信，敏，惠",
    "primary": "ren",
    "concepts": [
      "ren",
      "gong",
      "kuan",
      "xin",
      "min-diligence",
      "hui-kindness"
    ],
    "reason": "問仁以能行五者作答，再分述各自作用，將仁落在交往和辦事的不同面向。",
    "noteBoundary": "能行而非只會說五個德目；敏為勤敏，恭寬信敏惠的作用逐一對應，不合成同一外在表現。",
    "canonicalId": 482,
    "historicalIds": [
      482
    ],
    "sourceRowSha256": "da681e85f909c24f5c98645867f13337a96c1209ff9d8bc8c08dec2a3ceb9522"
  },
  {
    "coordinate": "17.7",
    "quote": "吾岂匏瓜也哉",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office",
      "gang",
      "zhi-aspiration"
    ],
    "reason": "佛肸召而欲往，子路引老師舊言質疑，孔子以磨不磷涅不緇與匏瓜說不願虛置。",
    "noteBoundary": "堅白為自守譬喻，不保證任何環境都無影響；欲往仍非已往，中牟非河南同名地，匏瓜註號待核。",
    "canonicalId": 483,
    "historicalIds": [
      483
    ],
    "sourceRowSha256": "73fd5c27a1ad00ca44d5637ef219680072b42dff1e9a4f4455cd8ac42bc16da0"
  },
  {
    "coordinate": "17.8",
    "quote": "好仁不好学",
    "primary": "liubi",
    "concepts": [
      "liubi",
      "haoxue",
      "xue",
      "ren",
      "wisdom",
      "xin",
      "zhi-upright",
      "yong",
      "gang"
    ],
    "reason": "六種好德若不好學各有蔽，將明理學習作為防止偏失的條件。",
    "noteBoundary": "六蔽對應六德不可串錯；原文共蔽及贼註號存在待核問題，不能把反面後果說成六種德目本身無益。",
    "canonicalId": 484,
    "historicalIds": [
      484
    ],
    "sourceRowSha256": "927148b80b1a7437bfbed6e0bb8ae6891d14efbb847462c3853643892ae85375"
  },
  {
    "coordinate": "17.10",
    "quote": "其犹正墙面而立也与",
    "primary": "shijiao",
    "concepts": [
      "shijiao",
      "shi-poetry",
      "xue"
    ],
    "reason": "以面牆無見無行提醒伯魚學二南，連到詩學對理解表達的作用。",
    "noteBoundary": "為二南不僅誦讀的說明來自注引沈括；面牆是譬喻，不能作實際懲罰動作。",
    "canonicalId": 486,
    "historicalIds": [
      486
    ],
    "sourceRowSha256": "464ad8cfbe0b74d833fe53077ccb2ebe6747272f94a59fb2ca67a674b617c334"
  },
  {
    "coordinate": "17.11",
    "quote": "玉帛云乎哉",
    "primary": "li",
    "concepts": [
      "li",
      "yue",
      "qi-vessel",
      "wenzhi"
    ],
    "reason": "反問禮豈只玉帛、樂豈只鐘鼓，要求超越器物形式看其內容。",
    "noteBoundary": "不僅是器物不等於完全不用器物；原文未在此列完禮樂本質，不以空白補成某一後世體系。",
    "canonicalId": 487,
    "historicalIds": [
      487
    ],
    "sourceRowSha256": "e5461d90fe7f7fc1c29da137f9f7349eade617b87a738596ed55124e79ad758f"
  },
  {
    "coordinate": "17.12",
    "quote": "色厉而内荏",
    "primary": "cheng",
    "concepts": [
      "cheng",
      "zhiren"
    ],
    "reason": "外厲內荏被譬為穿窬之盜，對外表與內在不相稱作批評。",
    "noteBoundary": "穿窬是譬喻，不是已記錄的竊盜案件；不能只依面貌嚴厲便判定內心怯弱或犯罪。",
    "canonicalId": 488,
    "historicalIds": [
      488
    ],
    "sourceRowSha256": "e7ce80982b59b47c31af98b2f397e47af4f987d037b7618e5a180b7286377b74"
  },
  {
    "coordinate": "17.14",
    "quote": "道听而涂说",
    "primary": "yan",
    "concepts": [
      "yan",
      "wen-hear",
      "de",
      "jin"
    ],
    "reason": "聽途說即傳被斥為德之棄，將言語傳播與謹慎核實相連。",
    "noteBoundary": "道、涂在此道路語境，非抽象道理；原文未命名任何具體傳言，不自行填入現代事件。",
    "canonicalId": 490,
    "historicalIds": [
      490
    ],
    "sourceRowSha256": "c3b0ceee173c06cdba8c58ab450d39392c9b3451f7d898fdf5b6530947475cb1"
  },
  {
    "coordinate": "17.15",
    "quote": "既得之，患失之",
    "primary": "shi-office",
    "concepts": [
      "shi-office",
      "yu",
      "chen",
      "zheng"
    ],
    "reason": "鄙夫未得患不得、既得患失而無所不至，呈官位得失牽動行為的問題。",
    "noteBoundary": "原文混入患得之當作患不得之校語，屬來源污染而非正文；保留版本異文待核，不把校語送作完整經文。",
    "sourceCompleteness": "original-editorial-contamination-review-required",
    "canonicalId": 491,
    "historicalIds": [
      491
    ],
    "sourceRowSha256": "626949eda4d96a4f66ac569d0f434106f23dcce4151356c66c5f2fd045b4eeec"
  },
  {
    "coordinate": "17.16",
    "quote": "古之狂也肆，今之狂也荡",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "kuang",
      "zhi-upright",
      "cheng"
    ],
    "reason": "狂矜愚的古今表現對照，指出即使被稱毛病也可能有不同內涵。",
    "noteBoundary": "古今相對說話時代；廉依注為有棱角、有原則，不只等於不貪財，也不以此建立現代人格診斷。",
    "canonicalId": 492,
    "historicalIds": [
      492
    ],
    "sourceRowSha256": "178ce0401f09645f2d8c0c6c5a1786a3277b07d47f227efcce670262ce6fd48f"
  },
  {
    "coordinate": "17.17",
    "quote": "巧言令色，鲜矣仁",
    "primary": "qiaoyanlingse",
    "concepts": [
      "qiaoyanlingse",
      "ren",
      "yan",
      "cheng"
    ],
    "reason": "巧言令色與少仁重見，提醒評價言貌要察其內在德行。",
    "noteBoundary": "與1.3互參而保留重見位置及全部歷史ID；鮮為少，不能把所有流暢表達或和悅神色一概判為無仁。",
    "canonicalId": 493,
    "historicalIds": [
      493
    ],
    "sourceRowSha256": "4d2ee5b354c9078636e8c23cf1302acf6c1b10119ba466e3ce2a20de0381cf3d"
  },
  {
    "coordinate": "17.18",
    "quote": "恶利口之覆邦家者",
    "primary": "yan",
    "concepts": [
      "yan",
      "ning-eloquence",
      "li",
      "yue",
      "zheng"
    ],
    "reason": "由紫奪朱、鄭聲亂雅到利口覆邦，將形式替代與言語政治後果相連。",
    "noteBoundary": "色彩與音樂的古代等級觀念須辨時代；不能把今日穿紫或喜愛某類音樂直接當作品格問題。",
    "canonicalId": 494,
    "historicalIds": [
      494
    ],
    "sourceRowSha256": "29b890373cfb9525dbd4dcd4e95325832d430ad31e25f831e64d81e1739485aa"
  },
  {
    "coordinate": "17.19",
    "quote": "天何言哉",
    "primary": "tian",
    "concepts": [
      "tian",
      "yan",
      "xing-act",
      "jiaoxue"
    ],
    "reason": "欲無言引子貢問傳述，孔子以天無言而四時百物運行回應，提示言說以外的作用。",
    "noteBoundary": "天的譬喻不能直接變成教師從此應停止說明；欲無言不是此後確實不再說話的年表記錄。",
    "canonicalId": 495,
    "historicalIds": [
      495
    ],
    "sourceRowSha256": "89a1cb5f359e04446690b365449d80f91b880ba94eb4221e1e1c79243244eae6"
  },
  {
    "coordinate": "17.20",
    "quote": "取瑟而歌，使之闻之",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "yan",
      "yue",
      "xing-act"
    ],
    "reason": "辭見卻使孺悲聽見瑟歌，呈現非直接言說的回應，可討論其教育意味。",
    "noteBoundary": "不屑之教為楊注援孟子提出的可能，正文未明言拒見原因，不編造孺悲具體過失。",
    "canonicalId": 496,
    "historicalIds": [
      496
    ],
    "sourceRowSha256": "d66df4e350e3201328ffa20d803b6abae8a1fc3f663d5264ed2dbd28d3d062ef"
  },
  {
    "coordinate": "17.21",
    "quote": "于女安乎",
    "primary": "sang",
    "concepts": [
      "sang",
      "xiao",
      "fumu",
      "ren",
      "li",
      "yue"
    ],
    "reason": "宰我以禮樂久廢主張一年喪，孔子以安不安與父母懷抱之愛反問，保留雙方理由。",
    "noteBoundary": "期讀jī為一年；女安則為之不能截作孔子認可縮喪，全文有後續不仁評語，首註缺⑴標識待核。",
    "canonicalId": 497,
    "historicalIds": [
      497
    ],
    "sourceRowSha256": "64350f9b94bd3efa5eb6f446e4c7343ae0626efcfc725fd6dc32f2c48921c2a6"
  },
  {
    "coordinate": "17.22",
    "quote": "为之，犹贤乎已",
    "primary": "si",
    "concepts": [
      "si",
      "xing-act"
    ],
    "reason": "以博弈仍勝無所用心作比較，反對飽食終日全不活動心思。",
    "noteBoundary": "古博依注有棋局與擲采，不等於後來純賭博；比閒著好不代表遊戲時數就是學習掌握或正式分數。",
    "canonicalId": 498,
    "historicalIds": [
      498
    ],
    "sourceRowSha256": "e79b093f7c299b4f8877df8887c6819707f55bb2b72d64dd0136b3e083354d93"
  },
  {
    "coordinate": "17.23",
    "quote": "君子义以为上",
    "primary": "yi",
    "concepts": [
      "yi",
      "yong",
      "junzi",
      "xiaoren"
    ],
    "reason": "問尚勇而答義為上，指出有勇無義在不同身分下都可能致害。",
    "noteBoundary": "尚、上同義而詞性有別；不是否定一切勇，而是把勇放在義的約束中。",
    "canonicalId": 499,
    "historicalIds": [
      499
    ],
    "sourceRowSha256": "0bf3673415096e98828a1afa4c28fd165e1312872750f140f54a2c9375f16366"
  },
  {
    "coordinate": "17.24",
    "quote": "恶果敢而窒者",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "yan",
      "yong",
      "li",
      "zhi-upright",
      "wisdom"
    ],
    "reason": "孔子与子貢各述所惡，辨果敢與窒、勇與不遜、直與訐、知與徼。",
    "noteBoundary": "兩人的話不混為同一清單；下流流字衍文混入正文，屬校語污染，未核前不稱完整校定原文。",
    "sourceCompleteness": "original-editorial-contamination-review-required",
    "canonicalId": 500,
    "historicalIds": [
      500
    ],
    "sourceRowSha256": "c020db38b2194bc0892f4e33ad23b1119274686e7591aa966bc089fed92dd446"
  },
  {
    "coordinate": "17.25",
    "quote": "近之则不孙，远之则怨",
    "primary": "yuan",
    "concepts": [
      "yuan",
      "xiaoren"
    ],
    "reason": "以親疏與不遜、怨的張力表述待人困難，須連其對女子小人的概括一起閱讀。",
    "noteBoundary": "底本未把女子限定為女僕或某個人，不能為回護而自行改義；其性別概括需作歷史批判閱讀，不作今日待人準則。",
    "canonicalId": 501,
    "historicalIds": [
      501
    ],
    "sourceRowSha256": "760340385341ddf574df1a350d8c3fe804903803c0c528cccf76309a59dc1f20"
  },
  {
    "coordinate": "17.26",
    "quote": "年四十而见恶焉",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "ming-name",
      "gai"
    ],
    "reason": "以年四十仍受惡的評語談長期行為評價，可與後生可畏及眾惡必察比較。",
    "noteBoundary": "不當作現代人生不可改變的截止線；楊注引末之也已標17.4，而該語在當前底本17.5，座標待核。",
    "canonicalId": 502,
    "historicalIds": [
      502
    ],
    "sourceRowSha256": "b8c6280967272270d0fa1e1baf9563bbcae31190cef23fad7e06d62dadb97638"
  },
  {
    "coordinate": "18.1",
    "quote": "殷有三仁焉",
    "primary": "ren",
    "concepts": [
      "ren",
      "chushi",
      "chen",
      "yin",
      "si-death"
    ],
    "reason": "去、為奴、諫死三種處境同被稱仁，顯示對仁的評價不能只限一種行為結果。",
    "noteBoundary": "微子箕子比干各自事跡與注中親屬說分別保存；不將三種選擇化成要求每人仿效同一犧牲方式。",
    "canonicalId": 503,
    "historicalIds": [
      503
    ],
    "sourceRowSha256": "83dd529a471ed9699e8677517254f6299cca179a971bcaeff737ba36b6230694"
  },
  {
    "coordinate": "18.2",
    "quote": "直道而事人",
    "primary": "zhi-upright",
    "concepts": [
      "zhi-upright",
      "chushi",
      "shi-office",
      "dao"
    ],
    "reason": "柳下惠對屢黜而不去作答，將正直事人與去國能否改變處境區別。",
    "noteBoundary": "三黜依譯可讀多次，原文沒有每次免職日期；不作受不公待遇者不得離開的普遍命令。",
    "canonicalId": 504,
    "historicalIds": [
      504
    ],
    "sourceRowSha256": "9c88a54026942e406b9d921f9ee1b4e6fa125de02f8bb842b0ae524964ff8895"
  },
  {
    "coordinate": "18.3",
    "quote": "吾老矣，不能用也",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "shi-office"
    ],
    "reason": "齊景公議待遇而終說不能用，孔子離去，保留受用條件與去留的事件。",
    "noteBoundary": "兩段曰均依底本為景公之語，不能把吾老歸給孔子；待遇高低不等於已任官。",
    "canonicalId": 505,
    "historicalIds": [
      505
    ],
    "sourceRowSha256": "9ae181e1073b2da51201ee3cb518cbf0a929636167f51674b8d1b488b927a596"
  },
  {
    "coordinate": "18.4",
    "quote": "三日不朝，孔子行",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "zheng",
      "yue",
      "yu"
    ],
    "reason": "季桓子受女樂而不朝，孔子離職，關聯政治怠政與個人出處。",
    "noteBoundary": "歸通饋，女樂是具體饋贈事件；不把離職責任歸罪所有女性或一切音樂。",
    "canonicalId": 506,
    "historicalIds": [
      506
    ],
    "sourceRowSha256": "b3bbe8de5448795507df5a0a5c2dc1c7c6327e2707c13bb867e3a49950f847b8"
  },
  {
    "coordinate": "18.5",
    "quote": "往者不可谏，来者犹可追",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "yin",
      "kuang",
      "shi-time",
      "zhi-aspiration"
    ],
    "reason": "接輿以鳳歌勸止从政，孔子欲言而不得，呈現隱者與行道者未完成的對話。",
    "noteBoundary": "歌是接輿立場，孔子未在本章表示接受；接輿依注可能以事稱呼而非名或字，譯歌有押韻意譯。",
    "canonicalId": 507,
    "historicalIds": [
      507
    ],
    "sourceRowSha256": "946ca4e5bdecfb13ecc6130c2ff7d22f211301c707332e51c635aa49cfd56924"
  },
  {
    "coordinate": "18.7",
    "quote": "君子之仕也，行其义也",
    "primary": "yi",
    "concepts": [
      "yi",
      "shi-office",
      "chushi",
      "yin",
      "jun",
      "chen"
    ],
    "reason": "子路遇丈人並受留宿，後以行義解釋仕的責任，與隱者重潔身的取向對照。",
    "noteBoundary": "末段為子路之言；四體不勤指子路或丈人自述有異說，底本取前者，不把隱者客套抹去。",
    "canonicalId": 509,
    "historicalIds": [
      509
    ],
    "sourceRowSha256": "6a98a4c1440748c62019b32c44335f6dcf942d74f1e7423643711fa757b5db73"
  },
  {
    "coordinate": "18.8",
    "quote": "无可无不可",
    "primary": "chushi",
    "concepts": [
      "chushi",
      "yimin",
      "zhi-aspiration",
      "qing-integrity",
      "quan",
      "yin"
    ],
    "reason": "列逸民而分述守志、受屈而言行合宜、清隱與權，孔子自說無可無不可，保留出處多樣。",
    "noteBoundary": "逸民為被遺落的人才，不只退隱者；虞仲等多不可考，不能附會確定身分，無可不可亦非無任何原則。",
    "canonicalId": 510,
    "historicalIds": [
      510
    ],
    "sourceRowSha256": "7bcea4c52866542283f51c32838bdcad3c9a428bf863c41939841a9280cce463"
  },
  {
    "coordinate": "18.9",
    "quote": "大师挚适齐",
    "primary": "yue",
    "concepts": [
      "yue",
      "chushi",
      "yin"
    ],
    "reason": "記多位樂官往各國及河漢海邊，連結禮樂人物與離散行跡。",
    "noteBoundary": "何時人不可定，師摯與8.15師摯是否同人亦未知；地名可溯原文，不能補造精確行旅年份和路線。",
    "canonicalId": 511,
    "historicalIds": [
      511
    ],
    "sourceRowSha256": "0789e281fe3a11433b8c0754e08972925210193b2567def0b8469cce646fe4ef"
  },
  {
    "coordinate": "18.10",
    "quote": "无求备于一人",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "chen",
      "fumu",
      "you",
      "kuan"
    ],
    "reason": "周公告魯公厚親、用臣、存故舊、不求全，將用人包容與關係責任并列。",
    "noteBoundary": "此為周公之語，魯公指伯禽；無大故才不棄的條件須保留，不解成任何過失都不能處置。",
    "canonicalId": 512,
    "historicalIds": [
      512
    ],
    "sourceRowSha256": "dc23384b62bd8bbd2d37b2d2f9d31a1d53d8c71892085cccf2360d17eb1ffcaf"
  },
  {
    "coordinate": "18.11",
    "quote": "周有八士",
    "primary": "shi",
    "concepts": [
      "shi"
    ],
    "reason": "保存周八士姓名次序，作為全書人物與士的稱呼材料。",
    "noteBoundary": "八人無可考，四對雙生子的說法僅從名序押韻推衍；不自造生平、親屬或唯一身份。",
    "canonicalId": 513,
    "historicalIds": [
      513
    ],
    "sourceRowSha256": "f461c4c354065dd520ca035343091e7302bbc371af3046a8cba5408efc657fce"
  },
  {
    "coordinate": "19.1",
    "quote": "见得思义，祭思敬，丧思哀",
    "primary": "shi",
    "concepts": [
      "shi",
      "yi",
      "jing",
      "ji",
      "sang",
      "yong"
    ],
    "reason": "子張以危難、利益、祭與喪四種情境說士，將判斷落在所遇之事。",
    "noteBoundary": "致命在本句為付出性命，不作宿命；祭敬喪哀各有對象，不能只以儀節動作或一次測验表示完全做到。",
    "canonicalId": 514,
    "historicalIds": [
      514
    ],
    "sourceRowSha256": "6da613b9e1a30496ce13e45d869c245a54d4d18336052b9191c2978fda6c412f"
  },
  {
    "coordinate": "19.2",
    "quote": "执德不弘，信道不笃",
    "primary": "de",
    "concepts": [
      "de",
      "dao",
      "xin",
      "hongyi",
      "heng"
    ],
    "reason": "執德不強、信道不篤被說成無所輕重，重點在所守是否堅定。",
    "noteBoundary": "弘依楊注為強；為有為亡疑當時成語，譯為有無不增損，非對人的生命價值作字面否定。",
    "canonicalId": 515,
    "historicalIds": [
      515
    ],
    "sourceRowSha256": "f41e8e42e314ab0dbab25328bf6bbe2bfa69c1c512f588bbf05fee13185ebdea"
  },
  {
    "coordinate": "19.3",
    "quote": "君子尊贤而容众",
    "primary": "kuan",
    "concepts": [
      "kuan",
      "you",
      "pengyou",
      "xian",
      "junzi"
    ],
    "reason": "子張回應子夏門人，提出尊賢容眾、嘉善矜不能，與可交不可交的擇友說形成差別。",
    "noteBoundary": "子夏說經學生轉述，子張明言所聞不同；不能刪去分歧或把兩者都改成孔子直接定論。",
    "canonicalId": 516,
    "historicalIds": [
      516
    ],
    "sourceRowSha256": "211b9ab400560403586cdc6e350132ff047412c5bebbf4746f754ff3c364135a"
  },
  {
    "coordinate": "19.4",
    "quote": "虽小道，必有可观者焉",
    "primary": "dao",
    "concepts": [
      "dao",
      "yi-arts",
      "zhi-aspiration",
      "junzi"
    ],
    "reason": "子夏承認小道可觀而憂其妨致遠，呈專门技藝與遠大目標的取捨。",
    "noteBoundary": "小道依底本為小技藝，不是歪門邪道；泥為受阻，不以一句貶低一切專業工作。",
    "canonicalId": 517,
    "historicalIds": [
      517
    ],
    "sourceRowSha256": "7a7e6b9afebf4146adb486378019c7abe890c2e67e93644e20b493c0e5fc7dd6"
  },
  {
    "coordinate": "19.5",
    "quote": "日知其所亡，月无忘其所能",
    "primary": "haoxue",
    "concepts": [
      "haoxue",
      "xue",
      "xi",
      "zhi"
    ],
    "reason": "日增新知、月不忘已能共同構成好學，連新學與保持。",
    "noteBoundary": "亡通無，能是已會；日月是持續學習的節律，不能把登入天數或看過頁面直接視為實際能知。",
    "canonicalId": 518,
    "historicalIds": [
      518
    ],
    "sourceRowSha256": "bb8ce982fbc748b072067eb37baea27e0b58756971e6cfe1ea2ec8e929ca67a7"
  },
  {
    "coordinate": "19.6",
    "quote": "博学而笃志，切问而近思",
    "primary": "ren",
    "concepts": [
      "ren",
      "boxue",
      "duzhi",
      "qiewen",
      "jinsi",
      "xue",
      "si"
    ],
    "reason": "子夏將廣學、篤志、切問、近思連到仁，形成學問與德行互相支持的路線。",
    "noteBoundary": "志作志趣為底本主解，與識同的記憶說在注中另存；近思不僅是縮小問題而須保留當前所關切。",
    "canonicalId": 519,
    "historicalIds": [
      519
    ],
    "sourceRowSha256": "65bf44c407a243cb8745657a609e3b9bcf7201e442be0decd3fecf36e1e3179f"
  },
  {
    "coordinate": "19.7",
    "quote": "君子学以致其道",
    "primary": "xue",
    "concepts": [
      "xue",
      "dao",
      "junzi",
      "xing-act"
    ],
    "reason": "百工在肆成事與君子學而致道相譬，凸顯學習是成就所求的實際工作。",
    "noteBoundary": "百工譬喻不表示君子無須實踐；肆為製造場所，不是放肆之義。",
    "canonicalId": 520,
    "historicalIds": [
      520
    ],
    "sourceRowSha256": "b3036c00fe7d7db242a5688b88fee1cdb4a3e1bfc820bcdb25a0d1ee77f89d79"
  },
  {
    "coordinate": "19.8",
    "quote": "小人之过也必文",
    "primary": "guo",
    "concepts": [
      "guo",
      "wen",
      "xiaoren",
      "cheng"
    ],
    "reason": "以文飾過失說小人，與公開認錯改過的章句形成反面對照。",
    "noteBoundary": "文在此動詞為掩飾，不是文獻或禮文；不能把所有說明錯誤原因都預判為掩飾。",
    "canonicalId": 521,
    "historicalIds": [
      521
    ],
    "sourceRowSha256": "ff57866429f98d999a19cf742efaf9505549c53a81bb3ef4a06c853d9debfc2f"
  },
  {
    "coordinate": "19.9",
    "quote": "君子有三变",
    "primary": "junzi",
    "concepts": [
      "junzi",
      "yan"
    ],
    "reason": "遠望、近就、聽言所感分別為儼溫厲，說明同一人物不同接觸層次的表現。",
    "noteBoundary": "三變不是品格反覆無常；嚴厲言語仍須連溫和可親讀，不孤取可畏作理想。",
    "canonicalId": 522,
    "historicalIds": [
      522
    ],
    "sourceRowSha256": "2c6235266c4b232e651cae98f78b15628d0c2bfc9b50c789b7fa896778ab100e"
  },
  {
    "coordinate": "19.10",
    "quote": "信而后谏",
    "primary": "xin",
    "concepts": [
      "xin",
      "minxin",
      "min",
      "chen",
      "yan"
    ],
    "reason": "動員百姓與進諫都以先得信任為條件，分別考慮受話者如何理解行為。",
    "noteBoundary": "信在此著重被信任，不只本人自認守信；未信以為厲或謗是可能誤解，不能倒推所有異議皆屬誹謗。",
    "canonicalId": 523,
    "historicalIds": [
      523
    ],
    "sourceRowSha256": "ce8bbf2b014ac6881b2b821b6fe693e5d788449e586ddab195fdb56f50de0324"
  },
  {
    "coordinate": "19.11",
    "quote": "大德不踰闲",
    "primary": "de",
    "concepts": [
      "de",
      "jie",
      "quan"
    ],
    "reason": "大節不越界而小節容出入，呈原則與細節的區別。",
    "noteBoundary": "閑為界限，非閒暇；原文未列哪些具體事可算小節，不能自行把重大過錯降為小事。",
    "canonicalId": 524,
    "historicalIds": [
      524
    ],
    "sourceRowSha256": "09c18312e880e9bd9a63fc8345dd3fd0261589d85edd9df8b54fdc3f39575f88"
  },
  {
    "coordinate": "19.12",
    "quote": "有始有卒者，其惟圣人乎",
    "primary": "jiaoxue",
    "concepts": [
      "jiaoxue",
      "xue",
      "li",
      "dao",
      "sheng-sage"
    ],
    "reason": "子游批子夏重應對灑掃而缺本，子夏回應學道有類別與先後，保留教學爭論。",
    "noteBoundary": "兩人的立場不可合成沒有分歧的課程表；卒為終，聖人的有始有終也不是某套課程已驗證有效。",
    "canonicalId": 525,
    "historicalIds": [
      525
    ],
    "sourceRowSha256": "25930ff981f90b239a6e44560cc842598120b74c97787df825e577a5c555af98"
  },
  {
    "coordinate": "19.14",
    "quote": "丧致乎哀而止",
    "primary": "sang",
    "concepts": [
      "sang",
      "li"
    ],
    "reason": "子游以充分表哀而止說喪，將情感與禮節程度相連。",
    "noteBoundary": "致哀不等於所有家庭都應有同一外在表情；本章未詳定喪期，不代補三年或一年結論。",
    "canonicalId": 527,
    "historicalIds": [
      527
    ],
    "sourceRowSha256": "f9d8e7328aea85b6028e27cae5c8df2acc6993c8ff03b3384b2ad78faef4d1fb"
  },
  {
    "coordinate": "19.15",
    "quote": "然而未仁",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhiren",
      "pengyou"
    ],
    "reason": "子游稱子張難能而仍未仁，將可貴表現與完整仁德判斷分開。",
    "noteBoundary": "為難能並未逐項列出行為；不能把這個評語移到所有弟子，亦不抹去稱許的一半。",
    "canonicalId": 528,
    "historicalIds": [
      528
    ],
    "sourceRowSha256": "c48d4141f04245215a00a6d8ab9a50c5c416d22ee379854835dce14ba59174c3"
  },
  {
    "coordinate": "19.16",
    "quote": "难与并为仁矣",
    "primary": "ren",
    "concepts": [
      "ren",
      "zhiren",
      "pengyou"
    ],
    "reason": "曾子對子張堂堂及難並為仁的評議，與19.15構成不同弟子對人格修養的討論。",
    "noteBoundary": "堂堂古今解釋紛紜，底本取高不可攀；注引後世學派評語不能冒充本章已載事件。",
    "canonicalId": 529,
    "historicalIds": [
      529
    ],
    "sourceRowSha256": "a11814d553b9b04bd7d0754026646248ed497596c2cc3d9c758291036b8abdf7"
  },
  {
    "coordinate": "19.17",
    "quote": "必也亲丧乎",
    "primary": "sang",
    "concepts": [
      "sang",
      "fumu",
      "xiao"
    ],
    "reason": "曾子引老師說親喪是情感充分自發的情境，連結孝親與喪哀。",
    "noteBoundary": "此為曾子傳聞孔子語，須保留轉述層次；自致不直接等同死亡或自我傷害。",
    "canonicalId": 530,
    "historicalIds": [
      530
    ],
    "sourceRowSha256": "d20cb0052a29f65f099512d6e506f92b39f7a5b31f528ff8c5e05ab0f0b4fe69"
  },
  {
    "coordinate": "19.18",
    "quote": "其不改父之臣与父之政",
    "primary": "xiao",
    "concepts": [
      "xiao",
      "fumu",
      "chen",
      "zheng"
    ],
    "reason": "孟莊子留用父臣、保留父政被稱孝之難能，與1.11不改父道互參。",
    "noteBoundary": "這是具體人物政治事例，不作一切制度永不改革的命令；父子卒年差依註而非本任務另行核定年表。",
    "canonicalId": 531,
    "historicalIds": [
      531
    ],
    "sourceRowSha256": "90589ed29dfbf4f7ef67be97077c87a7f2b233624e49536e6e71e6907b8d5030"
  },
  {
    "coordinate": "19.19",
    "quote": "哀矜而勿喜",
    "primary": "xing",
    "concepts": [
      "xing",
      "min",
      "ai",
      "zheng"
    ],
    "reason": "曾子告陽膚審得實情當同情而不自喜，將官上失道與人民處境納入審視。",
    "noteBoundary": "散有離心及犯法的異解，底本未取後者；不得把同情誤為可以不查事實或擅改原有判決。",
    "canonicalId": 532,
    "historicalIds": [
      532
    ],
    "sourceRowSha256": "8a6feeccfee16755f7b2fa3da382077676cbabfd08eb186a1820c7e4af47a437"
  },
  {
    "coordinate": "19.20",
    "quote": "天下之恶皆归焉",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "ming-name",
      "yan",
      "cheng"
    ],
    "reason": "子貢疑纣惡被過度集中敘述，提醒壞名聲可能使未核之惡都歸其身。",
    "noteBoundary": "這不是證明纣毫無過失；下流是此處低下聲名的譬喻，不能與17.24校語中的流字混同處理。",
    "canonicalId": 533,
    "historicalIds": [
      533
    ],
    "sourceRowSha256": "61b139bdc5fbbbc4d3e76d60dcee31661089ceb070990483547b83494f719fba"
  },
  {
    "coordinate": "19.21",
    "quote": "更也，人皆仰之",
    "primary": "gai",
    "concepts": [
      "gai",
      "guo",
      "junzi",
      "cheng"
    ],
    "reason": "以日月蝕譬君子公開的過與更，將改過的可見性與受尊重相連。",
    "noteBoundary": "更為改正，日月食為譬喻；不能以人皆仰之保證現實中一次改錯必獲所有人稱許。",
    "canonicalId": 534,
    "historicalIds": [
      534
    ],
    "sourceRowSha256": "8d670ef3113fca91aaf47d39e808b1d8e67b5e0dcb1f3d28fccd7a700f355dc7"
  },
  {
    "coordinate": "19.22",
    "quote": "而亦何常师之有",
    "primary": "xue",
    "concepts": [
      "xue",
      "dao",
      "zhiren"
    ],
    "reason": "子貢以文武道在人且無處不學答仲尼焉學，呈廣泛從人求學。",
    "noteBoundary": "識在此為認識記取，常師為固定老師；衛公孫朝須與其他同名者分開，不自行合併。",
    "canonicalId": 535,
    "historicalIds": [
      535
    ],
    "sourceRowSha256": "177d9d550c05f3d44c1cef64774ac73f38e97eb31d1a74921f9a3c1bc06df468"
  },
  {
    "coordinate": "19.23",
    "quote": "不得其门而入",
    "primary": "zhiren",
    "concepts": [
      "zhiren",
      "xue"
    ],
    "reason": "子貢以牆高難入譬孔子學問難盡見，回應自身賢於仲尼的比較。",
    "noteBoundary": "宮牆宗廟百官均在譬喻內，官依注指房舍；不得把數仞變成實測故居高度。",
    "canonicalId": 536,
    "historicalIds": [
      536
    ],
    "sourceRowSha256": "316db33630272ff64bdae11d82fd8e29084fb9cc15de94eaece9adaca0dab2ca"
  },
  {
    "coordinate": "19.24",
    "quote": "仲尼，日月也",
    "primary": "sheng-sage",
    "concepts": [
      "sheng-sage",
      "zhiren",
      "yan"
    ],
    "reason": "子貢以日月譬孔子回應叔孫武叔毀言，保存弟子尊師的辯護立場。",
    "noteBoundary": "不可毀是子貢的立場表述，不是禁止後世研究批評；多依注為只，不作多數人。",
    "canonicalId": 537,
    "historicalIds": [
      537
    ],
    "sourceRowSha256": "8ef50466f8e7c898cf880b6f2db24965c6648a4ee7af72e48ee827df8c2b9aad"
  },
  {
    "coordinate": "19.25",
    "quote": "言不可不慎也",
    "primary": "yan",
    "concepts": [
      "yan",
      "shen",
      "zhiren",
      "zheng",
      "min"
    ],
    "reason": "子貢先戒言慎，再以天不可階和假設得邦家稱師，連接識人與政治理想。",
    "noteBoundary": "如果得邦家是未實現的假設，不記為孔子真有此統治成果；說話人是子貢，非孔子自我宣傳。",
    "canonicalId": 538,
    "historicalIds": [
      538
    ],
    "sourceRowSha256": "3ee0e331964e0c48312a839c1e92a9c57d1c595c2696d7672382796e5d2f2528"
  },
  {
    "coordinate": "20.1",
    "quote": "所重：民、食、丧、祭",
    "primary": "zheng",
    "concepts": [
      "zheng",
      "tianming",
      "min",
      "ren",
      "yimin",
      "kuan",
      "min-diligence",
      "sang",
      "ji"
    ],
    "reason": "尧舜禹湯周各段及後續政教語分層呈現天命、民生、舉逸民與責己，不能合為一人一次演說。",
    "noteBoundary": "信則民任焉此五字衍文混入原文，需版本修訂；權量法度依註是度量衡而非通權達變或法律，重複⑴按各段配對不可全章誤併。",
    "sourceCompleteness": "original-editorial-contamination-review-required",
    "canonicalId": 539,
    "historicalIds": [
      539
    ],
    "sourceRowSha256": "10f221b06abc4e2223eb28d727fb01b7482b5a4e9718ece0f749c54a5c5ea16e"
  },
  {
    "coordinate": "20.2",
    "quote": "尊五美，屏四恶",
    "primary": "wumei",
    "concepts": [
      "wumei",
      "sie",
      "zheng",
      "hui-kindness",
      "min",
      "yu",
      "ren",
      "tai",
      "gong",
      "jiao"
    ],
    "reason": "子張問政由五美四惡展開，既有惠民、擇勞、欲仁、泰威的正面要求，也列不教殺、不戒責成等反面。",
    "noteBoundary": "五美後續解說與四惡须完整對應；欲特指欲仁義，出納只取出義，有司為此處貶其吝的譬稱，不誤配到所有官職。",
    "canonicalId": 540,
    "historicalIds": [
      540
    ],
    "sourceRowSha256": "a4e180e41845d9007f3f3438907683a25db0efbdaa959f9008d31598ba71626c"
  }
];
